/**
 * Katalog pesan ICU MessageFormat — en-US dan id-ID.
 *
 * ADR T1-06: server TIDAK PERNAH mengirim prosa yang ditujukan kepada pengguna.
 * Ia mengirim `{code, params}`; klien merender kalimatnya. Katalog ini karena itu
 * adalah satu-satunya tempat kalimat pengguna hidup, dan `uji katalog` menolak
 * setiap kunci yang hanya punya satu bahasa (peta §7 — "tidak ada string yang
 * hanya punya satu bahasa").
 *
 * Kunci berbentuk `<domain>.<nama>`. Kode galat server memakai domain `error.`,
 * dan namanya PERSIS `Problem.code` supaya pemetaannya tidak perlu tabel kedua.
 */
export const LOCALES = ['en-US', 'id-ID'];
export const DEFAULT_LOCALE = 'en-US';
// ---------------------------------------------------------------------------
// en-US
// ---------------------------------------------------------------------------
export const enUS = {
    // -- Kerangka aplikasi
    'app.name': 'ASSIST',
    /**
     * AUDIT S-02. Figma menulis `by PQM Consultants` di KEDUA bahasa, dan baris
     * Indonesianya TIDAK menerjemahkannya — aturan lokalisasi Figma §5 mendaftar nama
     * perusahaan di antara "yang tidak diterjemahkan". Kode memendekkannya menjadi
     * "by PQM" dan menerjemahkannya menjadi "oleh PQM"; keduanya salah pada hal yang
     * sama, yaitu memperlakukan nama sebagai frasa.
     */
    'app.byPqm': 'by PQM Consultants',
    'app.skipToContent': 'Skip to content',
    /** AUDIT S-03. Naskah Figma `5:53` persis, tanpa elipsis — Figma tidak memakainya. */
    'app.search.placeholder': 'Search findings, locations, or people',
    'app.search.label': 'Global search',
    'app.language': 'Language',
    'app.language.en': 'English',
    'app.language.id': 'Bahasa Indonesia',
    'app.signOut': 'Sign out',
    /**
     * PEMILIH PERIODE `5:58` dan LENCANA NOTIFIKASI `107:220` — dua elemen bilah atas yang
     * digambar di 28 frame dan tidak pernah ditulis sampai 28 September 2026.
     *
     * `app.period.notApplicable` sengaja berbentuk PENGINGKARAN. Ia lolos aturan A
     * `gerbang-janji-kendali.mjs` bukan karena celah: kalimat yang mengandung "on this
     * screen" DAN "does not" memang tidak menjanjikan aksi — ia menjelaskan ketiadaannya,
     * dan itulah bentuk jujur yang diminta aturan kendali.
     */
    'app.period.label': 'Period',
    'app.period.notApplicable': 'Not used on this screen.',
    'app.notifications.loading': 'Notifications have not loaded yet.',
    'app.notifications.unread': '{count, plural, =0 {No unread notifications} one {# unread notification} other {# unread notifications}}',
    'app.accountSettings': 'Account settings',
    'app.mainNavigation': 'Main navigation',
    'app.loading': 'Loading…',
    'app.retry': 'Try again',
    'app.cancel': 'Cancel',
    'app.save': 'Save',
    'app.close': 'Close',
    'app.dismiss': 'Dismiss',
    'app.of': 'of',
    // -- Kelompok navigasi (dari manifest nav[].group)
    'nav.group.OPERATIONS': 'Operations',
    'nav.group.IMPROVEMENT': 'Improvement',
    'nav.group.KNOWLEDGE': 'Knowledge',
    'nav.group.INSIGHT': 'Insight',
    'nav.group.MANAGE': 'Manage',
    'nav.group.PLATFORM': 'Platform',
    'nav.group.GOVERNANCE': 'Governance',
    // Kelompok navigasi aplikasi lapangan (`surface: mobile`). Ia ada di manifest
    // sejak hari pertama, jadi kalimatnya juga harus ada — meski layarnya belum.
    'nav.group.TAB': 'Tabs',
    // -- Sign In (20:2)
    /**
     * JUDUL DUA WARNA, DUA KUNCI — dan tidak pernah disambung dari satu string.
     *
     * Audit §5: urutan katanya MEMBALIK antarbahasa. `Sign in to` + `ASSIST` di Inggris,
     * `Masuk ke` + `ASSIST` di Indonesia; pada Dasbor urutannya justru terbalik
     * (`Operations`+`Dashboard` versus `Dasbor`+`Operasional`). Sebuah judul yang dirakit
     * dengan menyambung dua string memaksa satu urutan pada kedua bahasa.
     */
    'signIn.title': 'Sign in to',
    'signIn.titleAccent': 'ASSIST',
    'signIn.subtitle': 'Use your company account. No access yet? Contact your company administrator.',
    /** Panel merek kiri (Figma `20:3`), 620 px — 43 % layar yang dirancang dan hilang di kode. */
    'signIn.brand.org': 'PQM CONSULTANTS',
    'signIn.brand.orgTagline': 'Productivity & Quality Management',
    /** Teks alternatif tanda PQM. Nama perusahaan TIDAK diterjemahkan (aturan lokalisasi Figma §5). */
    'signIn.brand.logoAlt': 'PQM Consultants',
    /**
     * Lockup layar Masuk berbunyi `ASSIST by PQM` (Figma `20:22`), sementara blok merek
     * sidebar berbunyi `ASSIST by PQM Consultants` (Figma `5:10`). Keduanya BERBEDA di
     * Figma, jadi keduanya kunci yang berbeda di sini — memakai satu kunci untuk dua
     * tempat adalah cara paling mudah membuat salah satunya melenceng diam-diam.
     */
    'signIn.brand.by': 'by PQM',
    'signIn.brand.tagline': 'The Operational Excellence platform for improvement that actually finishes — 5S, kaizen, audits, and projects in one workflow.',
    'signIn.brand.stat.foundedValue': '1987',
    'signIn.brand.stat.foundedLabel': 'Founded',
    'signIn.brand.stat.clientsValue': '2.100+',
    'signIn.brand.stat.clientsLabel': 'Clients served',
    'signIn.brand.stat.projectsValue': '900+',
    'signIn.brand.stat.projectsLabel': 'Projects',
    'signIn.rememberDevice': 'Remember this device',
    'signIn.or': 'or',
    'signIn.sso': 'Sign in with company SSO',
    /**
     * DULU ia hanya muncul SESUDAH tombolnya ditekan, dan tombolnya tampak aktif.
     * Sebuah kendali yang baru mengatakan dirinya tidak bekerja setelah dicoba adalah
     * kelas cacat yang sama dengan tombol mati tanpa penjelasan: orang menempuh
     * langkahnya lebih dulu, lalu diberi tahu. Sekarang ia selalu terlihat, di bawah
     * tombol yang dimatikan, dan ia MENYEBUTKAN apa yang harus dilakukan sebagai gantinya.
     */
    'signIn.sso.unavailable': 'Single sign-on is not part of this version — no identity provider exists to sign ' +
        'you in to. Use your email address and password above.',
    /** Baris legal Figma `20:64`. Ia BUKAN pengganti pemberitahuan privasi; ia kalimat kedaulatan data. */
    'signIn.legal': 'Data is stored in Indonesia under Law No. 27 of 2022.',
    'signIn.tenantSlug': 'Organisation',
    'signIn.tenantSlug.hint': 'Leave empty if you are PQM staff.',
    'signIn.identity': 'Email or employee number',
    'signIn.password': 'Password',
    'signIn.submit': 'Sign in',
    'signIn.submitting': 'Signing in…',
    'signIn.forgotPassword': 'Forgot your password?',
    /**
     * UAT D-02. Tombol ini dulu hanya menyetel penanda di layar dan mengatakan
     * "tautan sekali pakai sedang dikirim" — nol permintaan jaringan, nol surel,
     * nol token. Pesan yang BERBOHONG lebih buruk daripada tombol yang mati,
     * terutama di titik pengguna paling butuh pertolongan.
     *
     * Sekarang layarnya menanyakan surel mana yang dimaksud, benar-benar memanggil
     * `POST /v1/auth/forgot-password` (yang menerbitkan token dan menulis jejak),
     * lalu mengatakan APA YANG SESUNGGUHNYA TERJADI: permintaannya tercatat, dan
     * pengiriman surel belum ada di versi ini. Kalimat di bawah sengaja tidak
     * menjanjikan surel. Ia akan diganti pada gelombang yang membangun pengirimnya.
     */
    'signIn.forgotPassword.title': 'Reset your password',
    'signIn.forgotPassword.body': 'Tell us which account, and we will record the request. Password reset e-mail is not switched on in this version yet, so someone will have to do it for you.',
    'signIn.forgotPassword.submit': 'Record the request',
    'signIn.forgotPassword.cancel': 'Back to sign in',
    'signIn.forgotPassword.recorded': 'Your request is recorded. Automatic e-mail is not available in this version — ask your area leader or your PQM contact to reset the password for you.',
    'signIn.logoPlaceholder': 'Client logo',
    // -- Dashboard (5:2)
    /**
     * Urutan kata judul MEMBALIK antarbahasa: `Operations` + `Dashboard` di Inggris,
     * `Dasbor` + `Operasional` di Indonesia. Dua kunci, satu per bagian, dan tiap lokal
     * menentukan urutannya sendiri lewat nilainya.
     */
    'dashboard.title': 'Operations',
    'dashboard.titleAccent': 'Dashboard',
    'dashboard.subtitle': '{org} · {areas, number} areas · {users, number} active users',
    'dashboard.stat.verifiedImpact': 'Verified impact',
    'dashboard.impact.excluded': '{count, plural, one {# claim awaiting finance} other {# claims awaiting finance}}',
    'dashboard.trend.needsAction': 'Needs action',
    'dashboard.decision.approve': 'Approve',
    'dashboard.decision.decline': 'Decline',
    'dashboard.decision.requestInfo': 'Request info',
    'dashboard.decisionQueue.auditor': 'Approvals belong to the area leader and the manager, not to audit.',
    'dashboard.recurring': 'Recurring findings',
    'dashboard.recurring.period': 'This period',
    'dashboard.recurring.empty': 'No findings opened in this period yet.',
    'dashboard.export': 'Export',
    'dashboard.recurring.note': 'By 5S category, this period.',
    'dashboard.recurring.action': 'See the full breakdown',
    'dashboard.areaCondition': 'Area condition',
    'dashboard.areaCondition.empty': 'No area figures for this period yet.',
    'dashboard.areaColumn.area': 'Area',
    'dashboard.areaColumn.completion': 'Completion',
    'dashboard.areaColumn.overdue': 'Overdue',
    'dashboard.areaColumn.lastAuditScore': 'Last audit score',
    'dashboard.areaFilter.needsAttention': 'Needs attention',
    'dashboard.areaFilter.all': 'All areas',
    'dashboard.areaCondition.allGood': 'Every area is on track this period.',
    'dashboard.areaColumn.status': 'Status',
    'dashboard.areaStatus.GOOD': 'Good',
    'dashboard.areaStatus.WATCH': 'Watch',
    'dashboard.areaStatus.CRITICAL': 'Critical',
    'dashboard.stat.openFindings': 'Open findings',
    'dashboard.stat.overdue': 'Overdue',
    'dashboard.stat.closedThisMonth': 'Closed this month',
    'dashboard.stat.awaitingVerification': 'Awaiting verification',
    'dashboard.decisionQueue': 'Decision queue',
    'dashboard.decisionQueue.empty': 'Nothing is waiting on you.',
    'dashboard.areaHealth': 'Area health',
    'dashboard.viewAll': 'View all',
    'dashboard.buildMonthlyReport': 'Build monthly report',
    'dashboard.empty.title': 'No findings this month yet',
    'dashboard.empty.body': 'When your team reports the first finding of the month, its numbers appear here.',
    'dashboard.completion': '{done, number} of {total, number} areas complete',
    // -- Findings List (8:2)
    'findings.title': 'Open',
    'findings.titleAccent': 'Findings',
    'findings.subtitle': '{open, number} open findings · {overdue, number} past due · {days} days average to close',
    'findings.pagination.showing': 'Showing {from, number}–{to, number} of {total, number} findings',
    'findings.pagination.previous': 'Previous page',
    'findings.pagination.next': 'Next page',
    'findings.pagination.page': 'Page {page, number}',
    'findings.new': 'New finding',
    'findings.column.priority': 'Priority',
    /**
     * KEEMPAT nilai `severity`, bukan tiga. Basis data menerima
     * `LOW|MEDIUM|HIGH|CRITICAL` (migrasi 0005 baris 116), dan katalog yang hanya
     * memuat tiga membuat baris ber-`CRITICAL` menampilkan KUNCI MENTAH
     * `findings.priority.CRITICAL` kepada pengguna — kelas cacat yang sama dengan
     * `category.SET` pada UAT G3 (D-06), dan gerbang `assert-teks-antarmuka.sh`
     * menangkapnya justru karena kuncinya dirakit dari data.
     */
    'findings.priority.CRITICAL': 'Critical',
    'findings.priority.HIGH': 'High',
    'findings.priority.MEDIUM': 'Medium',
    'findings.priority.LOW': 'Low',
    'findings.count': '{count, plural, =0 {No findings} one {# finding} other {# findings}}',
    'findings.column.code': 'Code',
    'findings.column.title': 'Title',
    'findings.column.category': 'Category',
    'findings.column.area': 'Area',
    'findings.column.owner': 'Owner',
    'findings.column.state': 'Status',
    'findings.column.due': 'Due',
    'findings.column.photos': 'Photos',
    'findings.filter.state': 'Status',
    'findings.filter.area': 'Area',
    'findings.filter.category': 'Category',
    'findings.filter.owner': 'Owner',
    'findings.filter.all': 'All',
    'findings.filter.clear': 'Clear filters',
    'findings.export': 'Export CSV',
    'findings.selected': '{count, plural, one {# selected} other {# selected}}',
    'findings.bulk.assign': 'Reassign',
    'findings.bulk.changeDue': 'Change due date',
    'findings.bulk.changeDue.describe': '{count, plural, one {# finding will move to the new due date.} other {# findings will move to the new due date.}} You can move it again later.',
    'findings.bulk.done': '{count, plural, one {Due date moved on # finding.} other {Due date moved on # findings.}}',
    'findings.bulk.partial': 'Due date moved on {done, number} findings; {failed, number} were refused: {codes}.',
    'findings.empty.title': 'No findings yet',
    'findings.empty.body': 'The first report from the floor will show up here.',
    'findings.empty.cta': 'Report a finding',
    'findings.emptyFiltered.title': 'No findings match these filters',
    'findings.emptyFiltered.body': 'Widen the filters, or clear them to see everything.',
    'findings.unassigned': 'Unassigned',
    'findings.noDueDate': 'No due date',
    // -- Finding Detail (9:2)
    'finding.reportedBy': 'Reported by {name}',
    'finding.owner': 'Owner',
    'finding.area': 'Area',
    'finding.category': 'Category',
    'finding.severity': 'Severity',
    'finding.due': 'Due',
    'finding.opened': 'Opened',
    'finding.closed': 'Closed',
    'finding.photos': 'Photos',
    'finding.photos.before': 'Before',
    'finding.photos.after': 'After',
    'finding.photos.evidence': 'Evidence',
    'finding.timeline': 'History',
    // UAT D-09: baris ini berisi penjelasan arsitektur — nama tabel dan kata
    // "cache" — pada layar yang dibaca leader area dan manajer produksi. Aturan itu
    // untuk TIM (peta §7), bukan untuk pengguna. Yang menggantikannya menyatakan
    // hal yang sama dalam bahasa yang berguna bagi pembacanya: riwayat ini lengkap,
    // berurutan, dan tidak dapat dihapus.
    'finding.timeline.note': 'Every change is recorded here, in order. Nothing can be removed.',
    'finding.comments': 'Comments',
    'finding.comment.add': 'Add a comment',
    'finding.comment.submit': 'Post comment',
    'finding.comment.empty': 'No comments yet.',
    'finding.actions': 'Actions',
    'finding.actions.none': 'No action is available to you in this state.',
    'finding.reason': 'Reason',
    'finding.reason.required': 'A reason is required and the reporter will read it.',
    'finding.description': 'Description',
    'finding.owner.choose': 'Choose a person…',
    'field.unknown': 'Additional information',
    'finding.assign.owner': 'Assign to',
    'finding.dueDate.new': 'New due date',
    'finding.notFound.title': 'That finding is not here',
    'finding.notFound.body': 'It may belong to another organisation, or to an area outside your scope.',
    // -- Transisi (workflow finding.v1)
    'transition.CREATE': 'Submit finding',
    'transition.ASSIGN': 'Assign',
    'transition.START': 'Start work',
    'transition.CHANGE_DUE_DATE': 'Change due date',
    'transition.ADD_NOTE': 'Add a note',
    'transition.REQUEST_INFO': 'Request info',
    'transition.SUBMIT_FOR_VERIFICATION': 'Close it out',
    'transition.SEND_BACK': 'Send back',
    'transition.CONFIRM_FIX': 'Confirm the fix',
    'transition.CLOSE': 'Close',
    'transition.REOPEN': 'Reopen',
    'transition.ESCALATE': 'Escalate',
    'transition.DE_ESCALATE': 'Step down',
    'transition.TAKE_OVER': 'Take over',
    // -- State dan faset
    'state.OPEN': 'Open',
    'state.ASSIGNED': 'Assigned',
    'state.IN_PROGRESS': 'In progress',
    'state.AWAITING_VERIFICATION': 'Awaiting verification',
    'state.SENT_BACK': 'Sent back',
    'state.CLOSED': 'Closed',
    'facet.OVERDUE': 'Overdue',
    'facet.ESCALATED': 'Escalated',
    // -- Kategori 5R (label ganda, satu kode tersimpan)
    'category.SORT': 'Sort',
    // `item_category.code` di basis data memakai `SET`, bukan `SET_IN_ORDER`, dan
    // `MEETING` untuk tindakan rapat. Keduanya tidak punya kunci, sehingga kolom
    // Kategori menampilkan `category.SET` MENTAH kepada pengguna di kedua bahasa
    // (UAT D-06). Yang hilang bukan terjemahannya melainkan kuncinya; alias lama
    // dipertahankan karena impor data lama memakainya.
    'category.SET': 'Set in order',
    'category.SET_IN_ORDER': 'Set in order',
    'category.MEETING': 'Meeting action',
    'category.SHINE': 'Shine',
    'category.STANDARDIZE': 'Standardize',
    'category.SUSTAIN': 'Sustain',
    'category.SAFETY': 'Safety',
    // -- Severity
    'severity.LOW': 'Low',
    'severity.MEDIUM': 'Medium',
    'severity.HIGH': 'High',
    'severity.CRITICAL': 'Critical',
    // -- Peran
    'role.OPR': 'Operator',
    'role.LDR': 'Area leader',
    'role.MGR': 'Manager',
    'role.AUD': 'Auditor',
    'role.KON': 'PQM consultant',
    'role.PLT': 'Platform admin',
    'role.FAC': 'Facilitator',
    // -- Layar kondisi lintas modul (peta §5.9)
    'state.loading.title': 'Loading',
    'state.error.title': 'We could not load this',
    'state.error.body': 'The server did not answer as expected. You can try again; nothing you typed was lost.',
    'state.error.code': 'Reference for support: {code}',
    'state.notInPlan.title': 'This module is not in your plan',
    'state.notInPlan.body': 'Module {module} is not switched on for {organisation}. A platform administrator can turn it on — it takes effect in minutes, with no deployment.',
    // UAT D-08: `{organisation}` dikirim KOSONG oleh layar, menyisakan kalimat yang
    // terputus tepat sebelum titik ("…is not switched on for ."). Nama organisasi
    // sekarang diambil dari sesi; varian di bawah dipakai ketika sesi memang tidak
    // punya tenant — bukan untuk menambal panggilan yang lupa mengisinya.
    'state.notInPlan.bodyNoOrg': 'Module {module} is not switched on for this organisation. A platform administrator can turn it on — it takes effect in minutes, with no deployment.',
    'state.notInPlan.whoCanHelp': 'Ask your PQM contact or a platform administrator.',
    // UAT D-07: layar yang BELUM DIBANGUN memakai alasan yang salah. "Modul ini
    // tidak ada di paket Anda" diperuntukkan khusus bagi `403 ENTITLEMENT_REQUIRED`
    // (layar 70:1007); memakainya untuk /users — yang ADA di paket ESSENTIALS —
    // memberi tahu pengguna hal yang tidak benar dan mendorongnya menghubungi PQM
    // untuk membeli sesuatu yang sudah dimilikinya.
    'state.notBuilt.title': 'This screen is not ready yet',
    'state.notBuilt.body': 'It is part of ASSIST and it is in your plan, but it has not been built yet. Your data is safe and nothing is missing.',
    'state.notBuilt.whoCanHelp': 'Your PQM contact can tell you when it arrives.',
    // UAT D-14: operator mendarat di Dasbor Manajer dengan bilah sisi kosong.
    'state.fieldApp.title': 'Your work happens on the phone',
    'state.fieldApp.body': 'Reporting findings, closing them out, and audits are all in the ASSIST app on your phone. This web console is for area leaders and managers.',
    'state.fieldApp.whoCanHelp': 'Ask your area leader if you cannot find the app.',
    'dialog.confirm.title': 'Are you sure?',
    'dialog.confirm.destructive': 'This cannot be undone.',
    'dialog.action.describe': 'Fill this in, then save. Every change is recorded.',
    // -- Umpan balik: kata sinyal (ANSI Z535.4)
    'signal.information': 'Information',
    'signal.success': 'Success',
    'signal.warning': 'Warning',
    'signal.danger': 'Error',
    // -- Kode galat server (Problem.code). Nama = kode, persis.
    'error.VALIDATION_FAILED': 'Some of what you entered needs a change.',
    'error.INVALID_CREDENTIALS': 'That email or password is not right.',
    'error.ACCOUNT_LOCKED': 'This account is locked after {attempts, number} failed attempts. Try again in {minutes, number} minutes, or ask a manager to reset it.',
    'error.TENANT_INACTIVE': 'That organisation is not active. Please contact your PQM consultant.',
    'error.USER_NOT_ELIGIBLE': 'This account cannot sign in. Ask a manager to check it.',
    'error.UNAUTHENTICATED': 'Please sign in again.',
    'error.SESSION_EXPIRED': 'Your session expired. Please sign in again.',
    'error.SESSION_REVOKED': 'This session was signed out elsewhere. Please sign in again.',
    'error.SESSION_UNKNOWN': 'Please sign in again.',
    'error.FORBIDDEN': 'You do not have permission to do that.',
    'error.NOT_FOUND': 'That record is not here.',
    'error.ENTITLEMENT_REQUIRED': 'Module {module} is not in this organisation’s plan, so it cannot be changed. Existing data stays readable and exportable.',
    'error.TRANSITION_NOT_ALLOWED': '“{transition}” cannot be done from {state}.',
    'error.STATE_CONFLICT': 'This finding has moved on since your device last saw it — it is now {actual}. Nothing was lost.',
    'error.AFTER_PHOTO_REQUIRED': 'An “after” photo is required before this can be closed. {found, number} of {required, number} attached.',
    'error.PHOTO_REQUIRED': 'At least one photo is required.',
    'error.LOCATION_REQUIRED': 'Pick an area.',
    'error.CATEGORY_REQUIRED': 'Pick a category.',
    'error.DESCRIPTION_TOO_LONG': 'Keep the description to {max, number} characters — you have {actual, number}.',
    'error.LOCATION_INACTIVE': 'That area is switched off.',
    'error.CODE_BELONGS_TO_ANOTHER_ORGANISATION': 'That code belongs to a different organisation.',
    'error.OWNER_REQUIRED': 'Choose who will own this.',
    'error.OWNER_OUTSIDE_TENANT': 'That person is not in this organisation.',
    'error.OWNER_DISABLED': 'That account is switched off.',
    'error.REASON_REQUIRED': 'A reason is required — the other person will read it.',
    'error.DUE_DATE_IN_PAST': 'Pick a date in the future.',
    'error.SELF_VERIFICATION_NOT_ALLOWED': 'The person who did the work cannot verify it. Ask an area leader or manager.',
    'error.ESCALATION_AT_TOP_LEVEL': 'This is already at the top escalation level.',
    'error.NOT_ESCALATED': 'This is not escalated.',
    'error.NO_ESCALATION_TRIGGER': 'Nothing here calls for an escalation yet.',
    'error.INSTRUMENT_VERSION_CONFLICT': 'The audit form changed while you were offline.',
    'error.INSTRUMENT_VERSION_WITHDRAWN': 'That version of the audit form has been withdrawn.',
    'error.RATE_LIMITED': 'Too many attempts. Wait a moment and try again.',
    'error.INTERNAL': 'Something went wrong on our side. Reference: {request_id}.',
    'error.TENANT_NOT_FOUND': 'That organisation was not found.',
    'error.CONSULTANT_NOT_ASSIGNED': 'You are not assigned to that organisation.',
    'error.CANNOT_REVOKE_CURRENT_SESSION': 'You cannot sign out the session you are using.',
    'error.RESET_TOKEN_INVALID': 'That link has expired or has already been used.',
    'error.CONTEXT_NOT_BOUND': 'Please sign in again.',
    // -- Kode yang lahir dari workflow M03 (Audit) dan M07 (Rapat & Tindakan).
    //
    // UAT D-06 ditemukan pada `category.SET`: sebuah kode yang ADA di data tetapi
    // TIDAK punya kunci tampil MENTAH kepada pengguna, dan gerbang bahasa tetap hijau
    // karena kedua katalog kehilangan kunci yang sama. Blok ini menutup kelasnya
    // untuk seluruh kode yang sudah dinyatakan `workflows/*.json` — termasuk milik
    // modul yang layarnya belum dibangun, karena kode itu SUDAH dapat muncul di
    // `item_event` dan di jawaban `Problem` hari ini.
    //
    // `tools/ci/cek-teks-antarmuka.py` mengenumerasi daftarnya dari berkas workflow,
    // jadi sebuah transisi baru tanpa kalimatnya menggagalkan gerbang — bukan tampil
    // mentah di layar orang.
    'state.SCHEDULED': 'Scheduled',
    'state.RUNNING': 'In progress',
    'state.SUBMITTED': 'Submitted',
    'state.VOIDED': 'Voided',
    'state.CANCELLED': 'Cancelled',
    'state.OVERRUN': 'Ran over time',
    'facet.PENDING_SYNC': 'Waiting to sync',
    'facet.INSTRUMENT_WITHDRAWN': 'Form withdrawn',
    'facet.CARRIED_OVER': 'Carried over',
    'facet.NO_ATTENDEES': 'No attendees',
    'event.ASSESSMENT_SCHEDULED': '{actor} scheduled the audit {code}',
    'event.ASSESSMENT_STARTED': '{actor} started the audit {code}',
    'event.ASSESSMENT_SUBMITTED': '{actor} submitted the audit {code}',
    'event.ASSESSMENT_VOIDED': '{actor} voided the audit {code}',
    'event.MEETING_SCHEDULED': '{actor} scheduled the meeting',
    'event.MEETING_STARTED': '{actor} started the meeting',
    'event.MEETING_CLOSED': '{actor} closed the meeting',
    'event.MEETING_CANCELLED': '{actor} cancelled the meeting',
    'event.MEETING_OVERRUN': 'The meeting ran over its ten minutes',
    'event.ACTION_CREATED': '{actor} created action {code}',
    'event.ACTION_ASSIGNED': '{actor} assigned action {code} to {owner}',
    'event.ACTION_STARTED': '{actor} started action {code}',
    'event.ACTION_DUE_CHANGED': '{actor} changed the due date of action {code}',
    'event.ACTION_CLOSED': '{actor} closed action {code}',
    'event.ACTION_CANCELLED': '{actor} cancelled action {code}',
    'error.TITLE_REQUIRED': 'Give this a short title.',
    'error.CRITERIA_INCOMPLETE': 'Every criterion needs an answer before this can be submitted.',
    'error.EVIDENCE_PHOTO_REQUIRED': 'A photo is required when a criterion does not pass.',
    'error.INSTRUMENT_NOT_PUBLISHED': 'That audit form has not been published.',
    'error.INSTRUMENT_VERSION_CHANGED': 'The audit form changed while you were working on it.',
    'error.UNFINISHED_AUDIT_EXISTS': 'You already have an audit in progress for this area.',
    'error.MEETING_REQUIRED': 'This has to be done from a meeting.',
    'error.NO_SLA_BREACH': 'This is still inside its time limit.',
    'error.NOT_SAFETY': 'This is not marked as a safety risk.',
    'error.NO_RECURRENCE': 'This has not happened before.',
    'error.MANUAL_REQUIRES_MANAGER': 'Only a manager can escalate this by hand.',
    // -- Peristiwa notifikasi / jejak (event_code)
    // `item_event.event_code` generik Kernel A (`CREATED`) dipakai butir yang lahir
    // di luar transisi M02 — mis. baris yang ditulis rangkaian uji basis data.
    // Tanpa kunci ini, Riwayat menampilkan `event.CREATED` mentah (UAT D-06).
    'event.CREATED': '{actor} created {code}',
    'event.FINDING_CREATED': '{actor} reported {code}',
    'event.FINDING_ASSIGNED': '{actor} assigned {code} to {owner}',
    'event.FINDING_STARTED': '{actor} started work on {code}',
    'event.FINDING_DUE_CHANGED': '{actor} changed the due date of {code}',
    'event.FINDING_NOTE_ADDED': '{actor} added a note to {code}',
    'event.FINDING_INFO_REQUESTED': '{actor} asked for more information on {code}',
    'event.FINDING_SUBMITTED_FOR_VERIFICATION': '{actor} submitted {code} for verification',
    'event.FINDING_SENT_BACK': '{actor} sent {code} back',
    'event.FINDING_VERIFIED': '{actor} confirmed the fix on {code}',
    'event.FINDING_CLOSED_BY_MANAGER': '{actor} closed {code}',
    'event.FINDING_REOPENED': '{actor} reopened {code}',
    'event.FINDING_ESCALATED': '{code} was escalated to level {level, number}',
    'event.FINDING_DE_ESCALATED': '{actor} stepped {code} down',
    'event.FINDING_TAKEN_OVER': '{actor} took over {code}',
    // -- Angka dan waktu
    'time.relative.justNow': 'just now',
    'time.relative.minutes': '{n, plural, one {# minute ago} other {# minutes ago}}',
    'time.relative.hours': '{n, plural, one {# hour ago} other {# hours ago}}',
    'time.relative.days': '{n, plural, one {# day ago} other {# days ago}}',
    'time.updatedAgo': 'Updated {when}',
    // -------------------------------------------------------------------------
    // Privasi, dasar pemrosesan, dan persetujuan (UU 27/2022).
    //
    // Kunci `privacy.basis.*` dan `privacy.purpose.*` DIRAKIT dari nilai manifest,
    // dan gerbang tools/ci/cek-dasar-pemrosesan.py menuntut setiap nilai yang
    // dipakai punya kalimatnya di KEDUA bahasa. Menambah dasar hukum baru tanpa
    // kalimatnya menggagalkan pembangunan, bukan menampilkan kunci mentah di kaki
    // formulir.
    // -------------------------------------------------------------------------
    'privacy.basis.CONSENT': 'your consent',
    'privacy.basis.CONTRACT_PERFORMANCE': 'performance of the employment relationship',
    'privacy.basis.LEGAL_OBLIGATION': 'a legal obligation, including occupational safety and health',
    'privacy.basis.VITAL_INTEREST': 'protection of a vital interest of the person concerned',
    'privacy.basis.PUBLIC_TASK': 'a task carried out in the public interest',
    'privacy.basis.LEGITIMATE_INTEREST': 'a legitimate interest, weighed against the impact on the people involved',
    'privacy.purpose.ACCOUNT_ACCESS': 'giving people access to the system and keeping that access safe',
    'privacy.purpose.SECURITY_MONITORING': 'recognising misuse of an account and being able to show who did what',
    'privacy.purpose.ORG_STRUCTURE': 'setting up the organisation, its areas, and who works where',
    'privacy.purpose.OHS_COMPLIANCE': 'managing occupational safety and health',
    'privacy.purpose.IMPROVEMENT_TRACKING': 'recording, assigning, and closing findings and corrective work',
    'privacy.purpose.AUDIT_ASSESSMENT': 'assessing and auditing work areas, with their evidence',
    'privacy.purpose.MEETING_GOVERNANCE': 'running daily meetings and following up their decisions',
    'privacy.purpose.ATTENDANCE_RECORD': 'recording who attended a meeting',
    'privacy.purpose.RECOGNITION_POINTS': 'points, recognition, and standings inside your own organisation',
    'privacy.purpose.REPORTING_ANALYTICS': 'reporting and impact figures at area level',
    'privacy.purpose.FINANCE_VERIFICATION': 'verifying the savings that have been claimed',
    'privacy.purpose.SERVICE_ADMINISTRATION': 'administering the subscription, plan, and modules',
    'privacy.purpose.SUPPORT_ASSIGNMENT': 'assigning PQM consultants and recording their access',
    'privacy.purpose.OFFLINE_SYNC': 'accepting work created while a device had no signal',
    'privacy.purpose.EVIDENCE_STORAGE': 'storing photographs and documents used as evidence',
    'privacy.purpose.PROFILE_PHOTO': 'showing your profile photograph',
    'privacy.purpose.REPORT_DELIVERY': 'sending scheduled reports to the recipients named on them',
    'privacy.purpose.ACCOUNTABILITY_TRAIL': 'an audit trail that cannot be altered or removed',
    'privacy.consent.NOTICE_ACKNOWLEDGED.label': 'I have read this notice. I understand that {organisation} decides how my personal data is used here, and that PQM runs this software on its instructions.',
    'privacy.consent.NOTICE_ACKNOWLEDGED.consequence': 'Ticking this does not give permission for anything — {organisation} processes this data to run your employment and to meet its safety obligations, and that does not depend on your tick. What the tick records is that you were shown this notice before anything was recorded about you. Until it is ticked your account stays in the invited state, because we will not start without having told you.',
    'privacy.consent.AVATAR.label': 'I agree to a profile photograph of my face being shown to colleagues in this organisation.',
    'privacy.consent.AVATAR.consequence': 'Without this, your initials are shown instead. Nothing else changes, and no part of your work is affected.',
    'privacy.recipients.note': 'Email addresses typed here are processed under your organisation\'s data processing agreement. Telling the people behind them is your organisation\'s responsibility as the data controller.',
    'privacy.withdrawEffect.ACKNOWLEDGEMENT_CANNOT_BE_WITHDRAWN': 'An acknowledgement that you were told cannot be withdrawn, because it is not permission: it records that the notice was given. Questions about how your data is used go to the organisation you work for, which decides that.',
    'privacy.withdrawEffect.AVATAR_DELETED': 'Your profile photograph is removed and the stored image is marked for destruction. Your initials are shown instead.',
    // -- Pemberitahuan privasi dan layar persetujuan
    // -- Kategori data pribadi. Dibaca organisasi pelanggan di daftar pemrosesan
    //    SEBELUM menandatangani perjanjian Pasal 51, dan dienumerasi dari
    //    privacy-vocabulary.json — bukan dari daftar tulis tangan.
    'privacy.category.IDENTITY': 'Full name',
    'privacy.category.CONTACT': 'Work email address',
    'privacy.category.EMPLOYEE_NO': 'Employee number',
    'privacy.category.JOB_TITLE': 'Job title',
    'privacy.category.ORG_PLACEMENT': 'Organisational unit and work areas',
    'privacy.category.PROFILE_PHOTO': 'Profile photograph',
    'privacy.category.WORK_AREA_PHOTO': 'Photographs of work areas, which can contain colleagues',
    'privacy.category.ACTIVITY_HISTORY': 'History of what each person did, and when',
    'privacy.category.PERFORMANCE_SCORES': 'Audit scores given and received, and timeliness',
    'privacy.category.ATTENDANCE': 'Meeting attendance',
    'privacy.category.POINTS_RANKING': 'Points and standings visible to colleagues',
    'privacy.category.REQUEST_METADATA': 'IP address and user agent on every request',
    'privacy.category.DEVICE_IDENTIFIER': 'Device identifier, label, and operating system',
    'privacy.category.OFFLINE_QUEUE_COPY': 'Copies of work created while a device had no signal',
    'privacy.category.SEARCH_INDEX_COPY': 'Copies of words from titles and descriptions, in the search index',
    'privacy.category.NOTIFICATION_STATE': 'Notifications received and when they were read',
    'privacy.category.AUDIT_TRAIL': 'An audit trail that cannot be altered or removed',
    'privacy.category.BENEFIT_FIGURES': 'Claimed savings figures, and who claimed and verified them',
    // -- Klausa perjanjian pemrosesan data (Pasal 51)
    'privacy.clause.ROLES': 'Division of roles',
    'privacy.clause.CATEGORIES': 'Categories of personal data',
    'privacy.clause.PURPOSES': 'Purposes of processing',
    'privacy.clause.LOCATION': 'Where processing happens',
    'privacy.clause.SUBPROCESSORS': 'Sub-processors',
    'privacy.clause.BREACH': 'Breach notification',
    'privacy.clause.SECURITY': 'Security measures',
    'privacy.clause.RIGHTS': 'Data subject rights',
    'privacy.clause.RETENTION': 'Retention and destruction',
    'privacy.clause.AUDIT': 'Right of audit',
    // Aspek lokasi pemrosesan (butir P9, Pasal 56). Ia berkunci per ASPEK dan bukan
    // satu kalimat, karena basis data, penyimpanan objek, cache, layanan push, dan
    // cadangan dapat berada di tempat yang berbeda — dan menyatakannya sebagai satu
    // kalimat menyembunyikan justru yang paling mungkin berbeda.
    'privacy.location.DATABASE': 'Database',
    'privacy.location.OBJECT_STORE': 'Photograph and document storage',
    'privacy.location.CACHE': 'Temporary working copies and queues',
    'privacy.location.PUSH_NOTIFICATION': 'Push notification delivery',
    'privacy.location.BACKUP': 'Backups',
    // -- Daftar pemrosesan dan perjanjian organisasi
    'register.title': 'What this software processes',
    'register.subtitle': 'Every category of personal data ASSIST processes, what for, and on what basis. This list is assembled from the modules themselves, so it cannot drift from what actually happens.',
    'register.moduleHeading': 'By module',
    'register.categoryHeading': 'By category of personal data',
    'register.purposeHeading': 'Purposes',
    'register.basisHeading': 'Legal bases relied on',
    'register.locationHeading': 'Where processing happens',
    'register.location.undeclared': 'Not yet stated. It is stated in the agreement between your organisation and PQM, and this page is updated when it is decided.',
    'register.subprocessorHeading': 'Sub-processors',
    'register.subprocessor.none': 'None listed yet. Adding one requires prior notice to your organisation, and this page is the list the agreement refers to.',
    'register.notCollected': 'Not processed at all',
    'register.notCollected.body': 'No national identity number, no health data, no salary or personal financial data, no biometric data, no religion, political opinions, sexual orientation, criminal records, or children\'s data. No face recognition, no automatic tagging of people in photographs, and no searching for photographs by person.',
    'register.writeOperations': '{n, plural, one {# write operation} other {# write operations}}',
    'agreement.title': 'Data processing agreement',
    'agreement.version': 'Version {version, number}',
    'agreement.acceptedBy': 'Accepted by {name}, {title}, on {date}',
    'agreement.notAccepted': 'Not yet accepted',
    'agreement.pending.title': 'This organisation is not open yet',
    'agreement.pending.body': 'The data processing agreement between your organisation and PQM has not been accepted yet, so nothing can be recorded about anyone here. Someone authorised at your organisation accepts it once; PQM records who, when, and which version. Ask your manager or whoever arranged this software.',
    'organisation.title': 'Your organisation',
    'organisation.subtitle': 'What was agreed, what is processed, and who did what here.',
    'organisation.auditHeading': 'Your own audit trail',
    'organisation.auditNote': 'Every action recorded for your organisation, including access by PQM staff. It cannot be altered or removed, by anyone.',
    'organisation.auditEmpty': 'Nothing has been recorded yet.',
    'organisation.controllerIs': '{organisation} decides how this data is used. PQM processes it on that instruction.',
    'organisation.auditCodeNote': 'Event names are shown exactly as they are stored, which is also how they appear in an export.',
    'organisation.actor.TENANT': 'Someone at your organisation',
    'organisation.actor.PLATFORM': 'A PQM platform administrator',
    'organisation.actor.CONSULTANT': 'A PQM consultant assigned to you',
    'organisation.actor.SYSTEM': 'A scheduled job',
    'organisation.actor.ANONYMOUS': 'Someone not signed in',
    // -- Identitas organisasi (gelombang 28 September 2026)
    //
    // Frame `216:2030` (EN) / `216:2294` (ID). Naskahnya diambil dari frame itu, bukan
    // dikarang di sini: kalimat panel logo dan kalimat "hanya PQM yang dapat mengubah"
    // adalah keputusan rancangan, dan menulis ulang keduanya di kode berarti dua naskah
    // yang akan berpisah.
    'identity.title': 'Organisation identity',
    'identity.subtitle': 'How your organisation appears inside ASSIST — logo, accent, display name, time zone and default language.',
    'identity.save': 'Save changes',
    'identity.discard': 'Discard',
    'identity.saved': 'Saved.',
    'identity.logo.heading': 'Organisation logo',
    'identity.logo.label': 'Logo mark',
    'identity.logo.hint': 'PNG or WebP · max 1 MB · longest edge 1024 px',
    'identity.logo.choose': 'Choose file',
    'identity.logo.chooseAnother': 'Choose another file',
    'identity.logo.replace': 'Replace',
    'identity.logo.remove': 'Remove',
    'identity.logo.uploading': 'Uploading logo',
    'identity.logo.verifying': 'Checking the file',
    'identity.logo.verifyingBody': 'The bytes are being checked. The logo appears once it is proven to be a PNG or WebP image.',
    'identity.logo.verified': 'Verified',
    'identity.logo.verifiedDetail': 'Checked from its bytes · {size} · {width} × {height} px',
    'identity.logo.none': 'No logo yet. Nothing is missing on any screen — the ASSIST mark stands in its place.',
    'identity.logo.rejected': 'File rejected',
    'identity.logo.whereShown': 'Shown on your dashboard header and to PQM staff on your organisation page. It is never shown to another organisation.',
    'identity.logo.wrongType': 'That file is not a PNG or WebP image. Nothing was uploaded, and the logo you have now is unchanged.',
    'identity.logo.tooLarge': 'That file is larger than 1 MB. Nothing was uploaded, and the logo you have now is unchanged.',
    'identity.name.heading': 'Display name',
    'identity.name.hint': 'The name your people read in ASSIST. The legal entity name on the signed data processing agreement is separate, and only PQM can change it.',
    'identity.locale.heading': 'Language and time zone',
    'identity.locale.label': 'Default language',
    'identity.timezone.label': 'Time zone',
    'identity.locale.hint': 'The default for people who have not chosen their own. Each person can still switch language from the top bar.',
    'identity.accent.heading': 'Accent colour',
    'identity.accent.hint': 'Status colours and text colours never change. A colour that cannot be told apart from the page is refused with a reason, not accepted quietly.',
    'identity.accent.contrastOk': 'Contrast {ratio}:1 — passes',
    'identity.pqm.heading': 'Only PQM can change these',
    'identity.pqm.legalName': 'Legal entity',
    'identity.pqm.address': 'Address',
    'identity.pqm.plan': 'Plan',
    'identity.pqm.hint': 'The legal entity name appears on the data processing agreement that has already been signed, so it is changed by PQM and written to the audit trail — not here.',
    'identity.platform.heading': 'Contract-binding identity',
    'identity.platform.subtitle': 'Identity of the organisation — what binds the contract, and what the organisation itself controls.',
    'identity.platform.legalName': 'Legal entity name',
    'identity.platform.slug': 'Address (slug)',
    'identity.platform.hint': "These three are PQM's to change: the legal entity name appears on the signed data processing agreement, and the address is the door every one of this organisation's people walks through. A manager of the organisation cannot change them, and neither can a consultant.",
    'identity.platform.controlled': 'What the organisation controls',
    'identity.platform.controlledHint': 'Read-only here. A manager of the organisation changes these from their own Organisation identity screen; PQM sees them so support and the customer are looking at the same thing.',
    'error.ACCENT_HEX_INVALID': 'An accent colour is six hexadecimal digits after a #, for example #2251FF.',
    'error.ACCENT_CONTRAST_TOO_LOW': 'That colour is {ratio}:1 against {surface} and needs at least {minimum}:1. It would be almost invisible on the page. Pick a darker or more saturated shade.',
    'error.TIMEZONE_NOT_SUPPORTED': 'That time zone is not available yet. Supported: {supported}.',
    'error.PLATFORM_STATE_CONFLICT': 'That cannot be done while this is {from}. Read the page again — someone else may have ' +
        'changed it.',
    'error.JOB_NOT_RETRYABLE': 'Job {job_code} is announced by a module but has no worker behind it yet, so re-running ' +
        'it would queue work nobody picks up.',
    'error.AUDIT_EXPORT_TOO_LARGE': 'That range covers {found, number} rows and the limit for one export is ' +
        '{limit, number}. Narrow the dates and export again.',
    'error.LOGO_WRONG_TYPE': 'A logo is a PNG or a WebP image. SVG is not accepted.',
    'error.TENANT_AGREEMENT_PENDING': 'Your organisation has not accepted the data processing agreement yet, so nothing can be recorded here.',
    'error.NOTICE_ACKNOWLEDGEMENT_REQUIRED': 'Confirm that you have read this notice before continuing.',
    'error.AGREEMENT_ALREADY_ACCEPTED': 'This organisation has already accepted the agreement.',
    'privacy.notice.title': 'Privacy notice',
    'privacy.notice.link': 'Privacy notice',
    'privacy.notice.version': 'Version {version, number}',
    'privacy.notice.effectiveFrom': 'In effect from {date}',
    'privacy.notice.publishedBy': 'Published by {publisher}',
    'privacy.notice.otherLanguage': 'Read this in the other language',
    'privacy.notice.unavailable': 'The privacy notice could not be loaded just now.',
    'privacy.basisNote.label': 'Why this is allowed',
    'privacy.basisNote.sentence': 'Personal data entered here is processed on the basis of {basis}.',
    'privacy.basisNote.purposes': 'For: {purposes}',
    'privacy.basisNote.more': 'What this is used for',
    'privacy.basisNote.photoGuidance': 'Photographs are meant to show the condition of an area. Avoid capturing the faces of colleagues.',
    'accept.title': 'Set your password',
    'accept.subtitle': 'Your organisation has invited you to {organisation}.',
    'accept.password': 'New password',
    'accept.passwordConfirm': 'Repeat the password',
    'accept.password.hint': 'At least twelve characters.',
    'accept.consentHeading': 'Before anything is recorded about you',
    'accept.controllerLine': '{organisation} decides how your personal data is used here. PQM supplies and runs this software on its instructions. Questions about your data go to {organisation} first — ask your manager or its human resources team.',
    'accept.submit': 'Set password and continue',
    'accept.withoutConsent': 'Continue without confirming',
    'accept.withoutConsentHeading': 'What ticking this does, and does not do',
    /**
     * PANEL AKIBAT — naskahnya apa adanya dari Figma `178:1182` / `190:1730`.
     *
     * Ia SELALU TERLIHAT, dan itu menggantikan layar penolakan yang pernah ada di sini.
     * Perancang menolak layar terpisah dengan alasan yang tidak dapat dibantah: layar
     * penolakan hanya muncul SETELAH orang menekan tombol, artinya ia baru tahu akibatnya
     * setelah salah memilih. Panel ini mengatakan tiga hal sebelum keputusan diambil —
     * akunnya tetap INVITED dan belum bisa masuk; TIDAK ADA yang dihapus dan tautannya
     * tetap berlaku; dan ke mana ia bertanya. Tidak ada jalan buntu, karena tidak ada pintu
     * yang tertutup di belakangnya.
     */
    'accept.ifNotTicked.heading': 'IF YOU DO NOT TICK THIS',
    'accept.ifNotTicked.body': 'Your account stays INVITED: you cannot sign in, and no work can be assigned to you. ' +
        'Nothing is deleted — this link stays open until {expires}, and you can come back to ' +
        'it. Ask your manager or the HR team at {organisation} if anything here is unclear.',
    'accept.ifNotTicked.bodyNoExpiry': 'Your account stays INVITED: you cannot sign in, and no work can be assigned to you. ' +
        'Nothing is deleted — this link stays open and you can come back to it. Ask your ' +
        'manager or the HR team at {organisation} if anything here is unclear.',
    'accept.needsTick': 'Tick the box above to continue. Nothing else is missing, and nothing is lost if you ' +
        'leave this page.',
    'accept.declined.title': 'Nothing has been recorded about you',
    'accept.declined.body': 'Your password has not been set and your account is still in the invited state. Nothing has been recorded about you. You can come back to this page from your invitation at any time.',
    'accept.invalid': 'This invitation is no longer valid. Ask your manager for a new one.',
    'consentSettings.title': 'Privacy and your data',
    'consentSettings.backToAccount': '‹  Account & notifications',
    /**
     * Panel RINGKAS di `/settings/account` (Figma `186:1228`), dengan tautan `Manage` ke
     * bagian penuh `188:1225`. Dua baris ringkas dan satu tautan — bukan seluruh isinya,
     * karena memerasnya ke rel kanan akan memperkecil justru bagian yang paling bermakna
     * secara hukum: riwayat append-only.
     */
    'account.privacyPanel.title': 'Privacy and your data',
    'account.privacyPanel.noticeVersion': 'Notice you have read: version {version}',
    'account.privacyPanel.noticeUnknown': 'You have not acknowledged a privacy notice yet',
    'account.privacyPanel.consentCount': '{count, number} consent records',
    'account.privacyPanel.manage': 'Manage',
    'consentSettings.subtitle': 'What you have agreed to, and how to change it.',
    'consentSettings.granted': 'Agreed on {date}',
    'consentSettings.withdrawn': 'Withdrawn on {date}',
    'consentSettings.never': 'Not agreed',
    'consentSettings.withdraw': 'Withdraw',
    'consentSettings.grant': 'Agree',
    'consentSettings.confirmTitle': 'Withdraw this consent?',
    'consentSettings.noneYet': 'There is nothing recorded here yet.',
    'consentSettings.historyTitle': 'Record of your consents',
    'consentSettings.noticeVersion': 'Privacy notice version {version, number}',
    'error.CONSENT_REQUIRED': 'This needs your agreement before it can be recorded.',
    'error.CONSENT_ALREADY_WITHDRAWN': 'That consent has already been withdrawn.',
    'error.CONSENT_PURPOSE_UNKNOWN': 'That is not something consent is asked for.',
    'error.INVITATION_INVALID': 'This invitation is no longer valid.',
    'error.ACKNOWLEDGEMENT_CANNOT_BE_WITHDRAWN': 'Confirming that you had been told is not permission, so there is nothing to withdraw. '
        + 'Your account and your work are not affected.',
    'error.ATTACHMENT_FILE_NOT_FOUND': 'That file cannot be attached. Upload it again from this organisation.',
    'error.ATTACHMENT_FILE_NOT_USABLE': 'That file has not finished uploading, or it was quarantined because its contents did not match what was declared.',
    'error.ATTACHMENT_FILE_NOT_VERIFIED': 'That file is still being checked. Try again in a moment — checking takes seconds.',
    'error.PHOTO_METADATA_PENDING': 'This photograph is still being prepared. Try again in a moment.',
    'error.LEADERBOARD_SCOPE_FORBIDDEN': 'Standings are only shown inside your own organisation.',
    'error.PROCESSING_BASIS_MISSING': 'This could not be recorded because no processing basis was declared.',
    // --- PERBAIKAN G3 ---------------------------------------------------------
    // `PHOTO_TOO_LARGE` was promised by the contract and never sent: the database
    // constraint message reached the screen instead (UAT U2-08). The sentence names the
    // numbers AND what to do, because "some of what you entered needs a change" on the
    // most frequent failure in the product is not an answer.
    'error.PHOTO_TOO_LARGE': 'That photograph is too large. The limit is {max_byte_size, number} bytes and ' +
        '{max_pixel_edge, number} pixels on the longest side. Take it again from the app camera, ' +
        'which resizes it for you.',
    'error.FILE_TYPE_NOT_ALLOWED': 'That kind of file cannot be attached here. Photographs (JPEG, PNG, WebP), PDF, CSV and ' +
        'spreadsheets are accepted.',
    'error.ITEM_CATEGORY_UNKNOWN': 'That category does not exist in this organisation. Pick one from the list.',
    'error.NOT_IMPLEMENTED': 'This part of ASSIST is not built yet. It is in your plan and it is coming; nothing you ' +
        'did is wrong.',
    'error.PASSWORD_TOO_WEAK': 'That password cannot be used. It must be at least {min_length, number} characters and ' +
        'must not be an obvious one, a keyboard run, or built from your own name, email or ' +
        'employee number. Three unrelated words are easy to remember and hard to guess.',
    'error.USER_ALREADY_ACTIVE': 'That person already has a working account here, so a new invitation would let whoever ' +
        'holds the link change their password. Ask them to use "Forgot password" instead.',
    'accept.controllerUnknown': 'the organisation that invited you',
    'mail.pending.title': 'Automatic email is not switched on yet',
    'mail.pending.body': 'The message has been queued but nothing will be sent until an email provider is ' +
        'configured. Pass the details on yourself, and tell whoever set up ASSIST.',
    'invite.title': 'Invite someone',
    'invite.sent': 'Invitation recorded for {email}.',
    'invite.queued': 'Invitation recorded for {email}, but no email was sent — see below.',
    'readiness.title': 'Before this organisation can be used',
    'readiness.step.ORG_UNIT_MISSING': 'Create at least one department.',
    'readiness.step.LOCATION_MISSING': 'Create at least one area.',
    'readiness.step.ITEM_CATEGORY_MISSING': 'Plant the 5R categories.',
    'readiness.step.WORKFLOW_MISSING': 'Publish the workflow definitions.',
    'readiness.step.NO_ONE_INVITED': 'Invite the first person.',
    'readiness.ready': 'This organisation is ready to be used.',
    'finding.photo.add': 'Add a photograph',
    'finding.photo.choose': 'Choose a photograph',
    'finding.photo.uploading': 'Uploading…',
    'finding.photo.checking': 'Checking the file…',
    // Label peran diserahkan SUDAH DITERJEMAHKAN oleh pemanggilnya, bukan lewat `select`
    // ICU: cabang `select` menaruh kata Inggris di dalam nilai katalog Indonesia, dan uji
    // kesetaraan placeholder — dengan benar — menolaknya.
    'finding.photo.alt': '{role} photograph of {code}',
    'finding.photo.unavailable': 'This photograph cannot be shown yet.',
    'dashboard.decisionQueue.why': 'Why it is here',
    'dashboard.decisionQueue.open': 'Open the verification queue',
    'dashboard.queueReason.AWAITING_VERIFICATION': 'Waiting for your decision',
    'dashboard.queueReason.SENT_BACK': 'Sent back, and stopped there',
    'dashboard.queueReason.OVERDUE': 'Past its deadline',
    'dashboard.stat.areaCompletion': 'Area completion',
    'dashboard.areaCompletion.value': '{done, number} of {total, number} areas with nothing open',
    // -------------------------------------------------------------------------
    // GELOMBANG LAYAR KONSOL — 21 layar, dan setiap kalimat di keduanya.
    //
    // `catalog.test.ts` menolak kunci yang hanya punya satu bahasa DAN placeholder
    // ICU yang berbeda antar bahasa. Keduanya bukan formalitas: peta §7 menulis
    // "tidak ada string yang hanya punya satu bahasa", dan sebuah placeholder yang
    // hilang di salah satunya menghasilkan `{code}` MENTAH di layar — yaitu UAT U2-05,
    // yang lolos gerbang teks antarmuka karena gerbang itu memeriksa keberadaan kunci,
    // bukan isinya.
    // -------------------------------------------------------------------------
    'state.fieldApp.meanwhile': 'While the field app is being built, two console screens are yours: report a finding, ' +
        'and look at the findings you reported.',
    'state.fieldApp.myFindings': 'My findings',
    'actorKind.TENANT': 'Organisation user',
    'actorKind.PLATFORM': 'Platform admin',
    'actorKind.CONSULTANT': 'PQM consultant',
    'actorKind.SYSTEM': 'System',
    /**
     * CACAT 4. "Not signed in" reads as a failed attempt. This label is also the filter option
     * on the audit screen, where "filter by people who failed to sign in" is not what the
     * filter gives you: it gives rows written before any session existed.
     */
    'actorKind.ANONYMOUS': 'No session',
    'report.title': 'Report a finding',
    'report.subtitle': 'What you saw, where it was, and one photograph.',
    'report.field.title': 'What did you see?',
    'report.field.title.hint': 'One sentence is enough. Write it the way you would say it.',
    'report.field.category': 'Category',
    'report.field.area': 'Location',
    /**
     * `report.field.severity` DIHAPUS, dan penghapusannya adalah instruksi perancang yang
     * ditulis apa adanya: "hapus medan keparahan dari ReportFinding.tsx, jangan pindahkan".
     * Figma `11:98` maupun `169:1116` tidak punya medan itu; kode yang menambahkannya.
     * Alasannya rancangan, bukan teknis: pelapor sering operator yang melihat sesuatu, dan
     * meminta ia menilai "seberapa serius" pada detik pertama menghasilkan penilaian yang
     * tidak dapat dibandingkan antar-orang. `Priority` sudah menjadi kolom di Daftar Temuan
     * (`8:2`) — tempatnya di triase, ditetapkan oleh yang menugaskan.
     *
     * `severity.*` dan `finding.severity` TETAP ADA: Detail Temuan dan Daftar Temuan
     * menampilkannya, dan yang dihapus adalah medan pengisiannya — bukan datanya.
     */
    'report.field.description': 'What happened',
    'report.field.description.hint': 'Write what you saw, not who you blame.',
    'report.field.description.counter': '{used, number} / {max, number} characters',
    'report.field.choose': 'Choose one',
    'report.field.photo': 'Evidence photo',
    'report.field.photo.hint': 'A photo is what makes this verifiable later. Faces of people who are not involved can ' +
        'be blurred before you send.',
    'report.field.category.hint': 'Safety stops the clock: it goes to the area manager straight away and it is the only ' +
        'category that turns a row red.',
    'report.field.area.hint': 'On the field app this is filled by scanning the QR tag on the post.',
    'report.photo.replace': 'Replace the photograph',
    'report.photo.add': 'Add photo',
    'report.photo.count': '{used, number} of {max, number} · up to {kb, number} KB each',
    'report.breadcrumb': '‹  Findings',
    'report.draftNote': 'Draft · kept for 7 days · same order as the field app',
    'report.points.title': 'Estimated points',
    'report.points.provisional': '{points, number} points, provisional',
    'report.points.breakdown': '{category} ×1.0 · zone {zone, number} ×1.0 · photo attached ×1.2',
    'report.points.note': 'They become final only when a leader confirms the fix. Nothing is awarded for reporting ' +
        'alone.',
    'report.next.title': 'What happens next',
    'report.next.1': 'It joins the area queue and the area leader is notified.',
    'report.next.2': 'A leader assigns an owner and a due date.',
    'report.next.3': 'Closing it needs an after photo — no photo, no close.',
    'report.submit': 'Submit finding',
    'report.submit.blocked': 'Fill in what you saw, the category, the area, and add one photograph. Then this ' +
        'becomes available.',
    'report.uploadFailed': 'The photograph did not finish uploading. Try again; nothing you typed was lost.',
    'verify.title': 'Verification queue',
    'verify.subtitle': 'Fixes waiting for someone to look at the evidence.',
    'verify.empty.title': 'Nothing is waiting',
    'verify.empty.body': 'Every fix that was submitted has been decided. This is the state you want this screen ' +
        'in.',
    'verify.column.closedBy': 'Fixed by',
    'verify.column.decide': 'Decide',
    'verify.action.comparePhotos': 'Look at the photographs',
    'verify.action.confirm': 'Confirm the fix',
    'verify.action.sendBack': 'Send it back',
    'verify.confirmed': '{code} is closed. Points are final for whoever reported it and whoever fixed it.',
    'verify.sentBack': '{code} went back to the person who fixed it, with your reason.',
    'verify.sendBack.title': 'Send it back',
    'verify.sendBack.describe': '{code} goes back to the person who fixed it. Your reason is what they will read.',
    'verify.sendBack.reasonHint': 'Say what is still missing, not that it is wrong.',
    'verify.footnote': 'Confirming a fix is the only way points move from provisional to final. Read as of ' +
        '{today}.',
    'locations.title': 'Locations & areas',
    'locations.subtitle': 'Where work happens, and how each area is holding up.',
    'locations.add': 'Add an area',
    'locations.add.describe': 'A code, a name, and which unit it belongs to.',
    'locations.add.codeHint': 'Short, and the same one printed on the QR label.',
    'locations.add.description': 'What happens here',
    'locations.added': 'Area {code} is ready to receive findings.',
    'locations.column.code': 'Code',
    'locations.column.name': 'Name',
    'locations.column.unit': 'Unit',
    'locations.column.open': 'Open findings',
    'locations.column.completion': 'Clear',
    'locations.column.state': 'State',
    'locations.column.action': 'Action',
    'locations.completion.value': '{pct, number}%',
    'locations.state.ACTIVE': 'Active',
    'locations.state.INACTIVE': 'Inactive',
    'locations.deactivate': 'Deactivate',
    'locations.deactivate.title': 'Deactivate this area?',
    'locations.deactivate.describe': 'Area {code} stops accepting new findings. Everything already recorded there stays ' +
        'readable, and you can activate it again.',
    'locations.deactivated': 'Area {code} is now inactive. Its history is untouched.',
    'locations.empty.title': 'No areas yet',
    'locations.empty.body': 'Findings are always reported against an area, so the first area comes first.',
    'locations.qrPending': 'Printing QR labels is not available from here yet. Ask whoever set up ASSIST to print ' +
        'them.',
    'users.title': 'Users & roles',
    'users.subtitle': 'Who works here, and what each of them may do.',
    'users.invite': 'Invite someone',
    'users.invite.describe': 'They receive a one-time link and set their own password.',
    'users.column.person': 'Person',
    'users.column.email': 'Work email',
    'users.column.role': 'Role',
    'users.column.employeeNo': 'Employee no.',
    'users.column.state': 'State',
    'users.state.ACTIVE': 'Active',
    'users.state.INVITED': 'Invited, not signed in yet',
    'users.state.DISABLED': 'Disabled',
    'users.state.LOCKED': 'Locked',
    'users.field.fullName': 'Full name',
    'users.field.email': 'Email',
    'users.nameHidden': 'Name not visible to you',
    'users.invitations.title': 'Invitations not used yet',
    'users.invitations.expires': 'Expires',
    'users.empty.title': 'Nobody here yet',
    'users.empty.body': 'Invite the first person. Everything else in ASSIST needs someone to do it.',
    'users.changesPending': 'Changing a role, limiting someone to certain areas, disabling an account and signing ' +
        'someone out of every device are not available from this screen yet.',
    'userDetail.title': 'Person',
    'userDetail.back': 'Back to the list',
    'userDetail.jobTitle': 'Job title',
    'userDetail.lastLogin': 'Last signed in',
    'userDetail.neverSignedIn': 'Has not signed in yet',
    'userDetail.points': 'Points this person earned',
    'userDetail.points.none': 'No points recorded yet.',
    'userDetail.changesPending': 'This screen reads. Changing a role or scope, and signing this person out of every ' +
        'device, are not available here yet.',
    'search.title': 'Search results',
    'search.subtitle': 'Everything matching “{query}” that you are allowed to see.',
    'search.filter.all': 'Everything',
    'search.type.FINDING': 'Findings',
    'search.type.ITEM': 'Findings',
    'search.type.USER': 'People',
    'search.type.LOCATION': 'Areas',
    'search.type.ASSESSMENT': 'Audits',
    'search.type.DOCUMENT': 'Documents',
    'search.type.IDEA': 'Kaizen ideas',
    'search.type.PROJECT': 'Projects',
    'search.none.title': 'Nothing matched',
    'search.none.body': 'Try a shorter word, or a finding code such as TMN-2419.',
    'search.tooShort.title': 'Type a little more',
    'search.tooShort.body': 'Search needs at least three characters to be useful.',
    'search.isolationNote': 'Search only reaches your own organisation, and only the areas you are allowed to see. ' +
        'Something missing from this list does not mean it does not exist.',
    'search.moreResults': 'Load more results',
    'huddle.title': 'Daily 10-minute huddle',
    'huddle.subtitle': 'Ten minutes, standing up, on what is actually blocking the line.',
    'huddle.schedule': 'Schedule a huddle',
    'huddle.schedule.describe': 'One huddle per unit per slot.',
    'huddle.schedule.unit': 'Unit',
    'huddle.schedule.when': 'When',
    'huddle.scheduled': 'The huddle is on the agenda.',
    'huddle.state.SCHEDULED': 'Not started',
    'huddle.state.RUNNING': 'Running',
    'huddle.state.OVERRUN': 'Over time',
    'huddle.state.CLOSED': 'Finished',
    'huddle.facet.NO_ATTENDEES': 'Nobody marked present',
    'huddle.facilitator': 'Led by {name}',
    'huddle.timer.label': 'Time used',
    'huddle.timer.running': '{elapsed} of {box}',
    'huddle.timer.over': '{elapsed} — past the {box} box',
    'huddle.agenda': 'Agenda — {discussed, number} of {total, number} discussed',
    'huddle.agenda.empty': 'Nothing on the agenda yet. Pick from the list below, or write your own item.',
    'huddle.item.add': 'Add an agenda item',
    'huddle.item.add.describe': 'Something to discuss that is not one of the open findings.',
    'huddle.item.title': 'What to discuss',
    'huddle.item.finding': 'Finding',
    'huddle.item.outcome': 'Outcome',
    'huddle.item.markDiscussed': 'Mark as discussed…',
    'huddle.item.notDiscussed': 'Not discussed',
    'huddle.outcome.RESOLVED': 'Resolved',
    'huddle.outcome.CARRIED_OVER': 'Carried over',
    'huddle.outcome.ACTION_CREATED': 'Action created',
    'huddle.outcome.NOTED': 'Noted',
    'huddle.candidates': 'Open findings worth discussing',
    'huddle.candidates.add': 'Put it on the agenda',
    'huddle.attendance': 'Attendance — {present, number} of {total, number} present',
    'huddle.attendance.none': 'Nobody has been recorded yet.',
    'huddle.attendance.present': 'Present',
    'huddle.attendance.absent': 'Absent',
    /**
     * DITEMUKAN GERBANG JANJI-KENDALI pada jalan pertamanya. Kalimat lamanya berbunyi
     * "Minutes have been sent to everyone who attended." — dan tidak ada satu pun baris
     * kode di repositori ini yang mengantre surel notulen: `enqueueMail()` dipanggil
     * TIGA kali (undangan, atur ulang kata sandi, undangan manajer pertama), tidak satu
     * pun dari modul rapat. Transport produksinya `queue`, yang tidak mengirim apa pun.
     *
     * Ia kelas UAT D-02 apa adanya, dan ia hidup di produksi sampai hari ini.
     */
    'huddle.minutes.ready': 'Minutes are available here. Nobody was emailed — ASSIST cannot send email in this ' +
        'version, so pass the link on yourself.',
    'huddle.minutes.pending': 'Minutes are not generated in this version yet. What was discussed is recorded here and ' +
        'in the audit trail.',
    'huddle.empty.title': 'No huddles yet',
    'huddle.empty.body': 'Schedule the first one. Ten minutes a day is the whole idea.',
    'escalation.title': 'Escalation & spotlight',
    'escalation.subtitle': 'What has waited too long, and what deserves saying out loud.',
    'escalation.readOnly': 'You can read this screen. Changing the ladder and the spotlight is the area manager’s.',
    'escalation.open': 'Escalated right now',
    'escalation.ladder': 'The four-step ladder',
    'escalation.level': 'Level {level, number}',
    'escalation.level.caption': '{role}, within {hours, number} hours',
    'escalation.column.level': 'Level',
    'escalation.column.trigger': 'Why',
    'escalation.column.target': 'Goes to',
    'escalation.column.due': 'Due at this level',
    'escalation.column.sla': 'Time allowed',
    'escalation.column.triggers': 'Triggers',
    'escalation.sla.value': '{hours, number} hours',
    'escalation.trigger.SLA_BREACH': 'Past its deadline',
    'escalation.trigger.RECURRENCE_90D': 'Came back within 90 days',
    'escalation.trigger.SAFETY_FLAG': 'Flagged as a safety risk',
    'escalation.trigger.MANUAL': 'Raised by a person',
    'escalation.target.LDR': 'Area leader',
    'escalation.target.MGR': 'Area manager',
    'escalation.target.ORG_UNIT_LEAD': 'Plant manager',
    'escalation.target.DISTRIBUTION': 'Steering committee (notified, no system rights)',
    'escalation.target.OPR': 'Operator',
    'escalation.target.AUD': 'Internal auditor',
    'escalation.target.FAC': 'Improvement facilitator',
    'escalation.target.KON': 'PQM consultant',
    'escalation.target.PLT': 'Platform administrator',
    'escalation.target.ROLE': 'By role',
    'escalation.none.title': 'Nothing is escalated',
    'escalation.none.body': 'Nothing has passed the time allowed at its level. This is the good state, not an empty ' +
        'one.',
    'escalation.rules.edit': 'Change the ladder',
    'escalation.rules.describe': 'Time allowed must not get shorter as the level goes up, or it stops being a ladder. ' +
        'Every change is recorded with its before and after.',
    'escalation.rules.slaFor': 'Level {level, number} — {role}, hours',
    'escalation.rules.saved': 'The ladder is saved, and the change is in the audit trail.',
    'spotlight.title': 'Spotlight this month',
    'spotlight.empty': 'Nothing in the spotlight yet.',
    'spotlight.remove': 'Take it down',
    'spotlight.remove.describe': 'It stops being shown. The record of it stays, including who took it down and when.',
    'spotlight.removed': 'Taken down.',
    'spotlight.subject.USER': 'Person',
    'spotlight.subject.ITEM': 'Finding',
    'spotlight.subject.LOCATION': 'Area',
    'points.title': 'Points & rules',
    'points.subtitle': 'What earns points, who approved each award, and where the ranking comes from.',
    'points.rules': 'Rules',
    'points.ledger': 'Ledger',
    'points.ledger.empty': 'No points have been awarded yet.',
    'points.leaderboard': 'Ranking for {month}',
    'points.leaderboard.empty': 'Nobody has final points this month yet.',
    'points.leaderboard.finalOnly': 'The ranking counts final points only. Provisional points are shown separately so the ' +
        'ranking cannot be moved by sending in many reports.',
    'points.rule.add': 'Add a rule',
    'points.rule.add.describe': 'A rule listens for one event and awards points to one person in it. Only events the ' +
        'system actually publishes are offered.',
    'points.rule.codeHint': 'Capitals, digits and underscores. It appears on every ledger row.',
    'points.rule.capValue': 'Ceiling for that period',
    'points.rule.created': 'Rule {code} is active.',
    'points.rule.changed': 'Rule {code} is updated, and the change is in the audit trail.',
    'points.rule.pause': 'Pause',
    'points.rule.resume': 'Resume',
    'points.rule.pause.describe': 'Rule {code} stops awarding. Points already given stay where they are.',
    'points.rules.empty.title': 'No rules yet',
    'points.rules.empty.body': 'Without a rule, nothing earns points and the ranking stays empty.',
    'points.column.rule': 'Rule',
    'points.column.trigger': 'Triggered by',
    'points.column.subject': 'Awarded to',
    'points.column.points': 'Points',
    'points.column.cap': 'Ceiling',
    'points.column.state': 'State',
    'points.column.person': 'Person',
    'points.column.approval': 'Approval',
    'points.column.awardedAt': 'Awarded',
    'points.column.rank': 'Rank',
    'points.column.final': 'Final',
    'points.column.provisional': 'Provisional',
    'points.trigger.FINDING_CREATED': 'A finding was reported',
    'points.trigger.FINDING_VERIFIED': 'A fix was confirmed',
    'points.trigger.FINDING_CLOSED': 'A finding was closed',
    'points.trigger.ASSESSMENT_SUBMITTED': 'An audit was submitted',
    'points.trigger.IDEA_APPROVED': 'A kaizen idea was approved',
    'points.trigger.IDEA_BENEFIT_VERIFIED': 'A saving was verified by finance',
    'points.trigger.MEETING_CLOSED': 'A huddle was closed',
    'points.subject.ACTOR': 'Whoever did it',
    'points.subject.REPORTER': 'Whoever reported it',
    'points.subject.OWNER': 'Whoever fixed it',
    'points.subject.VERIFIER': 'Whoever verified it',
    'points.awardState.PROVISIONAL': 'provisional until verified',
    'points.awardState.FINAL': 'final',
    'points.capPeriod.NONE': 'No ceiling',
    'points.capPeriod.DAY': 'per day',
    'points.capPeriod.WEEK': 'per week',
    'points.capPeriod.MONTH': 'per month',
    'points.cap.none': 'None',
    'points.cap.value': '{value, number} {period}',
    'points.ruleState.ACTIVE': 'Active',
    'points.ruleState.PAUSED': 'Paused',
    'points.ruleState.PENDING_APPROVAL': 'Waiting for approval',
    'points.ruleState.CANCELLED': 'Cancelled',
    'points.ledgerState.PROVISIONAL': 'Provisional',
    'points.ledgerState.FINAL': 'Final',
    'points.ledgerState.REVERSED': 'Reversed',
    'points.approval.NOT_REQUIRED': 'Not needed',
    'points.approval.PENDING': 'Waiting for you',
    'points.approval.APPROVED': 'Approved',
    'points.approval.REJECTED': 'Rejected',
    'points.award.approve': 'Approve',
    'points.award.approved': 'Approved. The points are final.',
    'points.award.reverse': 'Reverse it',
    'points.award.reverse.describe': 'The points stop counting, and the row stays with your reason on it. Whoever loses the ' +
        'points will be able to read why.',
    'points.award.reversed': 'Reversed, with your reason recorded.',
    'points.stat.activeRules': 'Active rules',
    'points.stat.pending': 'Waiting for approval',
    'points.stat.pending.caption': 'These do not count until you decide.',
    'points.stat.finalOnPage': 'Final points listed',
    'points.stat.finalOnPage.caption': 'From the {rows, number} rows shown below, not the whole ledger.',
    'points.stat.topRank': 'Top of the ranking',
    'points.stat.topRank.caption': '{points, number} final points',
    'reports.title': 'Reports & analytics',
    'reports.subtitle': 'The numbers, and exactly what is behind each of them.',
    'reports.period.from': 'From',
    'reports.period.to': 'To',
    'reports.money': '{currency} {amount}',
    'reports.stat.verifiedImpact': 'Verified impact',
    'reports.stat.verifiedImpact.caption': 'From {claims, number} claims signed off by finance.',
    'reports.stat.excluded': 'Claims left out',
    'reports.stat.excluded.caption': '{currency} {amount} waiting for finance to sign it off.',
    'reports.stat.opened': 'Findings opened',
    'reports.stat.closed': 'Findings closed',
    'reports.stat.period': '{from} to {to}',
    'reports.byCategory': 'By 5S category',
    'reports.column.opened': 'Opened',
    'reports.column.closed': 'Closed',
    'reports.column.name': 'Report',
    'reports.column.kind': 'What it reports',
    'reports.column.schedule': 'Schedule',
    'reports.column.recipients': 'Recipients',
    'reports.column.lastRun': 'Last run',
    'reports.exportCsv': 'Download this as CSV',
    'reports.noData.title': 'Nothing happened in this period',
    'reports.noData.body': 'Widen the dates, or pick a month that already closed.',
    'reports.aggregatesPending': 'The nightly job that builds these totals has not run in this environment yet. This is ' +
        'not the same as “nothing happened”.',
    'reports.saved': 'Scheduled reports',
    'reports.saved.empty': 'No scheduled reports yet.',
    'reports.saved.add': 'Schedule a report',
    'reports.saved.add.describe': 'What to report, in what format, to whom, and how often.',
    'reports.saved.created': 'Report {code} is scheduled.',
    'reports.saved.delete': 'Delete',
    'reports.saved.delete.describe': 'The schedule goes away. The record of what was sent, and to whom, stays.',
    'reports.saved.deleted': 'The schedule is gone.',
    'reports.field.code': 'Short code',
    'reports.field.format': 'Format',
    'reports.field.schedule': 'How often',
    'reports.field.schedule.hint': 'Choose "Only when asked" to run it yourself.',
    'reports.field.recipient': 'Send it to',
    'reports.schedule.manual': 'Only when asked',
    'reports.schedule.monthly': 'Every month on day {day}, at {time}',
    'reports.schedule.weekly': 'Every {weekday}, at {time}',
    'reports.schedule.daily': 'Every day at {time}',
    'reports.schedule.raw': 'Schedule: {expression}',
    'reports.weekday.0': 'Sunday',
    'reports.weekday.1': 'Monday',
    'reports.weekday.2': 'Tuesday',
    'reports.weekday.3': 'Wednesday',
    'reports.weekday.4': 'Thursday',
    'reports.weekday.5': 'Friday',
    'reports.weekday.6': 'Saturday',
    'reports.neverRun': 'Never',
    'reports.runNow': 'Run it now',
    'reports.run.queued': 'The run is queued ({state}).',
    'reports.run.noWorker': 'The run is recorded ({state}), but the worker that builds the file is not switched on ' +
        'in this version. Use “Download this as CSV” above for what is on screen.',
    'reports.exportWorkerMissing': 'Scheduled files are not produced in this version yet. Schedules and recipients are ' +
        'saved and will run when the worker is switched on.',
    'reports.runState.QUEUED': 'queued',
    'reports.runState.RUNNING': 'running',
    'reports.runState.READY': 'ready',
    'reports.runState.FAILED': 'failed',
    'reports.runState.EXPIRED': 'expired',
    'reports.kind.FINDINGS_BY_MONTH': 'Findings by month',
    'reports.kind.FINDINGS_OVERDUE': 'Findings past their deadline',
    'reports.kind.AUDIT_SCORECARD': 'Audit scorecards',
    'reports.kind.ESCALATION_LOG': 'Escalation log',
    'reports.kind.POINTS_LEDGER': 'Points ledger',
    'reports.kind.VERIFIED_IMPACT': 'Verified impact',
    'audits.title': 'Audits &',
    /** `16:251` — bagian kedua judul dua warna. Dua kunci; lihat `Screen.titleAccent`. */
    'audits.titleAccent': 'Assessments',
    'audits.subtitle.data': 'Instrument {instrument} v{version, number} · {areas, number} areas · a failed criterion raises a finding automatically',
    /** `107:1213`. Tombolnya ADA dan MATI — tidak ada rute yang mengubah instrumen. */
    'audits.manageInstrument': 'Manage instrument',
    'audits.manageInstrument.note': 'Not editable yet — published by PQM, read-only in the product.',
    'audits.stat.pastSchedule': 'Areas past schedule',
    'audits.stat.pastSchedule.trend': 'attention',
    'audits.pastScheduleBadge': '{count, number} past schedule',
    'audits.state.PAST_SCHEDULE': 'Past schedule',
    'audits.stateWithScore': '{state} · {pct, number}',
    'audits.subtitle': 'How each area scored, and what the last audit produced.',
    'audits.list': 'Audits',
    'audits.schedule': 'Schedule an audit',
    'audits.schedule.describe': 'An instrument, an area, an auditor, and a time.',
    'audits.schedule.when': 'When',
    'audits.scheduled': 'The audit is on the schedule.',
    'audits.column.area': 'Area',
    'audits.column.instrument': 'Instrument',
    'audits.column.auditor': 'Auditor',
    'audits.column.state': 'State',
    'audits.column.score': 'Score',
    'audits.column.when': 'When',
    'audits.column.criterion': 'Criterion',
    'audits.column.conforms': 'Meets it',
    'audits.column.note': 'Note',
    'audits.column.latest': 'Latest',
    'audits.column.previous': 'Before that',
    'audits.column.delta': 'Change',
    'audits.state.SCHEDULED': 'Scheduled',
    'audits.state.IN_PROGRESS': 'Being done',
    'audits.state.SUBMITTED': 'Submitted',
    'audits.state.VOIDED': 'Voided',
    'audits.band.HIJAU': 'Green',
    'audits.band.KUNING': 'Amber',
    'audits.band.MERAH': 'Red',
    'audits.pct': '{pct, number}%',
    'audits.delta': '{delta, number} points',
    'audits.instrumentVersion': 'version {version, number}',
    'audits.conform': 'Yes',
    'audits.notConform': 'No',
    'audits.scorecard': 'Scorecard — {area}',
    'audits.scorecard.total': '{total, number} out of {max, number}, which is {pct, number}%.',
    'audits.scorecard.noAnswers': 'No criteria have been answered yet.',
    'audits.comparison': 'Compared with the audit before it',
    'audits.comparison.empty': 'Nothing to compare yet — an area needs two submitted audits.',
    'audits.comparison.noPrevious': 'First audit',
    'audits.stat.total': 'Audits',
    'audits.stat.submitted': 'Submitted',
    'audits.stat.average': 'Average score',
    'audits.stat.average.none': 'No submitted audit yet.',
    'audits.stat.spawned': 'Findings born from audits',
    'audits.stat.spawned.caption': 'A failed criterion creates a finding with an owner and a deadline.',
    'audits.empty.title': 'No audits yet',
    'audits.empty.body': 'Schedule the first one against a published instrument.',
    'audits.auditorSelfOnly': 'You may schedule audits for yourself. Assigning someone else needs a manager.',
    'audits.offlineNote': 'An audit captured on a phone that has not met the network yet is not in this list. It ' +
        'appears once the phone syncs.',
    'audits.fieldOnlyNote': 'Answering criteria and submitting an audit happen in the field app — one criterion per ' +
        'screen, with a photograph required when something does not meet the standard.',
    'platform.tenants.title': 'Client organisations',
    'platform.tenants.subtitle': 'Every organisation on this platform, and what each one bought.',
    'platform.tenants.empty.title': 'No organisations yet',
    'platform.tenants.empty.body': 'The first client is created from here.',
    /**
     * GELOMBANG M15 — KALIMAT YANG LAHIR BERSAMA KENDALINYA.
     *
     * Ketiga kunci `…Pending` yang berbunyi "belum dapat dilakukan dari sini" DIHAPUS pada
     * gelombang yang sama, dan urutannya mengikat: kalimat yang tertinggal sesudah
     * kendalinya ada adalah kebohongan yang arahnya berlawanan dari yang
     * `tools/ci/gerbang-janji-kendali.mjs` larang — dan gerbang itu TIDAK dapat melihatnya,
     * karena pengingkaran ("belum") membuatnya hijau di mata gerbang selamanya.
     */
    'platform.lifecycle.title': 'Lifecycle',
    'platform.lifecycle.body': 'Suspending stops processing for everyone in this organisation, immediately. No data is deleted.',
    'platform.lifecycle.suspend': 'Suspend organisation',
    'platform.lifecycle.resume': 'Resume organisation',
    'platform.lifecycle.suspend.title': 'Suspend {name}?',
    'platform.lifecycle.suspend.body': 'Everyone in this organisation is signed out the moment you confirm — anyone mid-form loses ' +
        'what they were typing. Nothing is deleted, and you can resume it later. Resuming does not ' +
        'bring the sessions back: everyone signs in again.',
    'platform.lifecycle.resume.title': 'Resume {name}?',
    'platform.lifecycle.resume.body': 'The organisation becomes usable again and its people can sign in. Their old sessions are ' +
        'not restored.',
    'platform.lifecycle.reason': 'Reason (optional, recorded in the audit trail)',
    'platform.lifecycle.suspendedAt': 'Suspended on {at}.',
    'platform.plan.title': 'Subscription',
    'platform.plan.body': 'This is the only place the plan is changed. Changing it does not switch modules on — that ' +
        'stays one deliberate action per module, below.',
    'platform.plan.current': 'Plan',
    'platform.plan.choose': 'Choose a plan',
    'platform.plan.save': 'Change plan',
    'platform.plan.title.confirm': 'Move {name} to {plan}?',
    'platform.plan.body.confirm': 'The plan changes now. Modules the new plan carries are listed afterwards; switching each ' +
        'one on stays a separate decision.',
    'platform.usage.title': 'Usage this month',
    'platform.usage.body': 'Counted meter by meter, for {period}.',
    'platform.usage.column.meter': 'Meter',
    'platform.usage.column.value': 'Used',
    'platform.usage.column.quota': 'Quota',
    'platform.usage.unknown': 'Not measured',
    'platform.usage.empty.title': 'No meters yet',
    'platform.usage.empty.body': 'Meters appear once a module is switched on for this organisation.',
    'platform.provlog.title': 'Provisioning log',
    'platform.provlog.body': 'What has and has not been done to open this organisation.',
    'platform.provlog.empty.title': 'Nothing recorded yet',
    'platform.provlog.empty.body': 'Creating the organisation is the first line.',
    'platform.consultants.title': 'Consultant access',
    'platform.consultants.body': 'The one stated exception to isolation between organisations. Granting, switching into the ' +
        'client, and revoking are all recorded — and revoking signs the consultant out of this ' +
        'organisation immediately.',
    'platform.consultants.assign': 'Assign a consultant',
    'platform.consultants.assign.title': 'Give a consultant access to {name}?',
    'platform.consultants.assign.body': 'They will be able to read this organisation’s data once they switch into it. Every switch ' +
        'is written to the audit trail — this organisation’s own trail, not only ours.',
    'platform.consultants.choose': 'Consultant',
    'platform.consultants.choose.placeholder': 'Choose a PQM consultant',
    'platform.consultants.revoke': 'Revoke',
    'platform.consultants.revoke.title': 'Revoke {name}’s access?',
    'platform.consultants.revoke.body': 'It takes effect on their next request: any session they have open on this organisation is ' +
        'signed out as part of the same transaction.',
    'platform.consultants.granted': 'Since {at}, granted by {by}',
    'platform.consultants.empty.title': 'No consultant has access',
    'platform.consultants.empty.body': 'Nobody from PQM can read this organisation’s data until someone is assigned here.',
    'platform.jobs.title': 'Background jobs',
    'platform.jobs.body': 'Every run the worker recorded. Re-running inserts a new run; it never overwrites the failure.',
    'platform.jobs.empty.title': 'No runs recorded yet',
    'platform.jobs.empty.body': 'The worker writes a row every time it runs a cycle.',
    'platform.jobs.retry': 'Run again',
    'platform.jobs.cannotRetry': 'No worker behind this job yet',
    'platform.jobs.column.job': 'Job',
    'platform.jobs.column.when': 'Scheduled',
    'platform.jobs.column.state': 'State',
    'platform.jobs.attempt': 'Attempt {n, number}',
    'platform.jobs.retryOf': 'Re-run of an earlier failure',
    'platform.jobState.QUEUED': 'Queued',
    'platform.jobState.RUNNING': 'Running',
    'platform.jobState.SUCCEEDED': 'Succeeded',
    'platform.jobState.FAILED': 'Failed',
    'platform.jobState.SKIPPED_NO_ENTITLEMENT': 'Skipped — module off',
    'platform.incidents.title': 'Incidents',
    'platform.incidents.body': 'An incident is something that happened to the platform, so it has no owning organisation.',
    'platform.incidents.empty.title': 'No incidents',
    'platform.incidents.empty.body': 'Open one when something needs to be followed up.',
    'platform.incidents.open': 'Open an incident',
    'platform.incidents.open.title': 'Open an incident',
    'platform.incidents.open.body': 'It appears on this screen until someone closes it.',
    'platform.incidents.close': 'Close',
    'platform.incidents.close.title': 'Close “{title}”?',
    'platform.incidents.close.body': 'It stays on record. Closing is written to the audit trail.',
    'platform.incidents.severity': 'Severity',
    'platform.incidents.headline': 'What happened',
    'platform.incidents.detail': 'Detail (optional)',
    'platform.incidents.relatedJob': 'Related job code (optional)',
    'platform.incidentState.OPEN': 'Open',
    'platform.incidentState.CLOSED': 'Closed',
    'platform.incidents.openedAt': 'Opened {at}',
    'platform.incidents.closedAt': 'Closed {at}',
    'platform.anchors.title': 'Chain anchors',
    'platform.anchors.body': 'A hash chain that is only consistent with itself can be rebuilt by whoever holds the ' +
        'database. Each anchor copies the last position and hash to storage the database cannot ' +
        'write — which is what makes the promise above checkable from outside it.',
    'platform.anchors.empty.title': 'No anchor written yet',
    'platform.anchors.empty.body': 'The anchoring job writes one per chain per day, once object storage is reachable. Until ' +
        'the first one exists, the chain can only be checked against itself.',
    'platform.anchors.column.chain': 'Chain',
    'platform.anchors.column.seq': 'Position',
    'platform.anchors.column.at': 'Anchored',
    'platform.anchors.column.ref': 'Object',
    'platform.anchors.verified': '{checked, number} rows checked from the last anchor — nothing broken.',
    'platform.anchors.broken': 'The chain breaks at position {seq, number}.',
    'platform.export.title': 'Export the trail',
    'platform.export.body': 'The export itself is recorded in the trail. It finishes inside the request, so there is no ' +
        'ticket to chase — and it carries at most {limit, number} rows.',
    'platform.export.from': 'From',
    'platform.export.to': 'To',
    'platform.export.run': 'Export',
    'platform.export.done': '{rows, number} rows exported as {filename}.',
    'platform.isolationException': 'This screen crosses organisations, and it is the one place that is allowed to. ' +
        'Everywhere else, one organisation cannot see another at all.',
    'platform.column.tenant': 'Organisation',
    'platform.column.slug': 'Short name',
    'platform.column.plan': 'Plan',
    'platform.column.state': 'State',
    'platform.column.activated': 'Opened',
    'platform.column.module': 'Module',
    'platform.column.moduleName': 'Name',
    'platform.column.kernel': 'Kernel',
    'platform.column.entitlement': 'Entitlement',
    'platform.column.requires': 'Requires',
    'platform.tenantState.PROVISIONING': 'Being set up',
    'platform.tenantState.ACTIVE': 'Open',
    'platform.tenantState.SUSPENDED': 'Suspended',
    'platform.tenantState.CLOSED': 'Closed',
    'platform.stat.tenants': 'Organisations',
    'platform.stat.active': 'Open',
    'platform.stat.provisioning': 'Being set up',
    'platform.stat.provisioning.caption': 'Waiting for a signed processing agreement.',
    'platform.back': 'Back',
    'platform.tenantDetail.title': 'Organisation',
    'platform.tenantDetail.legalName': 'Legal name',
    'platform.tenantDetail.users': 'People',
    'platform.tenantDetail.modules': 'Modules',
    'platform.tenantDetail.modules.note': 'Switching a module on takes effect on the next request — no deployment, and under ' +
        'thirty minutes end to end.',
    'platform.module.core': 'Core, cannot be switched off',
    'platform.module.on': 'On',
    'platform.module.off': 'Off',
    'platform.module.enable': 'Switch on',
    'platform.module.disable': 'Switch off',
    'platform.module.enabled': 'Module {module} is on. Its menus, routes and reports are live now.',
    'platform.module.disabled': 'Module {module} is off.',
    'platform.module.enable.describe': 'Module {module} becomes visible to this organisation: menus appear, routes answer, ' +
        'jobs are scheduled.',
    'platform.module.disable.describe': 'Module {module} disappears from their menus and its routes stop answering. Their data ' +
        'stays whole and can still be exported, but it cannot be changed. Points already ' +
        'awarded are not taken back.',
    // -- ALUR ORGANISASI BARU: empat langkah, dan setiap langkah punya kendalinya.
    //
    //    Kalimat "The first client is created from here." berdiri di layar ini selama dua
    //    gelombang di atas keadaan kosong TANPA SATU PUN KENDALI, dan pemilik proyek
    //    membuat organisasi pertamanya dengan `curl`. Ia kini benar, karena tombolnya ada.
    'platform.tenants.add': 'Add tenant',
    'platform.tenants.empty.action': 'Add the first organisation',
    'platform.newTenant.title': 'New organisation',
    'platform.newTenant.describe': 'The organisation is created closed. Nobody can sign in to it until the data processing ' +
        'agreement is recorded — that is what keeps personal data from being processed before ' +
        'Article 51 is satisfied. The next three steps appear on its page as soon as it exists.',
    'platform.newTenant.slug': 'Short name',
    'platform.newTenant.slug.hint': 'Lowercase letters, numbers and hyphens. This is exactly what people type in the ' +
        'Organisation field when they sign in, so choose something they will recognise and ' +
        'can spell. It cannot be changed afterwards.',
    'platform.newTenant.name': 'Name',
    'platform.newTenant.name.hint': 'What the organisation is called on screen.',
    'platform.newTenant.legalName': 'Legal name',
    'platform.newTenant.legalName.hint': 'The name on the contract. It appears on the processing agreement. Leave it empty to ' +
        'reuse the name above.',
    'platform.newTenant.plan': 'Plan',
    'platform.newTenant.plan.choose': 'Choose a plan',
    'platform.newTenant.locale': 'Default language',
    'platform.newTenant.timezone': 'Time zone',
    'platform.newTenant.submit': 'Create organisation',
    'platform.newTenant.created': '{name} was created. Three steps remain, and they are below.',
    'platform.setup.title': 'Setting this organisation up',
    'platform.setup.note': 'Four steps stand between a new organisation and its first finding. All four are done ' +
        'from here. None of them needs a terminal, a database client, or a person with server ' +
        'access.',
    'platform.setup.unknown': 'How far this organisation got could not be read just now. The steps below are shown ' +
        'without their state rather than with a state that might be wrong.',
    'platform.setup.done': 'This organisation can be used. Its manager signs in and records findings.',
    'platform.setup.state.done': 'Done',
    'platform.setup.state.pending': 'Still to do',
    'platform.setup.step1': '1 · Organisation created',
    'platform.setup.step1.body': '{name} exists, with short name {slug} on plan {plan}.',
    'platform.setup.step2': '2 · Data processing agreement',
    'platform.setup.step3': '3 · 5R categories and workflows',
    'platform.setup.step4': '4 · First manager',
    'platform.agreement.body': 'Record the name and job title of the person at the customer who is authorised to ' +
        'accept the agreement. Until this is recorded, anyone who tries to sign in to this ' +
        'organisation is told the agreement is still pending — a state, not an error.',
    'platform.agreement.accepted': 'Version {version} was accepted by {name}. The organisation is open.',
    'platform.agreement.name': 'Name of the authorised person',
    'platform.agreement.jobTitle': 'Their job title',
    'platform.agreement.submit': 'Record acceptance and open the organisation',
    'platform.agreement.recorded': 'Acceptance recorded. {name} is open.',
    'platform.provisioning.body': 'Categories and workflow definitions are published by ASSIST itself the moment the ' +
        'agreement is recorded. Without them no finding can be reported at all.',
    'platform.provisioning.counts': '{categories} categories · {workflows} workflow definitions published.',
    'platform.provisioning.missing': 'They were not published. That happens when the automatic run failed; the acceptance ' +
        'above still stands, because it is a legal event that already happened.',
    'platform.provisioning.rerun': 'Publish them now',
    'platform.provisioning.done': '{categories} categories and {workflows} workflow definitions are published.',
    'platform.invite.body': 'A new organisation has nobody in it. Invite its first manager here; everyone after ' +
        'that is invited from inside the organisation, where the trail names the manager who ' +
        'invited them rather than PQM. The role is always Manager and cannot be chosen.',
    'platform.invite.needsAgreement': 'Step 2 comes first: nobody can be invited into an organisation whose processing ' +
        'agreement is not recorded, because that is the moment personal data may begin to ' +
        'be processed at all.',
    'platform.invite.email': 'Email address',
    'platform.invite.fullName': 'Full name',
    'platform.invite.jobTitle': 'Job title',
    'platform.invite.submit': 'Invite the first manager',
    'platform.invite.invited': '{name} has been invited as manager.',
    'platform.invite.alreadyIn': 'This organisation already has an active manager, so further people are invited from ' +
        'inside it.',
    'platform.invite.pending': 'Invitations waiting to be accepted: {count}. The manager is not in yet.',
    'platform.invite.link.title': 'The invitation link',
    'platform.invite.link.notDelivered': 'No email left this system: ASSIST has no email provider in this version, so nothing ' +
        'was delivered to anyone. Pass the link below to {name} yourself, through a channel ' +
        'you trust.',
    'platform.invite.link.once': 'It is shown once and cannot be read back — not from this screen, not from the ' +
        'database. If it is lost, invite them again and a new link is issued.',
    'platform.invite.link.copy': 'Copy link',
    'platform.invite.link.copied': 'Link copied.',
    'platform.invite.link.delivered': 'An email provider is configured for this deployment, so the link goes to the ' +
        "recipient's inbox only and is not shown here.",
    'platform.members.title': 'Platform — members',
    'platform.members.subtitle': 'Who inside PQM may sign in, and with which platform role. This is the only way to ' +
        'grant or revoke it through the product.',
    'platform.members.note': 'This list holds PQM staff only. It never contains a single user of a customer ' +
        'organisation: their rows stay bound to their own organisation, and nothing here can ' +
        'reach them.',
    'platform.members.column.person': 'Person',
    'platform.members.column.role': 'Platform role',
    'platform.members.column.account': 'Account',
    'platform.members.column.granted': 'Granted',
    'platform.members.grant': 'Grant a platform role',
    'platform.members.revoke': 'Revoke',
    'platform.members.lastAdmin': 'Only active platform administrator — grant the role to someone else first',
    'platform.members.noPassword': 'Has not set a password yet',
    'platform.members.passwordPath': 'A new colleague sets their own password through the forgot-password flow. No ' +
        'administrator ever sets it for them, because anyone who can set your password can ' +
        'sign in as you — and then the audit trail carries your name for their actions.',
    'platform.members.role.PLT': 'Platform administrator',
    'platform.members.role.KON': 'Consultant',
    'platform.members.reason': 'Why',
    'platform.members.reason.hint': 'Required, and it is recorded in the audit trail. A platform role that moves without a ' +
        'reason is a row nobody can explain a year later.',
    'platform.members.confirmRevoke': 'Revoke {name}\u2019s platform role?',
    'platform.members.confirmRevokeBody': 'They lose access on their next request, and every live session of theirs is ended. ' +
        'The row is kept as SUSPENDED, so the record that they once held this role remains.',
    'platform.members.granted': 'Platform role granted',
    'platform.members.revoked': 'Platform role revoked',
    'platform.members.bootstrapNote': 'The very first platform administrator is created once, through ' +
        'POST /v1/platform/bootstrap, guarded by a deployment secret and refused after it has ' +
        'been used. Its use is in the audit trail as PLATFORM_BOOTSTRAP_CLAIMED.',
    'platform.modules.title': 'Modules & Entitlements',
    'platform.modules.subtitle': 'What the platform can offer, and which plan includes what.',
    'platform.modules.registry': 'Module registry',
    'platform.modules.plans': 'Plans',
    'platform.modules.note': 'This list is built from the module manifests. Anything not declared there does not ' +
        'exist anywhere in the system.',
    'platform.audit.title': 'Audit trail',
    'platform.audit.subtitle': 'Who did what, when, and on what legal basis.',
    'platform.audit.appendOnly': 'These rows can only be added, never changed and never deleted — including by whoever ' +
        'holds the database.',
    'platform.audit.filterActor': 'Who did it',
    'platform.audit.allActors': 'Anyone',
    'platform.audit.column.when': 'When',
    'platform.audit.column.actor': 'Who',
    'platform.audit.column.event': 'What happened',
    'platform.audit.column.subject': 'On what',
    'platform.audit.column.module': 'Module',
    'platform.audit.column.basis': 'Legal basis',
    /**
     * CACAT 4, 28 September 2026. `SESSION_OPENED` and `CREDENTIAL_READ` are written BEFORE a
     * session exists, so the row genuinely holds no actor. The old text put `actorKind.ANONYMOUS`
     * — "Not signed in" — on BOTH lines: it repeated itself, and it read like a failed sign-in
     * when what actually happened was a SUCCESSFUL one. These two lines explain the state
     * instead, and they do not invent an identity the row does not hold.
     */
    'platform.audit.actor.preSession': 'Before the session existed',
    /**
     * The note stands ONCE, under the table, and only when such rows are on screen. Putting it
     * on every row was the first shape of this fix, and the screenshot settled it: the same
     * sentence repeated forty times turns a table meant to be read into a wall of text. The
     * original defect was two lines repeating themselves; forty is not an improvement.
     */
    'platform.audit.actor.preSessionNote': 'Rows marked “Before the session existed” are sign-in steps written before the session ' +
        'they create, so no actor was on record yet. They are the ordinary first moments of a ' +
        'successful sign-in, not a failed one.',
    'platform.audit.empty.title': 'Nothing recorded yet',
    'platform.audit.empty.body': 'The first sign-in will appear here.',
    'platform.health.title': 'System health',
    'platform.health.subtitle': 'Queues, failures and open incidents.',
    'platform.health.refresh': 'Read it again',
    'platform.health.checkedAt': 'Read at {at}.',
    'platform.health.metric.outbox_pending': 'Events waiting',
    /**
     * `outbox_stuck` is what GET /v1/platform/health actually returns, and it had no key —
     * so System Health rendered the raw `platform.health.metric.outbox_stuck` on screen, in
     * both languages. Found by looking at the captured reference, not by a gate: the interface-
     * text gate reads SOURCE for raw keys, and this key was assembled at runtime from the
     * server's own column name. `packages/i18n/src/metrik-kesehatan.test.ts` now closes it.
     *
     * "Stuck" is not "failed": these events were retried more than three times and are still
     * unprocessed. That is a person's problem, not a queue's.
     */
    'platform.health.metric.outbox_stuck': 'Events stuck after retries',
    'platform.health.metric.outbox_failed': 'Events failed',
    'platform.health.metric.job_failures': 'Job failures',
    'platform.health.metric.open_incidents': 'Open incidents',
    'platform.health.metric.outbox_oldest_seconds': 'Oldest event, seconds',
    'platform.health.metric.sessions_active': 'Active sessions',
    'platform.health.metric.emails_pending': 'Emails waiting',
    'platform.health.metric.files_pending_verification': 'Files awaiting checks',
    'portfolio.title': 'Client portfolio',
    'portfolio.subtitle': 'The organisations you have been assigned to support.',
    'portfolio.column.since': 'Assigned since',
    'portfolio.enter': 'Work in this client',
    'portfolio.switchIsLogged': 'Every time you enter a client, that is recorded in their audit trail with your name on ' +
        'it.',
    'portfolio.empty.title': 'No clients assigned to you',
    'portfolio.empty.body': 'A platform admin assigns clients to consultants. Until then you cannot reach any ' +
        'organisation, and that is the correct behaviour.',
    'portfolio.healthPending': 'A health summary per client is not shown here. Reading a client’s numbers means ' +
        'entering that client first — figures gathered across organisations without doing so ' +
        'would be a leak, not a summary.',
    'account.title': 'Account & notifications',
    'account.subtitle': 'Your details, your language, and the devices you are signed in on.',
    'account.profile': 'Your details',
    'account.saved': 'Saved.',
    'account.language': 'Language',
    'account.language.note': 'Your language is kept on your profile, not in this browser, so it follows you to any ' +
        'device. Switching it does not lose anything you have typed.',
    'account.notifications': 'Notifications',
    'account.quietHours': 'Push notifications are held between 20.00 and 06.00 and delivered the next morning.',
    'account.safetyAlways': 'Safety escalations are the exception: they are always sent immediately, at any hour.',
    'account.notifications.pending': 'Choosing which events reach you, on which channel, is not available from here yet. The ' +
        'two rules above apply as written.',
    'account.sessions': 'Devices you are signed in on',
    'account.session.device': 'Device',
    'account.session.lastSeen': 'Last seen',
    'account.session.unknownDevice': 'Unnamed device',
    'account.session.current': 'This one',
    'account.session.revoke': 'Sign this device out',
    'account.session.revoke.describe': 'Whoever holds that device has to sign in again. It takes effect immediately.',
    'account.session.revoked': 'That device is signed out.',
    'account.session.signOutInstead': 'Use “Sign out” to end this one',
    'error.MEETING_SLOT_TAKEN': 'There is already a huddle for that unit at that time. Pick another slot, or open the ' +
        'one that exists.',
    'error.NOTHING_DISCUSSED': 'A huddle cannot be closed before at least one agenda item has been discussed. Mark ' +
        'what you talked about.',
    'error.TIMEBOX_NOT_ELAPSED': 'The ten minutes are not up yet, so this cannot be marked as over time.',
    'error.QUERY_TOO_SHORT': 'Search needs at least {min_length, number} characters.',
};
// ---------------------------------------------------------------------------
// id-ID
// ---------------------------------------------------------------------------
export const idID = {
    'app.name': 'ASSIST',
    /** Nama perusahaan tidak diterjemahkan — lihat catatan di baris en-US (audit S-02). */
    'app.byPqm': 'by PQM Consultants',
    'app.skipToContent': 'Lompat ke konten',
    'app.search.placeholder': 'Cari temuan, lokasi, atau orang',
    'app.search.label': 'Pencarian global',
    'app.language': 'Bahasa',
    'app.language.en': 'English',
    'app.language.id': 'Bahasa Indonesia',
    'app.signOut': 'Keluar',
    'app.period.label': 'Periode',
    'app.period.notApplicable': 'Tidak dipakai di layar ini.',
    'app.notifications.loading': 'Notifikasi belum dimuat.',
    'app.notifications.unread': '{count, plural, =0 {Tidak ada notifikasi belum dibaca} other {# notifikasi belum dibaca}}',
    'app.accountSettings': 'Pengaturan akun',
    'app.mainNavigation': 'Navigasi utama',
    'app.loading': 'Memuat…',
    'app.retry': 'Coba lagi',
    'app.cancel': 'Batal',
    'app.save': 'Simpan',
    'app.close': 'Tutup',
    'app.dismiss': 'Tutup',
    'app.of': 'dari',
    'nav.group.OPERATIONS': 'Operasional',
    'nav.group.IMPROVEMENT': 'Perbaikan',
    'nav.group.KNOWLEDGE': 'Pengetahuan',
    'nav.group.INSIGHT': 'Wawasan',
    'nav.group.MANAGE': 'Kelola',
    'nav.group.PLATFORM': 'Platform',
    'nav.group.GOVERNANCE': 'Tata Kelola',
    'nav.group.TAB': 'Tab',
    'signIn.title': 'Masuk ke',
    'signIn.titleAccent': 'ASSIST',
    'signIn.subtitle': 'Gunakan akun perusahaan Anda. Belum punya akses? Hubungi admin perusahaan.',
    'signIn.brand.org': 'PQM CONSULTANTS',
    'signIn.brand.orgTagline': 'Productivity & Quality Management',
    /** Teks alternatif tanda PQM. Nama perusahaan TIDAK diterjemahkan (aturan lokalisasi Figma §5). */
    'signIn.brand.logoAlt': 'PQM Consultants',
    'signIn.brand.by': 'by PQM',
    'signIn.brand.tagline': 'Platform Operational Excellence untuk perbaikan yang benar-benar tuntas — 5R, kaizen, audit, dan proyek dalam satu alur kerja.',
    'signIn.brand.stat.foundedValue': '1987',
    'signIn.brand.stat.foundedLabel': 'Berdiri',
    'signIn.brand.stat.clientsValue': '2.100+',
    'signIn.brand.stat.clientsLabel': 'Klien terlayani',
    'signIn.brand.stat.projectsValue': '900+',
    'signIn.brand.stat.projectsLabel': 'Proyek',
    'signIn.rememberDevice': 'Ingat perangkat ini',
    'signIn.or': 'atau',
    'signIn.sso': 'Masuk dengan SSO perusahaan',
    'signIn.sso.unavailable': 'SSO bukan bagian dari versi ini — belum ada penyedia identitas yang dapat ' +
        'memasukkan Anda. Pakai alamat surel dan kata sandi Anda di atas.',
    'signIn.legal': 'Data disimpan di wilayah Indonesia sesuai UU No. 27 Tahun 2022.',
    'signIn.tenantSlug': 'Organisasi',
    'signIn.tenantSlug.hint': 'Kosongkan bila Anda staf PQM.',
    'signIn.identity': 'Surel atau nomor pegawai',
    'signIn.password': 'Kata sandi',
    'signIn.submit': 'Masuk',
    'signIn.submitting': 'Sedang masuk…',
    'signIn.forgotPassword': 'Lupa kata sandi?',
    'signIn.forgotPassword.title': 'Atur ulang kata sandi',
    'signIn.forgotPassword.body': 'Sebutkan akun yang dimaksud, dan permintaan Anda akan dicatat. Pengiriman surel atur ulang kata sandi belum dinyalakan pada versi ini, jadi seseorang perlu melakukannya untuk Anda.',
    'signIn.forgotPassword.submit': 'Catat permintaan',
    'signIn.forgotPassword.cancel': 'Kembali ke layar masuk',
    'signIn.forgotPassword.recorded': 'Permintaan Anda dicatat. Pengiriman surel otomatis belum tersedia pada versi ini — minta leader area atau kontak PQM Anda mengatur ulang kata sandinya.',
    'signIn.logoPlaceholder': 'Logo klien',
    'dashboard.title': 'Dasbor',
    'dashboard.titleAccent': 'Operasional',
    'dashboard.subtitle': '{org} · {areas, number} area · {users, number} pengguna aktif',
    'dashboard.stat.verifiedImpact': 'Dampak terverifikasi',
    'dashboard.impact.excluded': '{count, plural, other {# klaim menunggu keuangan}}',
    'dashboard.trend.needsAction': 'Perlu tindakan',
    'dashboard.decision.approve': 'Setujui',
    'dashboard.decision.decline': 'Tolak',
    'dashboard.decision.requestInfo': 'Minta info',
    'dashboard.decisionQueue.auditor': 'Persetujuan adalah pekerjaan leader area dan manajer, bukan pekerjaan audit.',
    'dashboard.recurring': 'Temuan berulang',
    'dashboard.recurring.period': 'Periode ini',
    'dashboard.recurring.empty': 'Belum ada temuan yang dibuka pada periode ini.',
    'dashboard.export': 'Ekspor',
    'dashboard.recurring.note': 'Per kategori 5R, periode ini.',
    'dashboard.recurring.action': 'Lihat rinciannya',
    'dashboard.areaCondition': 'Kondisi area',
    'dashboard.areaCondition.empty': 'Belum ada angka area untuk periode ini.',
    'dashboard.areaColumn.area': 'Area',
    'dashboard.areaColumn.completion': 'Ketuntasan',
    'dashboard.areaColumn.overdue': 'Lewat tenggat',
    'dashboard.areaColumn.lastAuditScore': 'Skor audit terakhir',
    'dashboard.areaFilter.needsAttention': 'Perlu perhatian',
    'dashboard.areaFilter.all': 'Semua area',
    'dashboard.areaCondition.allGood': 'Seluruh area berjalan sesuai rencana pada periode ini.',
    'dashboard.areaColumn.status': 'Status',
    'dashboard.areaStatus.GOOD': 'Baik',
    'dashboard.areaStatus.WATCH': 'Pantau',
    'dashboard.areaStatus.CRITICAL': 'Kritis',
    'dashboard.stat.openFindings': 'Temuan terbuka',
    'dashboard.stat.overdue': 'Lewat tenggat',
    'dashboard.stat.closedThisMonth': 'Selesai bulan ini',
    'dashboard.stat.awaitingVerification': 'Menunggu verifikasi',
    'dashboard.decisionQueue': 'Antrean keputusan',
    'dashboard.decisionQueue.empty': 'Tidak ada yang menunggu keputusan Anda.',
    'dashboard.areaHealth': 'Kondisi area',
    'dashboard.viewAll': 'Lihat semua',
    'dashboard.buildMonthlyReport': 'Susun laporan bulanan',
    'dashboard.empty.title': 'Belum ada temuan bulan ini',
    'dashboard.empty.body': 'Ketika tim melaporkan temuan pertama bulan ini, angkanya muncul di sini.',
    'dashboard.completion': '{done, number} dari {total, number} area tuntas',
    'findings.title': 'Temuan',
    'findings.titleAccent': 'Terbuka',
    'findings.subtitle': '{open, number} temuan terbuka · {overdue, number} lewat tenggat · rata-rata {days} hari sampai selesai',
    'findings.pagination.showing': 'Menampilkan {from, number}–{to, number} dari {total, number} temuan',
    'findings.pagination.previous': 'Halaman sebelumnya',
    'findings.pagination.next': 'Halaman berikutnya',
    'findings.pagination.page': 'Halaman {page, number}',
    'findings.new': 'Temuan baru',
    'findings.column.priority': 'Prioritas',
    'findings.priority.CRITICAL': 'Kritis',
    'findings.priority.HIGH': 'Tinggi',
    'findings.priority.MEDIUM': 'Sedang',
    'findings.priority.LOW': 'Rendah',
    'findings.count': '{count, plural, =0 {Tidak ada temuan} other {# temuan}}',
    'findings.column.code': 'Kode',
    'findings.column.title': 'Judul',
    'findings.column.category': 'Kategori',
    'findings.column.area': 'Area',
    'findings.column.owner': 'Penanggung jawab',
    'findings.column.state': 'Status',
    'findings.column.due': 'Tenggat',
    'findings.column.photos': 'Foto',
    'findings.filter.state': 'Status',
    'findings.filter.area': 'Area',
    'findings.filter.category': 'Kategori',
    'findings.filter.owner': 'Penanggung jawab',
    'findings.filter.all': 'Semua',
    'findings.filter.clear': 'Bersihkan saringan',
    'findings.export': 'Ekspor CSV',
    'findings.selected': '{count, plural, other {# dipilih}}',
    'findings.bulk.assign': 'Tugaskan ulang',
    'findings.bulk.changeDue': 'Ubah tenggat',
    'findings.bulk.changeDue.describe': '{count, plural, other {# temuan akan berpindah ke tenggat yang baru.}} Tenggatnya dapat dipindahkan lagi nanti.',
    'findings.bulk.done': '{count, plural, other {Tenggat # temuan dipindahkan.}}',
    'findings.bulk.partial': 'Tenggat {done, number} temuan dipindahkan; {failed, number} ditolak: {codes}.',
    'findings.empty.title': 'Belum ada temuan',
    'findings.empty.body': 'Laporan pertama dari lapangan akan muncul di sini.',
    'findings.empty.cta': 'Lapor temuan',
    'findings.emptyFiltered.title': 'Tidak ada temuan yang cocok dengan saringan ini',
    'findings.emptyFiltered.body': 'Longgarkan saringannya, atau bersihkan untuk melihat semuanya.',
    'findings.unassigned': 'Belum ditugaskan',
    'findings.noDueDate': 'Tanpa tenggat',
    'finding.reportedBy': 'Dilaporkan oleh {name}',
    'finding.owner': 'Penanggung jawab',
    'finding.area': 'Area',
    'finding.category': 'Kategori',
    'finding.severity': 'Tingkat',
    'finding.due': 'Tenggat',
    'finding.opened': 'Dibuka',
    'finding.closed': 'Selesai',
    'finding.photos': 'Foto',
    'finding.photos.before': 'Sebelum',
    'finding.photos.after': 'Sesudah',
    'finding.photos.evidence': 'Bukti',
    'finding.timeline': 'Riwayat',
    'finding.timeline.note': 'Setiap perubahan tercatat di sini, berurutan. Tidak ada yang dapat dihapus.',
    'finding.comments': 'Komentar',
    'finding.comment.add': 'Tambah komentar',
    'finding.comment.submit': 'Kirim komentar',
    'finding.comment.empty': 'Belum ada komentar.',
    'finding.actions': 'Aksi',
    'finding.actions.none': 'Tidak ada aksi yang tersedia bagi Anda pada kondisi ini.',
    'finding.reason': 'Alasan',
    'finding.reason.required': 'Alasan wajib diisi dan akan dibaca pelapor.',
    'finding.description': 'Deskripsi',
    'finding.owner.choose': 'Pilih orang…',
    'field.unknown': 'Keterangan tambahan',
    'finding.assign.owner': 'Tugaskan kepada',
    'finding.dueDate.new': 'Tenggat baru',
    'finding.notFound.title': 'Temuan itu tidak ada di sini',
    'finding.notFound.body': 'Bisa jadi ia milik organisasi lain, atau ada di area di luar cakupan Anda.',
    'transition.CREATE': 'Kirim temuan',
    'transition.ASSIGN': 'Tugaskan',
    'transition.START': 'Mulai kerjakan',
    'transition.CHANGE_DUE_DATE': 'Ubah tenggat',
    'transition.ADD_NOTE': 'Tambah catatan',
    'transition.REQUEST_INFO': 'Minta keterangan',
    'transition.SUBMIT_FOR_VERIFICATION': 'Selesaikan temuan',
    'transition.SEND_BACK': 'Kembalikan',
    'transition.CONFIRM_FIX': 'Sahkan perbaikan',
    'transition.CLOSE': 'Tutup',
    'transition.REOPEN': 'Buka kembali',
    'transition.ESCALATE': 'Eskalasi',
    'transition.DE_ESCALATE': 'Turunkan tingkat',
    'transition.TAKE_OVER': 'Ambil alih',
    'state.OPEN': 'Terbuka',
    'state.ASSIGNED': 'Ditugaskan',
    'state.IN_PROGRESS': 'Dikerjakan',
    'state.AWAITING_VERIFICATION': 'Menunggu verifikasi',
    'state.SENT_BACK': 'Dikembalikan',
    'state.CLOSED': 'Selesai',
    'facet.OVERDUE': 'Lewat tenggat',
    'facet.ESCALATED': 'Dieskalasi',
    'category.SORT': 'Ringkas',
    'category.SET': 'Rapi',
    'category.SET_IN_ORDER': 'Rapi',
    'category.MEETING': 'Tindakan Rapat',
    'category.SHINE': 'Resik',
    'category.STANDARDIZE': 'Rawat',
    'category.SUSTAIN': 'Rajin',
    'category.SAFETY': 'Keselamatan',
    'severity.LOW': 'Rendah',
    'severity.MEDIUM': 'Sedang',
    'severity.HIGH': 'Tinggi',
    'severity.CRITICAL': 'Kritis',
    'role.OPR': 'Operator',
    'role.LDR': 'Leader area',
    'role.MGR': 'Manajer',
    'role.AUD': 'Auditor',
    'role.KON': 'Konsultan PQM',
    'role.PLT': 'Admin platform',
    'role.FAC': 'Fasilitator',
    'state.loading.title': 'Memuat',
    'state.error.title': 'Kami tidak dapat memuat ini',
    'state.error.body': 'Server tidak menjawab seperti yang diharapkan. Anda dapat mencoba lagi; tidak ada isian yang hilang.',
    'state.error.code': 'Rujukan untuk dukungan: {code}',
    'state.notInPlan.title': 'Modul ini tidak ada di paket Anda',
    'state.notInPlan.body': 'Modul {module} belum dinyalakan untuk {organisation}. Administrator platform dapat menyalakannya — berlaku dalam hitungan menit, tanpa penggelaran.',
    'state.notInPlan.bodyNoOrg': 'Modul {module} belum dinyalakan untuk organisasi ini. Administrator platform dapat menyalakannya — berlaku dalam hitungan menit, tanpa penggelaran.',
    'state.notInPlan.whoCanHelp': 'Hubungi kontak PQM Anda atau administrator platform.',
    'state.notBuilt.title': 'Layar ini belum siap',
    'state.notBuilt.body': 'Layar ini bagian dari ASSIST dan ada di paket Anda, tetapi belum dibangun. Data Anda aman dan tidak ada yang hilang.',
    'state.notBuilt.whoCanHelp': 'Kontak PQM Anda dapat memberi tahu kapan ia tiba.',
    'state.fieldApp.title': 'Pekerjaan Anda ada di ponsel',
    'state.fieldApp.body': 'Melaporkan temuan, menyelesaikannya, dan audit semuanya ada di aplikasi ASSIST di ponsel Anda. Konsol web ini untuk leader area dan manajer.',
    'state.fieldApp.whoCanHelp': 'Tanyakan kepada leader area Anda bila aplikasinya belum ada.',
    'dialog.confirm.title': 'Anda yakin?',
    'dialog.confirm.destructive': 'Tindakan ini tidak dapat dibatalkan.',
    'dialog.action.describe': 'Isi dulu, lalu simpan. Setiap perubahan tercatat.',
    'signal.information': 'Informasi',
    'signal.success': 'Berhasil',
    'signal.warning': 'Perhatian',
    'signal.danger': 'Gagal',
    'error.VALIDATION_FAILED': 'Ada isian yang perlu diperbaiki.',
    'error.INVALID_CREDENTIALS': 'Surel atau kata sandi itu tidak cocok.',
    'error.ACCOUNT_LOCKED': 'Akun ini terkunci setelah {attempts, number} percobaan gagal. Coba lagi dalam {minutes, number} menit, atau minta manajer mengatur ulangnya.',
    'error.TENANT_INACTIVE': 'Organisasi itu tidak aktif. Silakan hubungi konsultan PQM Anda.',
    'error.USER_NOT_ELIGIBLE': 'Akun ini tidak dapat masuk. Minta manajer memeriksanya.',
    'error.UNAUTHENTICATED': 'Silakan masuk kembali.',
    'error.SESSION_EXPIRED': 'Sesi Anda berakhir. Silakan masuk kembali.',
    'error.SESSION_REVOKED': 'Sesi ini dikeluarkan dari perangkat lain. Silakan masuk kembali.',
    'error.SESSION_UNKNOWN': 'Silakan masuk kembali.',
    'error.FORBIDDEN': 'Anda tidak punya izin untuk melakukan itu.',
    'error.NOT_FOUND': 'Catatan itu tidak ada di sini.',
    'error.ENTITLEMENT_REQUIRED': 'Modul {module} tidak ada di paket organisasi ini, jadi ia tidak dapat diubah. Data yang ada tetap terbaca dan dapat diekspor.',
    'error.TRANSITION_NOT_ALLOWED': '“{transition}” tidak dapat dilakukan dari {state}.',
    'error.STATE_CONFLICT': 'Temuan ini sudah berubah sejak terakhir dilihat perangkat Anda — sekarang {actual}. Tidak ada yang hilang.',
    'error.AFTER_PHOTO_REQUIRED': 'Foto “sesudah” wajib ada sebelum ini dapat ditutup. Terlampir {found, number} dari {required, number}.',
    'error.PHOTO_REQUIRED': 'Minimal satu foto wajib dilampirkan.',
    'error.LOCATION_REQUIRED': 'Pilih areanya.',
    'error.CATEGORY_REQUIRED': 'Pilih kategorinya.',
    'error.DESCRIPTION_TOO_LONG': 'Batasi deskripsi sampai {max, number} karakter — sekarang {actual, number}.',
    'error.LOCATION_INACTIVE': 'Area itu sedang nonaktif.',
    'error.CODE_BELONGS_TO_ANOTHER_ORGANISATION': 'Kode itu milik organisasi lain.',
    'error.OWNER_REQUIRED': 'Pilih siapa penanggung jawabnya.',
    'error.OWNER_OUTSIDE_TENANT': 'Orang itu bukan anggota organisasi ini.',
    'error.OWNER_DISABLED': 'Akun itu sedang nonaktif.',
    'error.REASON_REQUIRED': 'Alasan wajib diisi — orang lain akan membacanya.',
    'error.DUE_DATE_IN_PAST': 'Pilih tanggal di masa depan.',
    'error.SELF_VERIFICATION_NOT_ALLOWED': 'Orang yang mengerjakan tidak dapat memverifikasinya sendiri. Minta leader area atau manajer.',
    'error.ESCALATION_AT_TOP_LEVEL': 'Ini sudah berada di tingkat eskalasi tertinggi.',
    'error.NOT_ESCALATED': 'Ini tidak sedang dieskalasi.',
    'error.NO_ESCALATION_TRIGGER': 'Belum ada yang menuntut eskalasi di sini.',
    'error.INSTRUMENT_VERSION_CONFLICT': 'Formulir audit berubah saat Anda sedang luring.',
    'error.INSTRUMENT_VERSION_WITHDRAWN': 'Versi formulir audit itu sudah ditarik.',
    'error.RATE_LIMITED': 'Terlalu banyak percobaan. Tunggu sejenak lalu coba lagi.',
    'error.INTERNAL': 'Ada yang tidak beres di sisi kami. Rujukan: {request_id}.',
    'error.TENANT_NOT_FOUND': 'Organisasi itu tidak ditemukan.',
    'error.CONSULTANT_NOT_ASSIGNED': 'Anda tidak ditugaskan pada organisasi itu.',
    'error.CANNOT_REVOKE_CURRENT_SESSION': 'Anda tidak dapat mengeluarkan sesi yang sedang Anda pakai.',
    'error.RESET_TOKEN_INVALID': 'Tautan itu sudah kedaluwarsa atau sudah dipakai.',
    'error.CONTEXT_NOT_BOUND': 'Silakan masuk kembali.',
    'state.SCHEDULED': 'Terjadwal',
    'state.RUNNING': 'Berjalan',
    'state.SUBMITTED': 'Terkirim',
    'state.VOIDED': 'Dibatalkan',
    'state.CANCELLED': 'Dibatalkan',
    'state.OVERRUN': 'Lewat waktu',
    'facet.PENDING_SYNC': 'Menunggu sinkron',
    'facet.INSTRUMENT_WITHDRAWN': 'Formulir ditarik',
    'facet.CARRIED_OVER': 'Dibawa ke rapat berikutnya',
    'facet.NO_ATTENDEES': 'Tanpa peserta',
    'event.ASSESSMENT_SCHEDULED': '{actor} menjadwalkan audit {code}',
    'event.ASSESSMENT_STARTED': '{actor} memulai audit {code}',
    'event.ASSESSMENT_SUBMITTED': '{actor} mengirim audit {code}',
    'event.ASSESSMENT_VOIDED': '{actor} membatalkan audit {code}',
    'event.MEETING_SCHEDULED': '{actor} menjadwalkan rapat',
    'event.MEETING_STARTED': '{actor} memulai rapat',
    'event.MEETING_CLOSED': '{actor} menutup rapat',
    'event.MEETING_CANCELLED': '{actor} membatalkan rapat',
    'event.MEETING_OVERRUN': 'Rapat melewati sepuluh menitnya',
    'event.ACTION_CREATED': '{actor} membuat tindakan {code}',
    'event.ACTION_ASSIGNED': '{actor} menugaskan tindakan {code} kepada {owner}',
    'event.ACTION_STARTED': '{actor} mulai mengerjakan tindakan {code}',
    'event.ACTION_DUE_CHANGED': '{actor} mengubah tenggat tindakan {code}',
    'event.ACTION_CLOSED': '{actor} menutup tindakan {code}',
    'event.ACTION_CANCELLED': '{actor} membatalkan tindakan {code}',
    'error.TITLE_REQUIRED': 'Beri judul singkat.',
    'error.CRITERIA_INCOMPLETE': 'Setiap kriteria harus dijawab sebelum ini dapat dikirim.',
    'error.EVIDENCE_PHOTO_REQUIRED': 'Foto wajib ada ketika sebuah kriteria tidak sesuai.',
    'error.INSTRUMENT_NOT_PUBLISHED': 'Formulir audit itu belum diterbitkan.',
    'error.INSTRUMENT_VERSION_CHANGED': 'Formulir audit berubah saat Anda sedang mengerjakannya.',
    'error.UNFINISHED_AUDIT_EXISTS': 'Anda masih punya audit yang berjalan untuk area ini.',
    'error.MEETING_REQUIRED': 'Ini harus dilakukan dari dalam rapat.',
    'error.NO_SLA_BREACH': 'Ini masih di dalam batas waktunya.',
    'error.NOT_SAFETY': 'Ini tidak ditandai sebagai risiko keselamatan.',
    'error.NO_RECURRENCE': 'Ini belum pernah terjadi sebelumnya.',
    'error.MANUAL_REQUIRES_MANAGER': 'Hanya manajer yang dapat mengeskalasi ini dengan tangan.',
    'event.CREATED': '{actor} membuat {code}',
    'event.FINDING_CREATED': '{actor} melaporkan {code}',
    'event.FINDING_ASSIGNED': '{actor} menugaskan {code} kepada {owner}',
    'event.FINDING_STARTED': '{actor} mulai mengerjakan {code}',
    'event.FINDING_DUE_CHANGED': '{actor} mengubah tenggat {code}',
    'event.FINDING_NOTE_ADDED': '{actor} menambahkan catatan pada {code}',
    'event.FINDING_INFO_REQUESTED': '{actor} meminta keterangan pada {code}',
    'event.FINDING_SUBMITTED_FOR_VERIFICATION': '{actor} mengirim {code} untuk diverifikasi',
    'event.FINDING_SENT_BACK': '{actor} mengembalikan {code}',
    'event.FINDING_VERIFIED': '{actor} mengesahkan perbaikan pada {code}',
    'event.FINDING_CLOSED_BY_MANAGER': '{actor} menutup {code}',
    'event.FINDING_REOPENED': '{actor} membuka kembali {code}',
    'event.FINDING_ESCALATED': '{code} dieskalasi ke tingkat {level, number}',
    'event.FINDING_DE_ESCALATED': '{actor} menurunkan tingkat {code}',
    'event.FINDING_TAKEN_OVER': '{actor} mengambil alih {code}',
    'time.relative.justNow': 'baru saja',
    'time.relative.minutes': '{n, plural, other {# menit lalu}}',
    'time.relative.hours': '{n, plural, other {# jam lalu}}',
    'time.relative.days': '{n, plural, other {# hari lalu}}',
    'time.updatedAgo': 'Diperbarui {when}',
    // -------------------------------------------------------------------------
    // Privasi, dasar pemrosesan, dan persetujuan (UU 27/2022).
    // -------------------------------------------------------------------------
    'privacy.basis.CONSENT': 'persetujuan Anda',
    'privacy.basis.CONTRACT_PERFORMANCE': 'pelaksanaan hubungan kerja',
    'privacy.basis.LEGAL_OBLIGATION': 'kewajiban hukum, termasuk keselamatan dan kesehatan kerja',
    'privacy.basis.VITAL_INTEREST': 'pelindungan kepentingan vital orang yang bersangkutan',
    'privacy.basis.PUBLIC_TASK': 'pelaksanaan tugas untuk kepentingan umum',
    'privacy.basis.LEGITIMATE_INTEREST': 'kepentingan sah, yang ditimbang terhadap dampaknya pada orang yang terlibat',
    'privacy.purpose.ACCOUNT_ACCESS': 'memberi orang akses ke sistem dan menjaga akses itu tetap aman',
    'privacy.purpose.SECURITY_MONITORING': 'mengenali penyalahgunaan akun dan dapat menunjukkan siapa melakukan apa',
    'privacy.purpose.ORG_STRUCTURE': 'menyusun organisasi, areanya, dan siapa bekerja di mana',
    'privacy.purpose.OHS_COMPLIANCE': 'mengelola keselamatan dan kesehatan kerja',
    'privacy.purpose.IMPROVEMENT_TRACKING': 'mencatat, menugaskan, dan menutup temuan serta perbaikannya',
    'privacy.purpose.AUDIT_ASSESSMENT': 'menilai dan mengaudit area kerja beserta buktinya',
    'privacy.purpose.MEETING_GOVERNANCE': 'menyelenggarakan rapat harian dan menindaklanjuti keputusannya',
    'privacy.purpose.ATTENDANCE_RECORD': 'mencatat siapa yang hadir pada suatu rapat',
    'privacy.purpose.RECOGNITION_POINTS': 'poin, apresiasi, dan peringkat di dalam organisasi Anda sendiri',
    'privacy.purpose.REPORTING_ANALYTICS': 'laporan dan angka dampak pada tingkat area',
    'privacy.purpose.FINANCE_VERIFICATION': 'memverifikasi penghematan yang dilaporkan',
    'privacy.purpose.SERVICE_ADMINISTRATION': 'mengelola langganan, paket, dan modul',
    'privacy.purpose.SUPPORT_ASSIGNMENT': 'menugaskan konsultan PQM dan mencatat aksesnya',
    'privacy.purpose.OFFLINE_SYNC': 'menerima pekerjaan yang dibuat saat perangkat tidak punya sinyal',
    'privacy.purpose.EVIDENCE_STORAGE': 'menyimpan foto dan dokumen yang dipakai sebagai bukti',
    'privacy.purpose.PROFILE_PHOTO': 'menampilkan foto profil Anda',
    'privacy.purpose.REPORT_DELIVERY': 'mengirim laporan terjadwal kepada penerima yang disebut di dalamnya',
    'privacy.purpose.ACCOUNTABILITY_TRAIL': 'jejak audit yang tidak dapat diubah maupun dihapus',
    'privacy.consent.NOTICE_ACKNOWLEDGED.label': 'Saya sudah membaca pemberitahuan ini. Saya mengerti bahwa {organisation} yang menentukan bagaimana data pribadi saya dipakai di sini, dan PQM menjalankan perangkat lunaknya atas instruksi organisasi itu.',
    'privacy.consent.NOTICE_ACKNOWLEDGED.consequence': 'Mencentang ini tidak memberi izin atas apa pun — {organisation} memproses data ini untuk menjalankan hubungan kerja Anda dan memenuhi kewajiban keselamatannya, dan itu tidak bergantung pada centang Anda. Yang dicatat centang ini adalah bahwa pemberitahuan ini sudah ditunjukkan kepada Anda sebelum ada apa pun yang dicatat tentang Anda. Sampai dicentang, akun Anda tetap dalam keadaan diundang, karena kami tidak akan memulai tanpa memberi tahu Anda lebih dulu.',
    'privacy.consent.AVATAR.label': 'Saya setuju foto wajah saya ditampilkan kepada rekan kerja di organisasi ini.',
    'privacy.consent.AVATAR.consequence': 'Tanpa ini, yang tampil adalah inisial nama Anda. Tidak ada hal lain yang berubah, dan tidak ada bagian pekerjaan Anda yang terpengaruh.',
    'privacy.recipients.note': 'Alamat surel yang diketik di sini diproses di bawah perjanjian pemrosesan data organisasi Anda. Memberi tahu orang di balik alamat itu adalah tanggung jawab organisasi Anda sebagai pengendali data.',
    'privacy.withdrawEffect.ACKNOWLEDGEMENT_CANNOT_BE_WITHDRAWN': 'Pengakuan telah diberi tahu tidak dapat ditarik, karena ia bukan izin: ia mencatat bahwa pemberitahuannya sudah disampaikan. Pertanyaan tentang bagaimana data Anda dipakai diajukan kepada organisasi tempat Anda bekerja, yang menentukannya.',
    'privacy.withdrawEffect.AVATAR_DELETED': 'Foto profil Anda dilepas dan berkas gambarnya ditandai untuk dimusnahkan. Yang tampil kemudian adalah inisial nama Anda.',
    // -- Kategori data pribadi
    'privacy.category.IDENTITY': 'Nama lengkap',
    'privacy.category.CONTACT': 'Alamat surel kantor',
    'privacy.category.EMPLOYEE_NO': 'Nomor pegawai',
    'privacy.category.JOB_TITLE': 'Jabatan',
    'privacy.category.ORG_PLACEMENT': 'Unit organisasi dan area kerja',
    'privacy.category.PROFILE_PHOTO': 'Foto profil',
    'privacy.category.WORK_AREA_PHOTO': 'Foto area kerja, yang dapat memuat rekan kerja',
    'privacy.category.ACTIVITY_HISTORY': 'Riwayat apa yang dilakukan setiap orang, dan kapan',
    'privacy.category.PERFORMANCE_SCORES': 'Skor audit yang diberikan dan diterima, serta ketepatan waktu',
    'privacy.category.ATTENDANCE': 'Kehadiran rapat',
    'privacy.category.POINTS_RANKING': 'Poin dan peringkat yang terlihat rekan kerja',
    'privacy.category.REQUEST_METADATA': 'Alamat IP dan user agent pada setiap permintaan',
    'privacy.category.DEVICE_IDENTIFIER': 'Pengenal perangkat, labelnya, dan sistem operasinya',
    'privacy.category.OFFLINE_QUEUE_COPY': 'Salinan pekerjaan yang dibuat saat perangkat tidak punya sinyal',
    'privacy.category.SEARCH_INDEX_COPY': 'Salinan kata-kata dari judul dan keterangan, di indeks pencarian',
    'privacy.category.NOTIFICATION_STATE': 'Notifikasi yang diterima dan waktu dibacanya',
    'privacy.category.AUDIT_TRAIL': 'Jejak audit yang tidak dapat diubah maupun dihapus',
    'privacy.category.BENEFIT_FIGURES': 'Angka penghematan yang dilaporkan, beserta pelapor dan pemverifikasinya',
    // -- Klausa perjanjian pemrosesan data (Pasal 51)
    'privacy.clause.ROLES': 'Pembagian peran',
    'privacy.clause.CATEGORIES': 'Kategori data pribadi',
    'privacy.clause.PURPOSES': 'Tujuan pemrosesan',
    'privacy.clause.LOCATION': 'Lokasi pemrosesan',
    'privacy.clause.SUBPROCESSORS': 'Subprosesor',
    'privacy.clause.BREACH': 'Pemberitahuan kebocoran',
    'privacy.clause.SECURITY': 'Langkah keamanan',
    'privacy.clause.RIGHTS': 'Hak subjek data',
    'privacy.clause.RETENTION': 'Retensi dan pemusnahan',
    'privacy.clause.AUDIT': 'Hak pemeriksaan',
    'privacy.location.DATABASE': 'Basis data',
    'privacy.location.OBJECT_STORE': 'Penyimpanan foto dan dokumen',
    'privacy.location.CACHE': 'Salinan kerja sementara dan antrean',
    'privacy.location.PUSH_NOTIFICATION': 'Pengiriman notifikasi push',
    'privacy.location.BACKUP': 'Cadangan',
    // -- Daftar pemrosesan dan perjanjian organisasi
    'register.title': 'Apa yang diproses perangkat lunak ini',
    'register.subtitle': 'Setiap kategori data pribadi yang diproses ASSIST, untuk apa, dan atas dasar apa. Daftar ini dirakit dari modulnya sendiri, jadi ia tidak dapat menyimpang dari apa yang benar-benar terjadi.',
    'register.moduleHeading': 'Per modul',
    'register.categoryHeading': 'Per kategori data pribadi',
    'register.purposeHeading': 'Tujuan',
    'register.basisHeading': 'Dasar hukum yang disandari',
    'register.locationHeading': 'Lokasi pemrosesan',
    'register.location.undeclared': 'Belum dinyatakan. Ia dinyatakan dalam perjanjian antara organisasi Anda dan PQM, dan halaman ini diperbarui ketika sudah diputuskan.',
    'register.subprocessorHeading': 'Subprosesor',
    'register.subprocessor.none': 'Belum ada yang terdaftar. Menambahkannya menuntut pemberitahuan lebih dulu kepada organisasi Anda, dan halaman ini adalah daftar yang dirujuk perjanjiannya.',
    'register.notCollected': 'Yang tidak diproses sama sekali',
    'register.notCollected.body': 'Tidak ada NIK atau nomor kartu identitas, data kesehatan, gaji atau data keuangan pribadi, data biometrik, agama, pandangan politik, orientasi seksual, catatan kejahatan, maupun data anak. Tidak ada pengenalan wajah, tidak ada penandaan otomatis orang di foto, dan tidak ada pencarian foto berdasarkan orang.',
    'register.writeOperations': '{n, plural, other {# operasi tulis}}',
    'agreement.title': 'Perjanjian pemrosesan data',
    'agreement.version': 'Versi {version, number}',
    'agreement.acceptedBy': 'Disetujui oleh {name}, {title}, pada {date}',
    'agreement.notAccepted': 'Belum disetujui',
    'agreement.pending.title': 'Organisasi ini belum dibuka',
    'agreement.pending.body': 'Perjanjian pemrosesan data antara organisasi Anda dan PQM belum disetujui, jadi belum ada apa pun yang dapat dicatat tentang siapa pun di sini. Seorang yang berwenang di organisasi Anda menyetujuinya sekali; PQM mencatat siapa, kapan, dan versi yang mana. Tanyakan kepada manajer Anda atau kepada yang mengurus perangkat lunak ini.',
    'organisation.title': 'Organisasi Anda',
    'organisation.subtitle': 'Apa yang disepakati, apa yang diproses, dan siapa melakukan apa di sini.',
    'organisation.auditHeading': 'Jejak audit organisasi Anda sendiri',
    'organisation.auditNote': 'Setiap tindakan yang tercatat untuk organisasi Anda, termasuk akses oleh pegawai PQM. Ia tidak dapat diubah maupun dihapus, oleh siapa pun.',
    'organisation.auditEmpty': 'Belum ada apa pun yang tercatat.',
    'organisation.controllerIs': '{organisation} yang menentukan bagaimana data ini dipakai. PQM memprosesnya atas instruksi itu.',
    'organisation.auditCodeNote': 'Nama peristiwa ditampilkan apa adanya sebagaimana ia disimpan, dan itu pula bentuknya di berkas ekspor.',
    'organisation.actor.TENANT': 'Seseorang di organisasi Anda',
    'organisation.actor.PLATFORM': 'Administrator platform PQM',
    'organisation.actor.CONSULTANT': 'Konsultan PQM yang ditugaskan kepada Anda',
    'organisation.actor.SYSTEM': 'Pekerjaan terjadwal',
    'organisation.actor.ANONYMOUS': 'Seseorang yang belum masuk',
    // -- Identitas organisasi (gelombang 28 September 2026)
    'identity.title': 'Identitas organisasi',
    'identity.subtitle': 'Bagaimana organisasi Anda tampil di dalam ASSIST — logo, warna aksen, nama tampilan, zona waktu, dan bahasa bawaan.',
    'identity.save': 'Simpan perubahan',
    'identity.discard': 'Batalkan perubahan',
    'identity.saved': 'Tersimpan.',
    'identity.logo.heading': 'Logo organisasi',
    'identity.logo.label': 'Tanda logo',
    'identity.logo.hint': 'PNG atau WebP · maksimum 1 MB · sisi terpanjang 1024 px',
    'identity.logo.choose': 'Pilih berkas',
    'identity.logo.chooseAnother': 'Pilih berkas lain',
    'identity.logo.replace': 'Ganti',
    'identity.logo.remove': 'Hapus',
    'identity.logo.uploading': 'Mengunggah logo',
    'identity.logo.verifying': 'Berkasnya sedang diperiksa',
    'identity.logo.verifyingBody': 'Byte-nya sedang diperiksa. Logonya tampil setelah terbukti gambar PNG atau WebP.',
    'identity.logo.verified': 'Terverifikasi',
    'identity.logo.verifiedDetail': 'Diperiksa dari byte-nya · {size} · {width} × {height} px',
    'identity.logo.none': 'Belum ada logo. Tidak ada bagian layar yang hilang — tanda ASSIST berdiri di tempatnya.',
    'identity.logo.rejected': 'Berkas ditolak',
    'identity.logo.whereShown': 'Tampil di kepala dasbor Anda dan kepada pegawai PQM di halaman organisasi Anda. Ia tidak pernah tampil kepada organisasi lain.',
    'identity.logo.wrongType': 'Berkas itu bukan gambar PNG atau WebP. Tidak ada yang terunggah, dan logo yang Anda punya sekarang tidak berubah.',
    'identity.logo.tooLarge': 'Berkas itu lebih besar daripada 1 MB. Tidak ada yang terunggah, dan logo yang Anda punya sekarang tidak berubah.',
    'identity.name.heading': 'Nama tampilan',
    'identity.name.hint': 'Nama yang dibaca orang-orang Anda di ASSIST. Nama badan hukum pada perjanjian pemrosesan data yang sudah ditandatangani adalah hal terpisah, dan hanya PQM yang dapat mengubahnya.',
    'identity.locale.heading': 'Bahasa dan zona waktu',
    'identity.locale.label': 'Bahasa bawaan',
    'identity.timezone.label': 'Zona waktu',
    'identity.locale.hint': 'Bawaan bagi orang yang belum memilih sendiri. Setiap orang tetap dapat mengganti bahasa dari bilah atas.',
    'identity.accent.heading': 'Warna aksen',
    'identity.accent.hint': 'Warna status dan warna teks tidak pernah berubah. Warna yang tidak dapat dibedakan dari halamannya ditolak beserta alasannya, bukan diterima diam-diam.',
    'identity.accent.contrastOk': 'Kontras {ratio}:1 — lulus',
    'identity.pqm.heading': 'Hanya PQM yang dapat mengubah ini',
    'identity.pqm.legalName': 'Badan hukum',
    'identity.pqm.address': 'Alamat',
    'identity.pqm.plan': 'Paket',
    'identity.pqm.hint': 'Nama badan hukum muncul di perjanjian pemrosesan data yang sudah ditandatangani, jadi ia diubah PQM dan dicatat ke jejak audit — bukan di sini.',
    'identity.platform.heading': 'Identitas yang mengikat kontrak',
    'identity.platform.subtitle': 'Identitas organisasi — apa yang mengikat kontrak, dan apa yang dikendalikan organisasinya sendiri.',
    'identity.platform.legalName': 'Nama badan hukum',
    'identity.platform.slug': 'Alamat (slug)',
    'identity.platform.hint': 'Ketiganya milik PQM untuk diubah: nama badan hukum muncul di perjanjian pemrosesan data yang sudah ditandatangani, dan alamat adalah pintu yang dilewati setiap orang organisasi ini. Manajer organisasi tidak dapat mengubahnya, dan konsultan pun tidak.',
    'identity.platform.controlled': 'Yang dikendalikan organisasi',
    'identity.platform.controlledHint': 'Baca-saja di sini. Manajer organisasi mengubahnya dari layar Identitas organisasi miliknya sendiri; PQM melihatnya supaya dukungan dan pelanggan menatap hal yang sama.',
    'error.ACCENT_HEX_INVALID': 'Warna aksen adalah enam digit heksadesimal setelah tanda #, misalnya #2251FF.',
    'error.ACCENT_CONTRAST_TOO_LOW': 'Warna itu {ratio}:1 terhadap {surface} dan butuh minimal {minimum}:1. Ia akan nyaris tidak terlihat di halaman. Pilih nada yang lebih gelap atau lebih pekat.',
    'error.TIMEZONE_NOT_SUPPORTED': 'Zona waktu itu belum tersedia. Yang tersedia: {supported}.',
    'error.PLATFORM_STATE_CONFLICT': 'Itu tidak dapat dilakukan selama keadaannya {from}. Baca ulang halamannya — mungkin ' +
        'orang lain sudah mengubahnya.',
    'error.JOB_NOT_RETRYABLE': 'Job {job_code} diumumkan sebuah modul tetapi belum punya pekerja di belakangnya, jadi ' +
        'menjalankannya ulang hanya mengantrekan pekerjaan yang tidak diambil siapa pun.',
    'error.AUDIT_EXPORT_TOO_LARGE': 'Rentang itu memuat {found, number} baris sementara batas satu ekspor adalah ' +
        '{limit, number}. Persempit tanggalnya lalu ekspor lagi.',
    'error.LOGO_WRONG_TYPE': 'Logo adalah gambar PNG atau WebP. SVG tidak diterima.',
    'error.TENANT_AGREEMENT_PENDING': 'Organisasi Anda belum menyetujui perjanjian pemrosesan data, jadi belum ada apa pun yang dapat dicatat di sini.',
    'error.NOTICE_ACKNOWLEDGEMENT_REQUIRED': 'Pastikan Anda sudah membaca pemberitahuan ini sebelum melanjutkan.',
    'error.AGREEMENT_ALREADY_ACCEPTED': 'Organisasi ini sudah menyetujui perjanjiannya.',
    'privacy.notice.title': 'Pemberitahuan privasi',
    'privacy.notice.link': 'Pemberitahuan privasi',
    'privacy.notice.version': 'Versi {version, number}',
    'privacy.notice.effectiveFrom': 'Berlaku sejak {date}',
    'privacy.notice.publishedBy': 'Diterbitkan oleh {publisher}',
    'privacy.notice.otherLanguage': 'Baca dalam bahasa yang lain',
    'privacy.notice.unavailable': 'Pemberitahuan privasi belum dapat dimuat saat ini.',
    'privacy.basisNote.label': 'Atas dasar apa ini boleh diproses',
    'privacy.basisNote.sentence': 'Data pribadi yang dimasukkan di sini diproses atas dasar {basis}.',
    'privacy.basisNote.purposes': 'Untuk: {purposes}',
    'privacy.basisNote.more': 'Dipakai untuk apa',
    'privacy.basisNote.photoGuidance': 'Foto ditujukan untuk kondisi area. Hindari mengambil wajah rekan kerja.',
    'accept.title': 'Tetapkan kata sandi Anda',
    'accept.subtitle': 'Organisasi Anda mengundang Anda ke {organisation}.',
    'accept.password': 'Kata sandi baru',
    'accept.passwordConfirm': 'Ulangi kata sandi',
    'accept.password.hint': 'Minimal dua belas karakter.',
    'accept.consentHeading': 'Sebelum ada apa pun yang dicatat tentang Anda',
    'accept.controllerLine': '{organisation} yang menentukan bagaimana data pribadi Anda dipakai di sini. PQM menyediakan dan menjalankan perangkat lunaknya atas instruksi organisasi itu. Pertanyaan tentang data Anda diajukan kepada {organisation} lebih dulu — tanyakan kepada manajer atau bagian sumber daya manusianya.',
    'accept.submit': 'Tetapkan kata sandi dan lanjutkan',
    'accept.withoutConsent': 'Lanjutkan tanpa mengonfirmasi',
    'accept.withoutConsentHeading': 'Apa yang dilakukan centang ini, dan apa yang tidak',
    'accept.ifNotTicked.heading': 'BILA KOTAK INI TIDAK ANDA CENTANG',
    'accept.ifNotTicked.body': 'Akun Anda tetap berstatus INVITED: Anda belum dapat masuk, dan belum ada pekerjaan ' +
        'yang dapat ditugaskan kepada Anda. Tidak ada yang dihapus — tautan ini tetap berlaku ' +
        'sampai {expires} dan Anda dapat kembali ke sini. Tanyakan kepada atasan Anda atau tim ' +
        'HR {organisation} bila ada yang belum jelas.',
    'accept.ifNotTicked.bodyNoExpiry': 'Akun Anda tetap berstatus INVITED: Anda belum dapat masuk, dan belum ada pekerjaan ' +
        'yang dapat ditugaskan kepada Anda. Tidak ada yang dihapus — tautan ini tetap berlaku ' +
        'dan Anda dapat kembali ke sini. Tanyakan kepada atasan Anda atau tim HR ' +
        '{organisation} bila ada yang belum jelas.',
    'accept.needsTick': 'Centang kotak di atas untuk melanjutkan. Tidak ada isian lain yang kurang, dan tidak ' +
        'ada yang hilang bila Anda meninggalkan halaman ini.',
    'accept.declined.title': 'Tidak ada apa pun yang dicatat tentang Anda',
    'accept.declined.body': 'Kata sandi Anda belum ditetapkan dan akun Anda masih dalam keadaan diundang. Tidak ada apa pun yang dicatat tentang Anda. Anda dapat kembali ke halaman ini dari undangan Anda kapan saja.',
    'accept.invalid': 'Undangan ini tidak lagi berlaku. Mintalah undangan baru kepada manajer Anda.',
    'consentSettings.title': 'Privasi dan data Anda',
    'consentSettings.backToAccount': '‹  Akun & notifikasi',
    'account.privacyPanel.title': 'Privasi dan data Anda',
    'account.privacyPanel.noticeVersion': 'Pemberitahuan yang Anda baca: versi {version}',
    'account.privacyPanel.noticeUnknown': 'Anda belum mengakui satu pun pemberitahuan privasi',
    'account.privacyPanel.consentCount': '{count, number} catatan persetujuan',
    'account.privacyPanel.manage': 'Kelola',
    'consentSettings.subtitle': 'Apa yang Anda setujui, dan cara mengubahnya.',
    'consentSettings.granted': 'Disetujui pada {date}',
    'consentSettings.withdrawn': 'Ditarik pada {date}',
    'consentSettings.never': 'Belum disetujui',
    'consentSettings.withdraw': 'Tarik',
    'consentSettings.grant': 'Setujui',
    'consentSettings.confirmTitle': 'Tarik persetujuan ini?',
    'consentSettings.noneYet': 'Belum ada apa pun yang tercatat di sini.',
    'consentSettings.historyTitle': 'Catatan persetujuan Anda',
    'consentSettings.noticeVersion': 'Pemberitahuan privasi versi {version, number}',
    'error.CONSENT_REQUIRED': 'Ini memerlukan persetujuan Anda sebelum dapat dicatat.',
    'error.CONSENT_ALREADY_WITHDRAWN': 'Persetujuan itu sudah ditarik sebelumnya.',
    'error.CONSENT_PURPOSE_UNKNOWN': 'Itu bukan hal yang persetujuannya diminta.',
    'error.INVITATION_INVALID': 'Undangan ini tidak lagi berlaku.',
    'error.ACKNOWLEDGEMENT_CANNOT_BE_WITHDRAWN': 'Pengakuan bahwa Anda sudah diberi tahu bukan izin, jadi tidak ada yang dapat ditarik. '
        + 'Akun dan pekerjaan Anda tidak terpengaruh.',
    'error.ATTACHMENT_FILE_NOT_FOUND': 'Berkas itu tidak dapat dilampirkan. Unggah ulang dari organisasi ini.',
    'error.ATTACHMENT_FILE_NOT_USABLE': 'Berkas itu belum selesai diunggah, atau dikarantina karena isinya tidak sesuai dengan yang dinyatakan.',
    'error.ATTACHMENT_FILE_NOT_VERIFIED': 'Berkas itu masih diperiksa. Coba lagi sebentar — pemeriksaannya hitungan detik.',
    'error.PHOTO_METADATA_PENDING': 'Foto ini masih disiapkan. Coba lagi sebentar.',
    'error.LEADERBOARD_SCOPE_FORBIDDEN': 'Peringkat hanya ditampilkan di dalam organisasi Anda sendiri.',
    'error.PROCESSING_BASIS_MISSING': 'Ini tidak dapat dicatat karena tidak ada dasar pemrosesan yang dinyatakan.',
    // --- PERBAIKAN G3 ---------------------------------------------------------
    'error.PHOTO_TOO_LARGE': 'Fotonya terlalu besar. Batasnya {max_byte_size, number} byte dan {max_pixel_edge, number} ' +
        'piksel pada sisi terpanjang. Ambil ulang dari kamera aplikasi, yang mengecilkannya ' +
        'untuk Anda.',
    'error.FILE_TYPE_NOT_ALLOWED': 'Jenis berkas itu tidak dapat dilampirkan di sini. Yang diterima: foto (JPEG, PNG, WebP), ' +
        'PDF, CSV, dan lembar kerja.',
    'error.ITEM_CATEGORY_UNKNOWN': 'Kategori itu tidak ada di organisasi ini. Pilih dari daftar.',
    'error.NOT_IMPLEMENTED': 'Bagian ASSIST ini belum dibangun. Ia ada di paket Anda dan sedang dikerjakan; tidak ada ' +
        'yang salah dengan yang Anda lakukan.',
    'error.PASSWORD_TOO_WEAK': 'Kata sandi itu tidak dapat dipakai. Minimal {min_length, number} karakter, dan tidak boleh ' +
        'kata sandi yang mudah ditebak, urutan tombol, atau dibuat dari nama, surel, maupun nomor ' +
        'pegawai Anda sendiri. Tiga kata yang tidak berhubungan mudah diingat dan sulit ditebak.',
    'error.USER_ALREADY_ACTIVE': 'Orang itu sudah punya akun yang dapat dipakai di sini, jadi undangan baru akan membuat ' +
        'siapa pun yang memegang tautannya dapat mengganti kata sandinya. Minta ia memakai ' +
        '"Lupa kata sandi" saja.',
    'accept.controllerUnknown': 'organisasi yang mengundang Anda',
    'mail.pending.title': 'Surel otomatis belum dinyalakan',
    'mail.pending.body': 'Pesannya sudah diantre, tetapi tidak akan terkirim sampai penyedia surel dikonfigurasi. ' +
        'Sampaikan sendiri keterangannya, dan beri tahu orang yang menyiapkan ASSIST.',
    'invite.title': 'Undang orang',
    'invite.sent': 'Undangan tercatat untuk {email}.',
    'invite.queued': 'Undangan tercatat untuk {email}, tetapi tidak ada surel yang terkirim — lihat di bawah.',
    'readiness.title': 'Sebelum organisasi ini dapat dipakai',
    'readiness.step.ORG_UNIT_MISSING': 'Buat sekurang-kurangnya satu unit organisasi.',
    'readiness.step.LOCATION_MISSING': 'Buat sekurang-kurangnya satu area.',
    'readiness.step.ITEM_CATEGORY_MISSING': 'Tanam kategori 5R.',
    'readiness.step.WORKFLOW_MISSING': 'Terbitkan definisi alur kerja.',
    'readiness.step.NO_ONE_INVITED': 'Undang orang pertama.',
    'readiness.ready': 'Organisasi ini siap dipakai.',
    'finding.photo.add': 'Tambahkan foto',
    'finding.photo.choose': 'Pilih foto',
    'finding.photo.uploading': 'Mengunggah…',
    'finding.photo.checking': 'Berkasnya sedang diperiksa…',
    'finding.photo.alt': 'Foto {role} untuk {code}',
    'finding.photo.unavailable': 'Foto ini belum dapat ditampilkan.',
    'dashboard.decisionQueue.why': 'Mengapa ia di sini',
    'dashboard.decisionQueue.open': 'Buka antrean verifikasi',
    'dashboard.queueReason.AWAITING_VERIFICATION': 'Menunggu keputusan Anda',
    'dashboard.queueReason.SENT_BACK': 'Dikembalikan, dan berhenti di situ',
    'dashboard.queueReason.OVERDUE': 'Lewat batas waktunya',
    'dashboard.stat.areaCompletion': 'Area tuntas',
    'dashboard.areaCompletion.value': '{done, number} dari {total, number} area tanpa temuan terbuka',
    // -------------------------------------------------------------------------
    // GELOMBANG LAYAR KONSOL — 21 layar, dan setiap kalimat di keduanya.
    //
    // `catalog.test.ts` menolak kunci yang hanya punya satu bahasa DAN placeholder
    // ICU yang berbeda antar bahasa. Keduanya bukan formalitas: peta §7 menulis
    // "tidak ada string yang hanya punya satu bahasa", dan sebuah placeholder yang
    // hilang di salah satunya menghasilkan `{code}` MENTAH di layar — yaitu UAT U2-05,
    // yang lolos gerbang teks antarmuka karena gerbang itu memeriksa keberadaan kunci,
    // bukan isinya.
    // -------------------------------------------------------------------------
    'state.fieldApp.meanwhile': 'Sementara aplikasi lapangan dibangun, dua layar konsol ini milik Anda: melaporkan ' +
        'temuan, dan melihat temuan yang Anda laporkan.',
    'state.fieldApp.myFindings': 'Temuan saya',
    'actorKind.TENANT': 'Pengguna organisasi',
    'actorKind.PLATFORM': 'Admin platform',
    'actorKind.CONSULTANT': 'Konsultan PQM',
    'actorKind.SYSTEM': 'Sistem',
    /**
     * CACAT 4. "Belum masuk" berbunyi seperti percobaan yang gagal. Label ini juga pilihan
     * penyaring di layar jejak audit, tempat "saring orang yang gagal masuk" bukan yang
     * diperoleh pemakainya: yang ia peroleh adalah baris yang ditulis sebelum ada sesi.
     */
    'actorKind.ANONYMOUS': 'Tanpa sesi',
    'report.title': 'Lapor temuan',
    'report.subtitle': 'Apa yang Anda lihat, di mana, dan satu foto.',
    'report.field.title': 'Apa yang Anda lihat?',
    'report.field.title.hint': 'Satu kalimat cukup. Tulis seperti Anda mengatakannya.',
    'report.field.category': 'Kategori',
    'report.field.area': 'Lokasi',
    'report.field.description': 'Apa yang terjadi',
    'report.field.description.hint': 'Tulis apa yang Anda lihat, bukan siapa yang Anda salahkan.',
    'report.field.description.counter': '{used, number} / {max, number} karakter',
    'report.field.choose': 'Pilih satu',
    'report.field.photo': 'Foto bukti',
    'report.field.photo.hint': 'Foto inilah yang membuat temuan ini dapat diperiksa nanti. Wajah orang yang tidak ' +
        'terkait dapat dikaburkan sebelum Anda kirim.',
    'report.field.category.hint': 'Keselamatan menghentikan hitungan waktu: temuan langsung naik ke manajer area, dan ' +
        'hanya kategori ini yang membuat baris berwarna merah.',
    'report.field.area.hint': 'Di aplikasi lapangan, baris ini terisi sendiri dari tag QR di tiangnya.',
    'report.photo.replace': 'Ganti fotonya',
    'report.photo.add': 'Tambah foto',
    'report.photo.count': '{used, number} dari {max, number} · maksimal {kb, number} KB per foto',
    'report.breadcrumb': '‹  Temuan',
    'report.draftNote': 'Draf · disimpan 7 hari · urutan sama dengan aplikasi lapangan',
    'report.points.title': 'Perkiraan poin',
    'report.points.provisional': '{points, number} poin, masih sementara',
    'report.points.breakdown': '{category} ×1,0 · zona {zone, number} ×1,0 · ada foto ×1,2',
    'report.points.note': 'Poin baru final setelah ketua area mengesahkan perbaikannya. Tidak ada poin hanya ' +
        'karena melapor.',
    'report.next.title': 'Setelah ini',
    'report.next.1': 'Temuan masuk antrean areanya dan ketua area diberi tahu.',
    'report.next.2': 'Ketua area menunjuk penanggung jawab dan tenggatnya.',
    'report.next.3': 'Menutupnya wajib dengan foto sesudah — tanpa foto, tidak bisa ditutup.',
    'report.submit': 'Kirim temuan',
    'report.submit.blocked': 'Isi apa yang Anda lihat, kategorinya, areanya, dan tambahkan satu foto. Setelah itu ' +
        'tombolnya aktif.',
    'report.uploadFailed': 'Fotonya tidak selesai diunggah. Coba lagi; isian Anda tidak hilang.',
    'verify.title': 'Antrean verifikasi',
    'verify.subtitle': 'Perbaikan yang menunggu seseorang memeriksa buktinya.',
    'verify.empty.title': 'Tidak ada yang menunggu',
    'verify.empty.body': 'Setiap perbaikan yang dikirim sudah diputuskan. Inilah keadaan yang Anda inginkan dari ' +
        'layar ini.',
    'verify.column.closedBy': 'Diperbaiki oleh',
    'verify.column.decide': 'Putuskan',
    'verify.action.comparePhotos': 'Lihat fotonya',
    'verify.action.confirm': 'Sahkan perbaikan',
    'verify.action.sendBack': 'Kembalikan',
    'verify.confirmed': '{code} ditutup. Poin menjadi final bagi pelapor dan yang memperbaikinya.',
    'verify.sentBack': '{code} dikembalikan kepada yang memperbaikinya, beserta alasan Anda.',
    'verify.sendBack.title': 'Kembalikan',
    'verify.sendBack.describe': '{code} kembali kepada yang memperbaikinya. Alasan Anda yang akan ia baca.',
    'verify.sendBack.reasonHint': 'Sebutkan apa yang masih kurang, bukan bahwa ia salah.',
    'verify.footnote': 'Mengesahkan perbaikan adalah satu-satunya jalan poin berpindah dari provisional ke ' +
        'final. Dibaca per {today}.',
    'locations.title': 'Lokasi & area',
    'locations.subtitle': 'Tempat pekerjaan berlangsung, dan bagaimana keadaan tiap area.',
    'locations.add': 'Tambah area',
    'locations.add.describe': 'Satu kode, satu nama, dan unit tempatnya bernaung.',
    'locations.add.codeHint': 'Pendek, dan sama dengan yang tercetak di label QR.',
    'locations.add.description': 'Apa yang terjadi di sini',
    'locations.added': 'Area {code} siap menerima temuan.',
    'locations.column.code': 'Kode',
    'locations.column.name': 'Nama',
    'locations.column.unit': 'Unit',
    'locations.column.open': 'Temuan terbuka',
    'locations.column.completion': 'Tuntas',
    'locations.column.state': 'Keadaan',
    'locations.column.action': 'Aksi',
    'locations.completion.value': '{pct, number}%',
    'locations.state.ACTIVE': 'Aktif',
    'locations.state.INACTIVE': 'Nonaktif',
    'locations.deactivate': 'Nonaktifkan',
    'locations.deactivate.title': 'Nonaktifkan area ini?',
    'locations.deactivate.describe': 'Area {code} berhenti menerima temuan baru. Semua yang sudah tercatat di sana tetap ' +
        'terbaca, dan Anda dapat mengaktifkannya kembali.',
    'locations.deactivated': 'Area {code} kini nonaktif. Riwayatnya tidak tersentuh.',
    'locations.empty.title': 'Belum ada area',
    'locations.empty.body': 'Temuan selalu dilaporkan pada sebuah area, jadi area pertama lebih dulu.',
    'locations.qrPending': 'Mencetak label QR belum dapat dilakukan dari sini. Minta orang yang menyiapkan ASSIST ' +
        'mencetaknya.',
    'users.title': 'Pengguna & peran',
    'users.subtitle': 'Siapa yang bekerja di sini, dan apa yang boleh dilakukan masing-masing.',
    'users.invite': 'Undang orang',
    'users.invite.describe': 'Ia menerima tautan sekali pakai dan menetapkan kata sandinya sendiri.',
    'users.column.person': 'Orang',
    'users.column.email': 'Surel kerja',
    'users.column.role': 'Peran',
    'users.column.employeeNo': 'Nomor pegawai',
    'users.column.state': 'Keadaan',
    'users.state.ACTIVE': 'Aktif',
    'users.state.INVITED': 'Diundang, belum masuk',
    'users.state.DISABLED': 'Nonaktif',
    'users.state.LOCKED': 'Terkunci',
    'users.field.fullName': 'Nama lengkap',
    'users.field.email': 'Surel',
    'users.nameHidden': 'Nama tidak terlihat bagi Anda',
    'users.invitations.title': 'Undangan yang belum dipakai',
    'users.invitations.expires': 'Kedaluwarsa',
    'users.empty.title': 'Belum ada siapa pun di sini',
    'users.empty.body': 'Undang orang pertama. Semua hal lain di ASSIST menuntut ada yang mengerjakannya.',
    'users.changesPending': 'Mengubah peran, membatasi cakupan area, menonaktifkan akun, dan mengeluarkan seseorang ' +
        'dari semua perangkat belum dapat dilakukan dari layar ini.',
    'userDetail.title': 'Orang',
    'userDetail.back': 'Kembali ke daftar',
    'userDetail.jobTitle': 'Jabatan',
    'userDetail.lastLogin': 'Terakhir masuk',
    'userDetail.neverSignedIn': 'Belum pernah masuk',
    'userDetail.points': 'Poin yang diperoleh orang ini',
    'userDetail.points.none': 'Belum ada poin yang tercatat.',
    'userDetail.changesPending': 'Layar ini membaca. Mengubah peran atau cakupan, dan mengeluarkan orang ini dari semua ' +
        'perangkat, belum tersedia di sini.',
    'search.title': 'Hasil pencarian',
    'search.subtitle': 'Semua yang cocok dengan “{query}” dan boleh Anda lihat.',
    'search.filter.all': 'Semua',
    'search.type.FINDING': 'Temuan',
    'search.type.ITEM': 'Temuan',
    'search.type.USER': 'Orang',
    'search.type.LOCATION': 'Area',
    'search.type.ASSESSMENT': 'Audit',
    'search.type.DOCUMENT': 'Dokumen',
    'search.type.IDEA': 'Ide kaizen',
    'search.type.PROJECT': 'Proyek',
    'search.none.title': 'Tidak ada yang cocok',
    'search.none.body': 'Coba kata yang lebih pendek, atau kode temuan seperti TMN-2419.',
    'search.tooShort.title': 'Ketik sedikit lagi',
    'search.tooShort.body': 'Pencarian butuh sekurang-kurangnya tiga karakter agar berguna.',
    'search.isolationNote': 'Pencarian hanya menjangkau organisasi Anda sendiri, dan hanya area yang boleh Anda ' +
        'lihat. Sesuatu yang tidak ada di daftar ini bukan berarti tidak ada.',
    'search.moreResults': 'Muat hasil berikutnya',
    'huddle.title': 'Rapat harian 10 menit',
    'huddle.subtitle': 'Sepuluh menit, berdiri, membahas apa yang benar-benar menghambat lini.',
    'huddle.schedule': 'Jadwalkan rapat',
    'huddle.schedule.describe': 'Satu rapat per unit per slot waktu.',
    'huddle.schedule.unit': 'Unit',
    'huddle.schedule.when': 'Kapan',
    'huddle.scheduled': 'Rapatnya masuk agenda.',
    'huddle.state.SCHEDULED': 'Belum mulai',
    'huddle.state.RUNNING': 'Berjalan',
    'huddle.state.OVERRUN': 'Lewat waktu',
    'huddle.state.CLOSED': 'Selesai',
    'huddle.facet.NO_ATTENDEES': 'Tanpa peserta',
    'huddle.facilitator': 'Dipimpin {name}',
    'huddle.timer.label': 'Waktu terpakai',
    'huddle.timer.running': '{elapsed} dari {box}',
    'huddle.timer.over': '{elapsed} — sudah lewat dari {box}',
    'huddle.agenda': 'Agenda — {discussed, number} dari {total, number} dibahas',
    'huddle.agenda.empty': 'Belum ada butir agenda. Pilih dari daftar di bawah, atau tulis butir Anda sendiri.',
    'huddle.item.add': 'Tambah butir agenda',
    'huddle.item.add.describe': 'Sesuatu yang perlu dibahas dan bukan salah satu temuan terbuka.',
    'huddle.item.title': 'Yang dibahas',
    'huddle.item.finding': 'Temuan',
    'huddle.item.outcome': 'Hasil',
    'huddle.item.markDiscussed': 'Tandai sudah dibahas…',
    'huddle.item.notDiscussed': 'Tidak dibahas',
    'huddle.outcome.RESOLVED': 'Selesai',
    'huddle.outcome.CARRIED_OVER': 'Dibawa ke besok',
    'huddle.outcome.ACTION_CREATED': 'Tindakan dibuat',
    'huddle.outcome.NOTED': 'Dicatat',
    'huddle.candidates': 'Temuan terbuka yang layak dibahas',
    'huddle.candidates.add': 'Masukkan ke agenda',
    'huddle.attendance': 'Kehadiran — {present, number} dari {total, number} hadir',
    'huddle.attendance.none': 'Belum ada yang tercatat.',
    'huddle.attendance.present': 'Hadir',
    'huddle.attendance.absent': 'Tidak hadir',
    'huddle.minutes.ready': 'Notulen tersedia di sini. Tidak ada surel yang dikirim kepada siapa pun — ASSIST ' +
        'belum dapat mengirim surel di versi ini, jadi teruskan tautannya sendiri.',
    'huddle.minutes.pending': 'Notulen belum dibangkitkan di versi ini. Yang dibahas tercatat di sini dan di jejak ' +
        'audit.',
    'huddle.empty.title': 'Belum ada rapat',
    'huddle.empty.body': 'Jadwalkan yang pertama. Sepuluh menit sehari, itu seluruh gagasannya.',
    'escalation.title': 'Eskalasi & sorotan',
    'escalation.subtitle': 'Apa yang menunggu terlalu lama, dan apa yang layak disebut di depan orang.',
    'escalation.readOnly': 'Anda dapat membaca layar ini. Mengubah tangga eskalasi dan sorotan adalah wewenang ' +
        'manajer area.',
    'escalation.open': 'Sedang dieskalasi',
    'escalation.ladder': 'Tangga empat tingkat',
    'escalation.level': 'Tingkat {level, number}',
    'escalation.level.caption': '{role}, dalam {hours, number} jam',
    'escalation.column.level': 'Tingkat',
    'escalation.column.trigger': 'Sebab',
    'escalation.column.target': 'Ditujukan ke',
    'escalation.column.due': 'Batas di tingkat ini',
    'escalation.column.sla': 'Waktu yang diberikan',
    'escalation.column.triggers': 'Pemicu',
    'escalation.sla.value': '{hours, number} jam',
    'escalation.trigger.SLA_BREACH': 'Lewat batas waktunya',
    'escalation.trigger.RECURRENCE_90D': 'Terulang dalam 90 hari',
    'escalation.trigger.SAFETY_FLAG': 'Ditandai risiko keselamatan',
    'escalation.trigger.MANUAL': 'Diangkat seseorang',
    'escalation.target.LDR': 'Leader area',
    'escalation.target.MGR': 'Manajer area',
    'escalation.target.ORG_UNIT_LEAD': 'Manajer pabrik',
    'escalation.target.DISTRIBUTION': 'Komite pengarah (diberi tahu, tanpa hak di sistem)',
    'escalation.target.OPR': 'Operator',
    'escalation.target.AUD': 'Auditor internal',
    'escalation.target.FAC': 'Fasilitator perbaikan',
    'escalation.target.KON': 'Konsultan PQM',
    'escalation.target.PLT': 'Administrator platform',
    'escalation.target.ROLE': 'Menurut peran',
    'escalation.none.title': 'Tidak ada yang dieskalasi',
    'escalation.none.body': 'Tidak ada yang melewati batas waktu tingkatnya. Ini kondisi baik, bukan kondisi ' +
        'kosong.',
    'escalation.rules.edit': 'Ubah tangga',
    'escalation.rules.describe': 'Waktu yang diberikan tidak boleh memendek saat tingkatnya naik, atau ia berhenti ' +
        'menjadi tangga. Setiap perubahan tercatat beserta nilai sebelum dan sesudahnya.',
    'escalation.rules.slaFor': 'Tingkat {level, number} — {role}, jam',
    'escalation.rules.saved': 'Tangga tersimpan, dan perubahannya masuk jejak audit.',
    'spotlight.title': 'Sorotan bulan ini',
    'spotlight.empty': 'Belum ada sorotan.',
    'spotlight.remove': 'Cabut',
    'spotlight.remove.describe': 'Ia berhenti ditampilkan. Catatannya tetap ada, termasuk siapa yang mencabutnya dan ' +
        'kapan.',
    'spotlight.removed': 'Sudah dicabut.',
    'spotlight.subject.USER': 'Orang',
    'spotlight.subject.ITEM': 'Temuan',
    'spotlight.subject.LOCATION': 'Area',
    'points.title': 'Poin & aturan',
    'points.subtitle': 'Apa yang menghasilkan poin, siapa yang menyetujui tiap pemberian, dan dari mana ' +
        'peringkatnya.',
    'points.rules': 'Aturan',
    'points.ledger': 'Buku besar',
    'points.ledger.empty': 'Belum ada poin yang diberikan.',
    'points.leaderboard': 'Peringkat {month}',
    'points.leaderboard.empty': 'Belum ada yang punya poin final bulan ini.',
    'points.leaderboard.finalOnly': 'Peringkat menghitung poin final saja. Poin provisional ditampilkan terpisah supaya ' +
        'peringkat tidak dapat digoyang dengan mengirim banyak laporan.',
    'points.rule.add': 'Tambah aturan',
    'points.rule.add.describe': 'Sebuah aturan menunggu satu peristiwa dan memberi poin kepada satu orang di dalamnya. ' +
        'Hanya peristiwa yang benar-benar diterbitkan sistem yang ditawarkan.',
    'points.rule.codeHint': 'Huruf besar, angka, dan garis bawah. Ia muncul di setiap baris buku besar.',
    'points.rule.capValue': 'Batas untuk periode itu',
    'points.rule.created': 'Aturan {code} aktif.',
    'points.rule.changed': 'Aturan {code} diperbarui, dan perubahannya masuk jejak audit.',
    'points.rule.pause': 'Tunda',
    'points.rule.resume': 'Aktifkan',
    'points.rule.pause.describe': 'Aturan {code} berhenti memberi poin. Poin yang sudah diberikan tetap di tempatnya.',
    'points.rules.empty.title': 'Belum ada aturan',
    'points.rules.empty.body': 'Tanpa aturan, tidak ada yang menghasilkan poin dan peringkatnya tetap kosong.',
    'points.column.rule': 'Aturan',
    'points.column.trigger': 'Dipicu oleh',
    'points.column.subject': 'Diberikan kepada',
    'points.column.points': 'Poin',
    'points.column.cap': 'Batas',
    'points.column.state': 'Keadaan',
    'points.column.person': 'Orang',
    'points.column.approval': 'Persetujuan',
    'points.column.awardedAt': 'Diberikan',
    'points.column.rank': 'Peringkat',
    'points.column.final': 'Final',
    'points.column.provisional': 'Provisional',
    'points.trigger.FINDING_CREATED': 'Sebuah temuan dilaporkan',
    'points.trigger.FINDING_VERIFIED': 'Sebuah perbaikan disahkan',
    'points.trigger.FINDING_CLOSED': 'Sebuah temuan ditutup',
    'points.trigger.ASSESSMENT_SUBMITTED': 'Sebuah audit dikirim',
    'points.trigger.IDEA_APPROVED': 'Sebuah ide kaizen disetujui',
    'points.trigger.IDEA_BENEFIT_VERIFIED': 'Sebuah penghematan disahkan keuangan',
    'points.trigger.MEETING_CLOSED': 'Sebuah rapat ditutup',
    'points.subject.ACTOR': 'Yang melakukannya',
    'points.subject.REPORTER': 'Yang melaporkannya',
    'points.subject.OWNER': 'Yang memperbaikinya',
    'points.subject.VERIFIER': 'Yang memverifikasinya',
    'points.awardState.PROVISIONAL': 'provisional sampai diverifikasi',
    'points.awardState.FINAL': 'final',
    'points.capPeriod.NONE': 'Tanpa batas',
    'points.capPeriod.DAY': 'per hari',
    'points.capPeriod.WEEK': 'per minggu',
    'points.capPeriod.MONTH': 'per bulan',
    'points.cap.none': 'Tidak ada',
    'points.cap.value': '{value, number} {period}',
    'points.ruleState.ACTIVE': 'Aktif',
    'points.ruleState.PAUSED': 'Ditunda',
    'points.ruleState.PENDING_APPROVAL': 'Menunggu persetujuan',
    'points.ruleState.CANCELLED': 'Dibatalkan',
    'points.ledgerState.PROVISIONAL': 'Provisional',
    'points.ledgerState.FINAL': 'Final',
    'points.ledgerState.REVERSED': 'Dibatalkan',
    'points.approval.NOT_REQUIRED': 'Tidak diperlukan',
    'points.approval.PENDING': 'Menunggu Anda',
    'points.approval.APPROVED': 'Disetujui',
    'points.approval.REJECTED': 'Ditolak',
    'points.award.approve': 'Setujui',
    'points.award.approved': 'Disetujui. Poinnya menjadi final.',
    'points.award.reverse': 'Batalkan',
    'points.award.reverse.describe': 'Poinnya berhenti dihitung, dan barisnya tetap ada beserta alasan Anda. Yang kehilangan ' +
        'poinnya akan dapat membaca sebabnya.',
    'points.award.reversed': 'Dibatalkan, dengan alasan Anda tercatat.',
    'points.stat.activeRules': 'Aturan aktif',
    'points.stat.pending': 'Menunggu persetujuan',
    'points.stat.pending.caption': 'Yang ini belum dihitung sampai Anda memutuskan.',
    'points.stat.finalOnPage': 'Poin final yang terdaftar',
    'points.stat.finalOnPage.caption': 'Dari {rows, number} baris di bawah, bukan dari seluruh buku besar.',
    'points.stat.topRank': 'Puncak peringkat',
    'points.stat.topRank.caption': '{points, number} poin final',
    'reports.title': 'Laporan & analitik',
    'reports.subtitle': 'Angkanya, dan apa yang ada di belakang masing-masing.',
    'reports.period.from': 'Dari',
    'reports.period.to': 'Sampai',
    'reports.money': '{currency} {amount}',
    'reports.stat.verifiedImpact': 'Dampak terverifikasi',
    'reports.stat.verifiedImpact.caption': 'Dari {claims, number} klaim yang disahkan keuangan.',
    'reports.stat.excluded': 'Klaim yang dikeluarkan',
    'reports.stat.excluded.caption': '{currency} {amount} menunggu pengesahan keuangan.',
    'reports.stat.opened': 'Temuan dibuka',
    'reports.stat.closed': 'Temuan ditutup',
    'reports.stat.period': '{from} sampai {to}',
    'reports.byCategory': 'Menurut kategori 5R',
    'reports.column.opened': 'Dibuka',
    'reports.column.closed': 'Ditutup',
    'reports.column.name': 'Laporan',
    'reports.column.kind': 'Apa yang dilaporkan',
    'reports.column.schedule': 'Jadwal',
    'reports.column.recipients': 'Penerima',
    'reports.column.lastRun': 'Terakhir dijalankan',
    'reports.exportCsv': 'Unduh ini sebagai CSV',
    'reports.noData.title': 'Tidak ada kejadian pada periode ini',
    'reports.noData.body': 'Lebarkan rentang tanggalnya, atau pilih bulan yang sudah berakhir.',
    'reports.aggregatesPending': 'Pekerjaan malam yang menyusun angka-angka ini belum pernah berjalan di lingkungan ini. ' +
        'Itu bukan hal yang sama dengan “tidak ada kejadian”.',
    'reports.saved': 'Laporan terjadwal',
    'reports.saved.empty': 'Belum ada laporan terjadwal.',
    'reports.saved.add': 'Jadwalkan laporan',
    'reports.saved.add.describe': 'Apa yang dilaporkan, dalam format apa, kepada siapa, dan seberapa sering.',
    'reports.saved.created': 'Laporan {code} terjadwal.',
    'reports.saved.delete': 'Hapus',
    'reports.saved.delete.describe': 'Jadwalnya hilang. Catatan apa yang pernah dikirim, dan kepada siapa, tetap ada.',
    'reports.saved.deleted': 'Jadwalnya sudah hilang.',
    'reports.field.code': 'Kode pendek',
    'reports.field.format': 'Format',
    'reports.field.schedule': 'Seberapa sering',
    'reports.field.schedule.hint': 'Pilih “Hanya bila diminta” bila Anda menjalankannya sendiri.',
    'reports.field.recipient': 'Kirim ke',
    'reports.schedule.manual': 'Hanya bila diminta',
    'reports.schedule.monthly': 'Setiap bulan pada tanggal {day}, pukul {time}',
    'reports.schedule.weekly': 'Setiap hari {weekday}, pukul {time}',
    'reports.schedule.daily': 'Setiap hari pukul {time}',
    'reports.schedule.raw': 'Jadwal: {expression}',
    'reports.weekday.0': 'Minggu',
    'reports.weekday.1': 'Senin',
    'reports.weekday.2': 'Selasa',
    'reports.weekday.3': 'Rabu',
    'reports.weekday.4': 'Kamis',
    'reports.weekday.5': 'Jumat',
    'reports.weekday.6': 'Sabtu',
    'reports.neverRun': 'Belum pernah',
    'reports.runNow': 'Jalankan sekarang',
    'reports.run.queued': 'Jalannya masuk antrean ({state}).',
    'reports.run.noWorker': 'Jalannya tercatat ({state}), tetapi pekerja yang menyusun berkasnya belum menyala di ' +
        'versi ini. Pakai “Unduh ini sebagai CSV” di atas untuk yang ada di layar.',
    'reports.exportWorkerMissing': 'Berkas terjadwal belum dihasilkan di versi ini. Jadwal dan penerimanya tersimpan dan ' +
        'akan berjalan ketika pekerjanya dinyalakan.',
    'reports.runState.QUEUED': 'menunggu di antrean',
    'reports.runState.RUNNING': 'sedang dijalankan',
    'reports.runState.READY': 'siap',
    'reports.runState.FAILED': 'gagal',
    'reports.runState.EXPIRED': 'kedaluwarsa',
    'reports.kind.FINDINGS_BY_MONTH': 'Temuan per bulan',
    'reports.kind.FINDINGS_OVERDUE': 'Temuan lewat tenggat',
    'reports.kind.AUDIT_SCORECARD': 'Kartu skor audit',
    'reports.kind.ESCALATION_LOG': 'Catatan eskalasi',
    'reports.kind.POINTS_LEDGER': 'Buku besar poin',
    'reports.kind.VERIFIED_IMPACT': 'Dampak terverifikasi',
    'audits.title': 'Audit &',
    'audits.titleAccent': 'Asesmen',
    'audits.subtitle.data': 'Instrumen {instrument} v{version, number} · {areas, number} area · kriteria gagal otomatis melahirkan temuan',
    'audits.manageInstrument': 'Kelola instrumen',
    'audits.manageInstrument.note': 'Belum dapat diubah — diterbitkan PQM, hanya dapat dibaca di produk.',
    'audits.stat.pastSchedule': 'Area lewat jadwal',
    'audits.stat.pastSchedule.trend': 'perhatian',
    'audits.pastScheduleBadge': '{count, number} lewat jadwal',
    'audits.state.PAST_SCHEDULE': 'Lewat jadwal',
    'audits.stateWithScore': '{state} · {pct, number}',
    'audits.subtitle': 'Berapa skor tiap area, dan apa yang dihasilkan audit terakhir.',
    'audits.list': 'Audit',
    'audits.schedule': 'Jadwalkan audit',
    'audits.schedule.describe': 'Satu instrumen, satu area, satu auditor, dan satu waktu.',
    'audits.schedule.when': 'Kapan',
    'audits.scheduled': 'Auditnya masuk jadwal.',
    'audits.column.area': 'Area',
    'audits.column.instrument': 'Instrumen',
    'audits.column.auditor': 'Auditor',
    'audits.column.state': 'Keadaan',
    'audits.column.score': 'Skor',
    'audits.column.when': 'Kapan',
    'audits.column.criterion': 'Kriteria',
    'audits.column.conforms': 'Sesuai',
    'audits.column.note': 'Catatan',
    'audits.column.latest': 'Terakhir',
    'audits.column.previous': 'Sebelumnya',
    'audits.column.delta': 'Perubahan',
    'audits.state.SCHEDULED': 'Terjadwal',
    'audits.state.IN_PROGRESS': 'Berjalan',
    'audits.state.SUBMITTED': 'Selesai',
    'audits.state.VOIDED': 'Dibatalkan',
    'audits.band.HIJAU': 'Hijau',
    'audits.band.KUNING': 'Kuning',
    'audits.band.MERAH': 'Merah',
    'audits.pct': '{pct, number}%',
    'audits.delta': '{delta, number} poin',
    'audits.instrumentVersion': 'versi {version, number}',
    'audits.conform': 'Ya',
    'audits.notConform': 'Tidak',
    'audits.scorecard': 'Kartu skor — {area}',
    'audits.scorecard.total': '{total, number} dari {max, number}, yaitu {pct, number}%.',
    'audits.scorecard.noAnswers': 'Belum ada kriteria yang dijawab.',
    'audits.comparison': 'Dibandingkan dengan audit sebelumnya',
    'audits.comparison.empty': 'Belum ada yang dapat dibandingkan — sebuah area butuh dua audit yang sudah selesai.',
    'audits.comparison.noPrevious': 'Audit pertama',
    'audits.stat.total': 'Audit',
    'audits.stat.submitted': 'Selesai',
    'audits.stat.average': 'Skor rata-rata',
    'audits.stat.average.none': 'Belum ada audit yang selesai.',
    'audits.stat.spawned': 'Temuan yang lahir dari audit',
    'audits.stat.spawned.caption': 'Kriteria yang gagal melahirkan temuan beserta penanggung jawab dan tenggatnya.',
    'audits.empty.title': 'Belum ada audit',
    'audits.empty.body': 'Jadwalkan yang pertama dengan instrumen yang sudah terbit.',
    'audits.auditorSelfOnly': 'Anda dapat menjadwalkan audit untuk diri sendiri. Menugaskan orang lain adalah wewenang manajer.',
    'audits.offlineNote': 'Audit yang direkam di ponsel dan belum bertemu jaringan tidak ada di daftar ini. Ia ' +
        'muncul setelah ponselnya tersinkron.',
    'audits.fieldOnlyNote': 'Menjawab kriteria dan mengirim audit berlangsung di aplikasi lapangan — satu kriteria ' +
        'per layar, dengan foto wajib saat sesuatu tidak memenuhi standar.',
    'platform.tenants.title': 'Organisasi klien',
    'platform.tenants.subtitle': 'Setiap organisasi di platform ini, dan apa yang dibeli masing-masing.',
    'platform.tenants.empty.title': 'Belum ada organisasi',
    'platform.tenants.empty.body': 'Klien pertama dibuat dari sini.',
    /** GELOMBANG M15 — lihat catatan pada katalog Inggris di atas. */
    'platform.lifecycle.title': 'Siklus hidup',
    'platform.lifecycle.body': 'Menangguhkan menghentikan pemrosesan bagi semua orang di organisasi ini, seketika. Tidak ada data yang dihapus.',
    'platform.lifecycle.suspend': 'Tangguhkan organisasi',
    'platform.lifecycle.resume': 'Pulihkan organisasi',
    'platform.lifecycle.suspend.title': 'Tangguhkan {name}?',
    'platform.lifecycle.suspend.body': 'Semua orang di organisasi ini dikeluarkan pada saat Anda menekan tombolnya — siapa pun yang ' +
        'sedang mengisi formulir kehilangan isiannya. Tidak ada yang dihapus, dan Anda dapat ' +
        'memulihkannya nanti. Memulihkan tidak mengembalikan sesinya: semua orang masuk lagi.',
    'platform.lifecycle.resume.title': 'Pulihkan {name}?',
    'platform.lifecycle.resume.body': 'Organisasinya dapat dipakai kembali dan orang-orangnya dapat masuk. Sesi lama mereka tidak dipulihkan.',
    'platform.lifecycle.reason': 'Alasan (opsional, tercatat di jejak audit)',
    'platform.lifecycle.suspendedAt': 'Ditangguhkan pada {at}.',
    'platform.plan.title': 'Paket',
    'platform.plan.body': 'Ini satu-satunya tempat paket diubah. Mengubahnya tidak menyalakan modul — itu tetap satu ' +
        'tindakan sadar per modul, di bawah.',
    'platform.plan.current': 'Paket',
    'platform.plan.choose': 'Pilih paket',
    'platform.plan.save': 'Ubah paket',
    'platform.plan.title.confirm': 'Pindahkan {name} ke {plan}?',
    'platform.plan.body.confirm': 'Paketnya berubah sekarang. Modul yang dibawa paket baru ditampilkan sesudahnya; menyalakan ' +
        'masing-masing tetap keputusan tersendiri.',
    'platform.usage.title': 'Pemakaian bulan ini',
    'platform.usage.body': 'Dihitung per meter, untuk {period}.',
    'platform.usage.column.meter': 'Meter',
    'platform.usage.column.value': 'Terpakai',
    'platform.usage.column.quota': 'Kuota',
    'platform.usage.unknown': 'Belum diukur',
    'platform.usage.empty.title': 'Belum ada meter',
    'platform.usage.empty.body': 'Meter muncul begitu sebuah modul dinyalakan untuk organisasi ini.',
    'platform.provlog.title': 'Log penyediaan',
    'platform.provlog.body': 'Apa yang sudah dan belum dilakukan untuk membuka organisasi ini.',
    'platform.provlog.empty.title': 'Belum ada yang tercatat',
    'platform.provlog.empty.body': 'Pembuatan organisasi adalah baris pertamanya.',
    'platform.consultants.title': 'Akses konsultan',
    'platform.consultants.body': 'Satu-satunya pengecualian isolasi antar organisasi yang dinyatakan. Pemberian, perpindahan ' +
        'ke klien, dan pencabutan seluruhnya tercatat — dan pencabutan mengeluarkan konsultan dari ' +
        'organisasi ini seketika.',
    'platform.consultants.assign': 'Tugaskan konsultan',
    'platform.consultants.assign.title': 'Beri seorang konsultan akses ke {name}?',
    'platform.consultants.assign.body': 'Ia akan dapat membaca data organisasi ini begitu ia berpindah ke dalamnya. Setiap ' +
        'perpindahan ditulis ke jejak audit — jejak organisasi ini sendiri, bukan hanya jejak kami.',
    'platform.consultants.choose': 'Konsultan',
    'platform.consultants.choose.placeholder': 'Pilih konsultan PQM',
    'platform.consultants.revoke': 'Cabut',
    'platform.consultants.revoke.title': 'Cabut akses {name}?',
    'platform.consultants.revoke.body': 'Ia berlaku pada permintaan berikutnya: sesi mana pun yang sedang ia buka pada organisasi ' +
        'ini dicabut di dalam transaksi yang sama.',
    'platform.consultants.granted': 'Sejak {at}, diberikan {by}',
    'platform.consultants.empty.title': 'Belum ada konsultan yang punya akses',
    'platform.consultants.empty.body': 'Tidak seorang pun dari PQM dapat membaca data organisasi ini sampai ada yang ditugaskan di sini.',
    'platform.jobs.title': 'Pekerjaan latar',
    'platform.jobs.body': 'Setiap jalan yang dicatat pekerja. Menjalankan ulang menyisipkan jalan baru; ia tidak pernah menimpa kegagalannya.',
    'platform.jobs.empty.title': 'Belum ada jalan yang tercatat',
    'platform.jobs.empty.body': 'Pekerja menulis satu baris setiap kali ia menjalankan satu siklus.',
    'platform.jobs.retry': 'Jalankan ulang',
    'platform.jobs.cannotRetry': 'Belum ada pekerja di belakang job ini',
    'platform.jobs.column.job': 'Job',
    'platform.jobs.column.when': 'Dijadwalkan',
    'platform.jobs.column.state': 'Keadaan',
    'platform.jobs.attempt': 'Percobaan {n, number}',
    'platform.jobs.retryOf': 'Jalan ulang dari kegagalan sebelumnya',
    'platform.jobState.QUEUED': 'Mengantre',
    'platform.jobState.RUNNING': 'Berjalan',
    'platform.jobState.SUCCEEDED': 'Berhasil',
    'platform.jobState.FAILED': 'Gagal',
    'platform.jobState.SKIPPED_NO_ENTITLEMENT': 'Dilewati — modul mati',
    'platform.incidents.title': 'Insiden',
    'platform.incidents.body': 'Insiden adalah peristiwa yang menimpa platform, jadi ia tidak dimiliki organisasi mana pun.',
    'platform.incidents.empty.title': 'Tidak ada insiden',
    'platform.incidents.empty.body': 'Buka satu ketika ada hal yang perlu ditindaklanjuti.',
    'platform.incidents.open': 'Buka insiden',
    'platform.incidents.open.title': 'Buka insiden',
    'platform.incidents.open.body': 'Ia tampil di layar ini sampai ada yang menutupnya.',
    'platform.incidents.close': 'Tutup',
    'platform.incidents.close.title': 'Tutup “{title}”?',
    'platform.incidents.close.body': 'Ia tetap tercatat. Penutupannya ditulis ke jejak audit.',
    'platform.incidents.severity': 'Kegawatan',
    'platform.incidents.headline': 'Apa yang terjadi',
    'platform.incidents.detail': 'Keterangan (opsional)',
    'platform.incidents.relatedJob': 'Kode job terkait (opsional)',
    'platform.incidentState.OPEN': 'Terbuka',
    'platform.incidentState.CLOSED': 'Tertutup',
    'platform.incidents.openedAt': 'Dibuka {at}',
    'platform.incidents.closedAt': 'Ditutup {at}',
    'platform.anchors.title': 'Jangkar rantai',
    'platform.anchors.body': 'Rantai hash yang hanya konsisten dengan dirinya sendiri dapat disusun ulang oleh pemegang ' +
        'basis datanya. Setiap jangkar menyalin posisi dan hash terakhir ke penyimpanan yang tidak ' +
        'dapat ditulis basis data — itulah yang membuat janji di atas dapat diperiksa dari luarnya.',
    'platform.anchors.empty.title': 'Belum ada jangkar yang ditulis',
    'platform.anchors.empty.body': 'Job penjangkaran menulis satu per rantai per hari, begitu penyimpanan objek dapat dicapai. ' +
        'Selama yang pertama belum ada, rantainya hanya dapat diperiksa terhadap dirinya sendiri.',
    'platform.anchors.column.chain': 'Rantai',
    'platform.anchors.column.seq': 'Posisi',
    'platform.anchors.column.at': 'Dijangkarkan',
    'platform.anchors.column.ref': 'Objek',
    'platform.anchors.verified': '{checked, number} baris diperiksa dari jangkar terakhir — tidak ada yang putus.',
    'platform.anchors.broken': 'Rantainya putus pada posisi {seq, number}.',
    'platform.export.title': 'Ekspor jejak',
    'platform.export.body': 'Ekspornya sendiri tercatat di jejak. Ia selesai di dalam permintaannya, jadi tidak ada tanda ' +
        'terima yang harus dikejar — dan ia memuat paling banyak {limit, number} baris.',
    'platform.export.from': 'Dari',
    'platform.export.to': 'Sampai',
    'platform.export.run': 'Ekspor',
    'platform.export.done': '{rows, number} baris diekspor sebagai {filename}.',
    'platform.isolationException': 'Layar ini melintasi organisasi, dan ia satu-satunya tempat yang boleh. Di semua tempat ' +
        'lain, satu organisasi tidak dapat melihat organisasi lain sama sekali.',
    'platform.column.tenant': 'Organisasi',
    'platform.column.slug': 'Nama pendek',
    'platform.column.plan': 'Paket',
    'platform.column.state': 'Keadaan',
    'platform.column.activated': 'Dibuka',
    'platform.column.module': 'Modul',
    'platform.column.moduleName': 'Nama',
    'platform.column.kernel': 'Kernel',
    'platform.column.entitlement': 'Entitlement',
    'platform.column.requires': 'Menuntut',
    'platform.tenantState.PROVISIONING': 'Sedang disiapkan',
    'platform.tenantState.ACTIVE': 'Terbuka',
    'platform.tenantState.SUSPENDED': 'Ditangguhkan',
    'platform.tenantState.CLOSED': 'Ditutup',
    'platform.stat.tenants': 'Organisasi',
    'platform.stat.active': 'Terbuka',
    'platform.stat.provisioning': 'Sedang disiapkan',
    'platform.stat.provisioning.caption': 'Menunggu perjanjian pemrosesan data yang ditandatangani.',
    'platform.back': 'Kembali',
    'platform.tenantDetail.title': 'Organisasi',
    'platform.tenantDetail.legalName': 'Nama badan hukum',
    'platform.tenantDetail.users': 'Orang',
    'platform.tenantDetail.modules': 'Modul',
    'platform.tenantDetail.modules.note': 'Menyalakan modul berlaku pada permintaan berikutnya — tanpa penggelaran, dan di bawah ' +
        'tiga puluh menit dari ujung ke ujung.',
    'platform.module.core': 'Inti, tidak dapat dimatikan',
    'platform.module.on': 'Menyala',
    'platform.module.off': 'Mati',
    'platform.module.enable': 'Nyalakan',
    'platform.module.disable': 'Matikan',
    'platform.module.enabled': 'Modul {module} menyala. Menu, rute, dan laporannya hidup sekarang.',
    'platform.module.disabled': 'Modul {module} mati.',
    'platform.module.enable.describe': 'Modul {module} menjadi terlihat bagi organisasi ini: menunya muncul, rutenya menjawab, ' +
        'pekerjaannya dijadwalkan.',
    'platform.module.disable.describe': 'Modul {module} hilang dari menu mereka dan rutenya berhenti menjawab. Data mereka ' +
        'tetap utuh dan masih dapat diekspor, tetapi tidak dapat diubah. Poin yang sudah ' +
        'diberikan tidak ditarik.',
    // -- ALUR ORGANISASI BARU (lihat catatan pada blok en-US).
    'platform.tenants.add': 'Tambah organisasi',
    'platform.tenants.empty.action': 'Tambah organisasi pertama',
    'platform.newTenant.title': 'Organisasi baru',
    'platform.newTenant.describe': 'Organisasinya lahir tertutup. Tidak ada seorang pun dapat masuk ke dalamnya sebelum ' +
        'perjanjian pemrosesan datanya tercatat — itulah yang menahan data pribadi diproses ' +
        'sebelum Pasal 51 terpenuhi. Tiga langkah berikutnya muncul di halamannya begitu ia ada.',
    'platform.newTenant.slug': 'Nama singkat',
    'platform.newTenant.slug.hint': 'Huruf kecil, angka, dan tanda hubung. Inilah yang PERSIS diketik orang di kolom ' +
        'Organisasi saat masuk, jadi pilih yang mereka kenali dan dapat mereka eja. Ia tidak ' +
        'dapat diubah sesudahnya.',
    'platform.newTenant.name': 'Nama',
    'platform.newTenant.name.hint': 'Nama organisasi sebagaimana tampil di layar.',
    'platform.newTenant.legalName': 'Nama badan hukum',
    'platform.newTenant.legalName.hint': 'Nama yang tertulis di kontrak. Ia muncul di perjanjian pemrosesan data. Kosongkan ' +
        'untuk memakai nama di atas.',
    'platform.newTenant.plan': 'Paket',
    'platform.newTenant.plan.choose': 'Pilih paket',
    'platform.newTenant.locale': 'Bahasa baku',
    'platform.newTenant.timezone': 'Zona waktu',
    'platform.newTenant.submit': 'Buat organisasi',
    'platform.newTenant.created': '{name} dibuat. Tiga langkah tersisa, dan semuanya ada di bawah.',
    'platform.setup.title': 'Menyiapkan organisasi ini',
    'platform.setup.note': 'Empat langkah memisahkan organisasi baru dari temuan pertamanya. Keempatnya ' +
        'dikerjakan dari sini. Tidak satu pun menuntut terminal, klien basis data, atau ' +
        'orang yang punya akses ke server.',
    'platform.setup.unknown': 'Sampai di mana organisasi ini belum dapat dibaca barusan. Langkah di bawah ' +
        'ditampilkan tanpa keadaannya, alih-alih dengan keadaan yang mungkin keliru.',
    'platform.setup.done': 'Organisasi ini dapat dipakai. Manajernya masuk dan mulai mencatat temuan.',
    'platform.setup.state.done': 'Selesai',
    'platform.setup.state.pending': 'Belum',
    'platform.setup.step1': '1 · Organisasi dibuat',
    'platform.setup.step1.body': '{name} ada, dengan nama singkat {slug} pada paket {plan}.',
    'platform.setup.step2': '2 · Perjanjian pemrosesan data',
    'platform.setup.step3': '3 · Kategori 5R dan alur kerja',
    'platform.setup.step4': '4 · Manajer pertama',
    'platform.agreement.body': 'Catat nama dan jabatan orang di pihak pelanggan yang berwenang menyetujui ' +
        'perjanjiannya. Sampai ini tercatat, siapa pun yang mencoba masuk ke organisasi ini ' +
        'dijawab bahwa perjanjiannya masih menunggu — sebuah keadaan, bukan galat.',
    'platform.agreement.accepted': 'Versi {version} disetujui oleh {name}. Organisasinya terbuka.',
    'platform.agreement.name': 'Nama orang yang berwenang',
    'platform.agreement.jobTitle': 'Jabatannya',
    'platform.agreement.submit': 'Catat persetujuan dan buka organisasinya',
    'platform.agreement.recorded': 'Persetujuan tercatat. {name} terbuka.',
    'platform.provisioning.body': 'Kategori dan definisi alur kerja diterbitkan ASSIST sendiri pada saat perjanjiannya ' +
        'tercatat. Tanpa keduanya, tidak satu pun temuan dapat dilaporkan.',
    'platform.provisioning.counts': '{categories} kategori · {workflows} definisi alur kerja diterbitkan.',
    'platform.provisioning.missing': 'Keduanya tidak terbit. Itu terjadi ketika penerbitan otomatisnya gagal; persetujuan ' +
        'di atas tetap berlaku, karena ia peristiwa hukum yang sudah terjadi.',
    'platform.provisioning.rerun': 'Terbitkan sekarang',
    'platform.provisioning.done': '{categories} kategori dan {workflows} definisi alur kerja diterbitkan.',
    'platform.invite.body': 'Organisasi baru tidak berisi siapa pun. Undang manajer pertamanya di sini; setiap ' +
        'orang sesudahnya diundang dari dalam organisasi, tempat jejaknya menyebut manajer ' +
        'yang mengundang alih-alih PQM. Perannya selalu Manajer dan tidak dapat dipilih.',
    'platform.invite.needsAgreement': 'Langkah 2 lebih dulu: tidak ada seorang pun dapat diundang ke organisasi yang ' +
        'perjanjian pemrosesannya belum tercatat, karena saat itulah data pribadi baru ' +
        'boleh mulai diproses sama sekali.',
    'platform.invite.email': 'Alamat surel',
    'platform.invite.fullName': 'Nama lengkap',
    'platform.invite.jobTitle': 'Jabatan',
    'platform.invite.submit': 'Undang manajer pertama',
    'platform.invite.invited': '{name} sudah diundang sebagai manajer.',
    'platform.invite.alreadyIn': 'Organisasi ini sudah punya manajer aktif, jadi orang berikutnya diundang dari dalam.',
    'platform.invite.pending': 'Undangan yang menunggu diterima: {count}. Manajernya belum masuk.',
    'platform.invite.link.title': 'Tautan undangannya',
    'platform.invite.link.notDelivered': 'Tidak ada surel yang meninggalkan sistem ini: ASSIST belum punya penyedia surel di ' +
        'versi ini, jadi tidak ada apa pun yang sampai kepada siapa pun. Serahkan tautan di ' +
        'bawah kepada {name} sendiri, lewat kanal yang Anda percaya.',
    'platform.invite.link.once': 'Ia ditampilkan sekali dan tidak dapat dibaca ulang — tidak dari layar ini, tidak ' +
        'dari basis data. Bila hilang, undang ulang dan tautan baru diterbitkan.',
    'platform.invite.link.copy': 'Salin tautan',
    'platform.invite.link.copied': 'Tautan disalin.',
    'platform.invite.link.delivered': 'Penggelaran ini punya penyedia surel, jadi tautannya hanya masuk ke kotak surel ' +
        'penerimanya dan tidak ditampilkan di sini.',
    'platform.members.title': 'Platform — anggota',
    'platform.members.subtitle': 'Siapa di dalam PQM yang boleh masuk, dan dengan peran platform apa. Ini satu-satunya ' +
        'jalan memberi atau mencabutnya lewat produk.',
    'platform.members.note': 'Daftar ini hanya memuat pegawai PQM. Ia tidak pernah memuat satu pun pengguna ' +
        'organisasi pelanggan: baris mereka tetap terikat organisasinya sendiri, dan tidak ada ' +
        'apa pun di sini yang dapat menjangkaunya.',
    'platform.members.column.person': 'Orang',
    'platform.members.column.role': 'Peran platform',
    'platform.members.column.account': 'Akun',
    'platform.members.column.granted': 'Diberikan',
    'platform.members.grant': 'Beri peran platform',
    'platform.members.revoke': 'Cabut',
    'platform.members.lastAdmin': 'Satu-satunya admin platform aktif — berikan perannya kepada orang lain lebih dulu',
    'platform.members.noPassword': 'Belum menetapkan kata sandi',
    'platform.members.passwordPath': 'Rekan baru menetapkan kata sandinya sendiri lewat alur lupa kata sandi. Tidak ada ' +
        'admin yang pernah menetapkannya untuk orang lain, karena siapa pun yang dapat ' +
        'menetapkan kata sandi Anda dapat masuk sebagai Anda — dan sesudah itu jejak audit ' +
        'membawa nama Anda untuk perbuatannya.',
    'platform.members.role.PLT': 'Administrator platform',
    'platform.members.role.KON': 'Konsultan',
    'platform.members.reason': 'Alasan',
    'platform.members.reason.hint': 'Wajib, dan ia dicatat di jejak audit. Peran platform yang berpindah tanpa alasan ' +
        'adalah baris yang tidak dapat dijelaskan siapa pun setahun kemudian.',
    'platform.members.confirmRevoke': 'Cabut peran platform {name}?',
    'platform.members.confirmRevokeBody': 'Ia kehilangan akses pada permintaan berikutnya, dan seluruh sesinya yang hidup ' +
        'diakhiri. Barisnya tetap disimpan sebagai SUSPENDED, sehingga catatan bahwa ia pernah ' +
        'punya peran ini tidak hilang.',
    'platform.members.granted': 'Peran platform diberikan',
    'platform.members.revoked': 'Peran platform dicabut',
    'platform.members.bootstrapNote': 'Admin platform yang PERTAMA dibuat sekali, lewat POST /v1/platform/bootstrap, dijaga ' +
        'rahasia penggelaran dan ditolak sesudah dipakai. Pemakaiannya ada di jejak audit ' +
        'sebagai PLATFORM_BOOTSTRAP_CLAIMED.',
    'platform.modules.title': 'Modul & Entitlement',
    'platform.modules.subtitle': 'Apa yang dapat ditawarkan platform, dan paket mana memuat apa.',
    'platform.modules.registry': 'Registry modul',
    'platform.modules.plans': 'Paket',
    'platform.modules.note': 'Daftar ini disusun dari manifest modul. Apa pun yang tidak dinyatakan di sana tidak ' +
        'ada di mana pun di sistem.',
    'platform.audit.title': 'Jejak audit',
    'platform.audit.subtitle': 'Siapa melakukan apa, kapan, dan atas dasar hukum apa.',
    'platform.audit.appendOnly': 'Baris-baris ini hanya dapat ditambahkan, tidak dapat diubah dan tidak dapat dihapus — ' +
        'termasuk oleh pemegang basis datanya.',
    'platform.audit.filterActor': 'Siapa yang melakukan',
    'platform.audit.allActors': 'Siapa pun',
    'platform.audit.column.when': 'Kapan',
    'platform.audit.column.actor': 'Siapa',
    'platform.audit.column.event': 'Apa yang terjadi',
    'platform.audit.column.subject': 'Pada apa',
    'platform.audit.column.module': 'Modul',
    'platform.audit.column.basis': 'Dasar hukum',
    /**
     * CACAT 4, 28 September 2026. `SESSION_OPENED` dan `CREDENTIAL_READ` ditulis SEBELUM sesi
     * ada, jadi barisnya memang tidak memuat pelaku. Naskah lamanya menaruh
     * `actorKind.ANONYMOUS` — "Belum masuk" — di KEDUA baris: ia mengulang dirinya, dan ia
     * berbunyi seperti masuk yang GAGAL padahal yang terjadi adalah masuk yang BERHASIL. Kedua
     * baris di bawah menjelaskan keadaannya, dan tidak mengarang identitas yang tidak ada.
     */
    'platform.audit.actor.preSession': 'Sebelum sesi terbentuk',
    /**
     * Catatannya berdiri SEKALI, di bawah tabel, dan hanya ketika baris seperti itu ada di
     * layar. Bentuk pertama perbaikan ini menaruhnya di setiap baris, dan tangkapan layarnya
     * yang memutuskan: kalimat yang sama terulang empat puluh kali mengubah tabel yang
     * seharusnya dibaca menjadi dinding teks. Cacat aslinya dua baris yang mengulang diri;
     * empat puluh bukan perbaikan.
     */
    'platform.audit.actor.preSessionNote': 'Baris bertanda “Sebelum sesi terbentuk” adalah langkah masuk yang ditulis sebelum sesi ' +
        'yang ia bentuk, jadi belum ada pelaku yang tercatat. Ia saat-saat pertama sebuah proses ' +
        'masuk yang BERHASIL, bukan yang gagal.',
    'platform.audit.empty.title': 'Belum ada yang tercatat',
    'platform.audit.empty.body': 'Masuk pertama akan muncul di sini.',
    'platform.health.title': 'Kesehatan sistem',
    'platform.health.subtitle': 'Antrean, kegagalan, dan insiden yang terbuka.',
    'platform.health.refresh': 'Baca ulang',
    'platform.health.checkedAt': 'Dibaca pada {at}.',
    'platform.health.metric.outbox_pending': 'Peristiwa menunggu',
    /**
     * `outbox_stuck` adalah yang BENAR-BENAR dikembalikan GET /v1/platform/health, dan ia tidak
     * punya kunci — sehingga layar Kesehatan Sistem merender `platform.health.metric.outbox_stuck`
     * mentah di layar, di kedua bahasa. Ditemukan dengan MELIHAT tangkapan acuan, bukan oleh
     * gerbang: gerbang teks antarmuka mencari kunci mentah di SUMBER, dan kunci ini dirakit saat
     * berjalan dari nama kolom servernya sendiri. `packages/i18n/src/metrik-kesehatan.test.ts`
     * menutupnya sekarang.
     *
     * "Tersangkut" bukan "gagal": peristiwanya sudah dicoba lebih dari tiga kali dan masih
     * belum terproses. Itu urusan manusia, bukan urusan antrean.
     */
    'platform.health.metric.outbox_stuck': 'Peristiwa tersangkut setelah dicoba ulang',
    'platform.health.metric.outbox_failed': 'Peristiwa gagal',
    'platform.health.metric.job_failures': 'Pekerjaan gagal',
    'platform.health.metric.open_incidents': 'Insiden terbuka',
    'platform.health.metric.outbox_oldest_seconds': 'Peristiwa tertua, detik',
    'platform.health.metric.sessions_active': 'Sesi aktif',
    'platform.health.metric.emails_pending': 'Surel menunggu',
    'platform.health.metric.files_pending_verification': 'Berkas menunggu pemeriksaan',
    'portfolio.title': 'Portofolio klien',
    'portfolio.subtitle': 'Organisasi yang ditugaskan kepada Anda untuk didampingi.',
    'portfolio.column.since': 'Ditugaskan sejak',
    'portfolio.enter': 'Masuk ke klien ini',
    'portfolio.switchIsLogged': 'Setiap kali Anda masuk ke sebuah klien, itu tercatat di jejak audit mereka beserta ' +
        'nama Anda.',
    'portfolio.empty.title': 'Belum ada klien yang ditugaskan',
    'portfolio.empty.body': 'Admin platform yang menugaskan klien kepada konsultan. Sampai itu terjadi Anda tidak ' +
        'dapat mencapai organisasi mana pun, dan itu memang perilaku yang benar.',
    'portfolio.healthPending': 'Ringkasan kesehatan per klien tidak ditampilkan di sini. Membaca angka sebuah klien ' +
        'berarti masuk ke klien itu lebih dulu — angka yang dikumpulkan lintas organisasi tanpa ' +
        'itu adalah kebocoran, bukan ringkasan.',
    'account.title': 'Akun & notifikasi',
    'account.subtitle': 'Keterangan Anda, bahasa Anda, dan perangkat tempat Anda masuk.',
    'account.profile': 'Keterangan Anda',
    'account.saved': 'Tersimpan.',
    'account.language': 'Bahasa',
    'account.language.note': 'Bahasa Anda disimpan di profil, bukan di peramban ini, jadi ia ikut ke perangkat mana ' +
        'pun. Menggantinya tidak menghilangkan apa pun yang sudah Anda ketik.',
    'account.notifications': 'Notifikasi',
    'account.quietHours': 'Notifikasi dorong ditahan antara 20.00 dan 06.00 lalu dikirim pagi berikutnya.',
    'account.safetyAlways': 'Eskalasi keselamatan adalah pengecualian: ia selalu dikirim seketika, pada jam berapa ' +
        'pun.',
    'account.notifications.pending': 'Memilih peristiwa mana yang sampai kepada Anda, lewat kanal apa, belum dapat dilakukan ' +
        'dari sini. Dua aturan di atas berlaku apa adanya.',
    'account.sessions': 'Perangkat tempat Anda masuk',
    'account.session.device': 'Perangkat',
    'account.session.lastSeen': 'Terakhir terlihat',
    'account.session.unknownDevice': 'Perangkat tanpa nama',
    'account.session.current': 'Yang ini',
    'account.session.revoke': 'Keluarkan perangkat ini',
    'account.session.revoke.describe': 'Siapa pun yang memegang perangkat itu harus masuk kembali. Ia berlaku seketika.',
    'account.session.revoked': 'Perangkat itu sudah dikeluarkan.',
    'account.session.signOutInstead': 'Pakai “Keluar” untuk mengakhiri yang ini',
    'error.MEETING_SLOT_TAKEN': 'Sudah ada rapat untuk unit itu pada waktu itu. Pilih slot lain, atau buka rapat yang ' +
        'sudah ada.',
    'error.NOTHING_DISCUSSED': 'Rapat tidak dapat ditutup sebelum sekurang-kurangnya satu butir agenda dibahas. Tandai ' +
        'apa yang Anda bicarakan.',
    'error.TIMEBOX_NOT_ELAPSED': 'Sepuluh menitnya belum habis, jadi ini belum dapat ditandai lewat waktu.',
    'error.QUERY_TOO_SHORT': 'Pencarian butuh sekurang-kurangnya {min_length, number} karakter.',
};
export const catalogs = {
    'en-US': enUS,
    'id-ID': idID,
};
//# sourceMappingURL=catalog.js.map